import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetOverlay,
  SheetPortal,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  OpportunityContactType,
  OpportunityInteractionType,
} from "@/lib/validations/schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { PopoverClose } from "@radix-ui/react-popover";
import { ColumnDef } from "@tanstack/react-table";
import { AxiosRequestConfig } from "axios";
import {
  channel_options,
  close_reason_options,
  opportunity_status_options,
} from "common/filters";
import { Img, Spinner } from "components";
import { DataTableColumnHeader } from "components/Table/data-table-column-header";
import { DataTable } from "components/Table/DataTable";
import {
  Download,
  Eye,
  FingerprintIcon,
  LucideFingerprint,
  Pencil,
  RotateCw,
  ThumbsDown,
  ThumbsUp,
  X,
  XIcon,
} from "lucide-react";
import DocumentModal from "models/DocumentPopup";
import LinkCorporate from "models/LinkCorporate";
import LinkPartner from "models/LinkPartner";
import DocPopup from "pages/Create Opportunities/popup/docpopup";
import { CreatePartnerLeadType } from "pages/CreatePartnerLead/schema";
import { ChannelOptions } from "pages/Leads/lead";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import ReactSelect from "react-select";
import { resetState } from "redux/features/CreateOpportunity";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  GenerateSummary,
  GenerateSummaryReqType,
  PutUpdateCompanyDetails,
  PutUpdateCompanyDetailsReqType,
  UploadLetterDocumnets,
  UploadLetterDocumnetsReqType,
} from "service/apis/Opportunities";
// Same signatures, answered by the new backend. See legacy-opportunity-api.ts.
import {
  clearReminderPatch,
  convertLeadToOrder,
  ClearReminderReqType,
  downloadDoc,
  editProductMode,
  editProductModeReqType,
  getCompanyById,
  GetCompanyByIdReqType,
  getContactDetailById,
  putUpdateInteractionStatus,
  PutUpdateInteractionStatusReqType,
  putUpdateOpportunityStatus,
  PutUpdateOpportunityStatusReqType,
  requestDoc,
  RequestDocReqType,
} from "service/moneeflo/legacy-opportunity-api";
// Same signatures, answered by the new backend. See legacy-opportunity-api.ts.
import {
  getForexOpportunityById,
  getOpportunityByCompany,
  GetOpportunityByCompanyReqType,
  GetOpportunityByIdReqType,
  postCorporateInteractions,
  PostCorporateInteractionsReqType,
} from "service/moneeflo/legacy-opportunity-api";
// Same signatures, answered by the new backend. See legacy-lookup-api.ts.
import {
  getAllStaff,
  GetAllStaffRequestType,
  getRelationShipManager,
} from "service/moneeflo/legacy-lookup-api";
import { toast, Toaster } from "sonner";
import { DateTimePicker } from "./dateandtime";
import EditForm from "./editform";
import OrderDetailForm, {
  OrderDetail,
  orderDetailSchema,
} from "./orderdetailform";
import { Option } from "./types";
import {
  buttonInfo,
  channel_options_inbound,
  channel_options_outbound,
  formatDate,
  formatUnixTimestamp,
  getRelativeDate,
  sheetTabs,
} from "./utils/featureRule";
import { contactInsheet } from "./utils/icons";
import { useSelectState } from "./utils/useSelect";
import { useIsMobile } from "@/hooks/use-mobile";
import { InteractionCard } from "common/InteractionCardMobile/Index";
import EmailPopup from "pages/Create Opportunities/popup/emailpopup";
import { checkLevel } from "common/rules";
import { LEVEL_2 } from "common/constants";
import { LeadOverView } from "./LeadOverView";
import {
  setContactId,
  setEmail,
  setIsReferralMade,
  setPhone,
} from "redux/features/CustomerRegistration";
import { convertFileToBase64 } from "pages/CreateRemit/documentation";
import { handleDownloadViaURL } from "utils";
import Mobilecontactdetail from "./mobilecontactdetail";
import { Button } from "@/components/ui/button";
import { useMutation } from "react-query";
import { update_stage } from "./apis";
import { number } from "prop-types";
import { setIsLoan, setMode, setPurposeId } from "redux/features/CreateDeal";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useDealForm } from "pages/CreateDeal/hooks/useDealForm";
import { FaFingerprint } from "react-icons/fa";
// Same signatures, answered by the new backend. See legacy-customer-api.ts.
import {
  getStaffCustomer,
  GetStaffCustomerRequestType,
} from "service/moneeflo/legacy-customer-api";
import { PutUpdateCustomerDetails, PutUpdateCustomerDetailsReqType } from "service/apis/CustomerRegistrationapi";

const remitProductModeOptions: Option[] = [
  { value: "Flywire", label: "Flywire" },
  { value: "WesternUnion", label: "Western Union" },
  { value: "CIBC", label: "CIBC" },
  { value: "PayMyTuition", label: "PayMyTuition" },
  { value: "ViaTransfer", label: "Via Transfer" },
];

const columns: ColumnDef<OpportunityContactType>[] = [
  {
    accessorKey: "id",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="ID" />
    ),
    cell: ({ row }) => {
      const stageTag = stageTags.find(
          (stage) => stage.value.toLowerCase() === row?.original?.stage_tag?.toLowerCase()
      );
      return (
        <div className="flex flex-row gap-2.5 items-center relative group">
          {row?.original?.requestQuote_Type && (
            <div className="relative group flex items-center">
              <div className="w-2.5 h-2.5 bg-red-500 rounded-full"></div>
              <span className="absolute bottom-full left-0 mb-1 bg-gray-300 text-[#333] text-xs rounded-md py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                Quotation request for ID: {row.getValue("id")}
              </span>
            </div>
          )}
          <div className="flex flex-col justify-start gap-1.5">
            <p className="capitalize text-sm leading-4">{row.getValue("id")}</p>
            {row?.original?.stage_tag && (
              <div className="flex gap-1.5 items-center">
                <span
                  dangerouslySetInnerHTML={{ __html: stageTag?.icon || "" }}
                  className=""
                />
                <h4
                  className={`${stageTag?.color} text-sm leading-4 font-semibold`}
                >
                  {row.original?.stage_tag}
                </h4>
              </div>
            )}
          </div>
        </div>
      );
    },
    enableHiding: false,
    filterFn: (row, id, value) => {
      const rowValue = String(row.getValue(id));
      const searchValue = String(value);
      return rowValue.includes(searchValue);
    },
  },
  // {
  //   accessorKey: "contactPerson",
  //   header: ({ column }) => (
  //     <DataTableColumnHeader column={column} title="Contact Person" />
  //   ),
  //   cell: ({ row }) => {
  //     return <p className=" capitalize">{row.getValue("contactPerson")}</p>;
  //   },
  //   filterFn: (row, id, value) => {
  //     return String(row.getValue(id))
  //       .toLowerCase()
  //       .includes(String(value).toLowerCase());
  //   },
  //   enableSorting: false,
  // },
  {
    accessorKey: "product",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Product" />
    ),
    cell: ({ row }) => {
      return (
        <div className="flex flex-col gap-1.5">
          <p className="capitalize">{row.getValue("product")}</p>
          <p className="capitalize font-normal text-sm text-[#666666]">
            {row?.original?.subCategory}
          </p>
        </div>
      );
    },
    filterFn: (row, id, value) => {
      return String(row.getValue(id))
        .toLowerCase()
        .includes(String(value).toLowerCase());
    },
    enableSorting: false,
  },
  {
    accessorKey: "assignedTo",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Assigned To" />
    ),
    cell: ({ row }) => {
      // const name =
      //   row.getValue("assignedTo") ??
      //   localStorage.getItem("owner_name")?.toString();
      return (
        <div className="flex flex-col gap-1.5">
          <p className="capitalize">{row.getValue("assignedTo")}</p>
          <p className="capitalize font-normal text-sm text-[#666666]">
            {row?.original?.location}
          </p>
        </div>
      );
    },
    filterFn: (row, id, value) => {
      return String(row.getValue(id))
        .toLowerCase()
        .includes(String(value).toLowerCase());
    },
    enableSorting: false,
  },

  {
    id: "source",
    accessorKey: "source",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Channel" />
    ),
    cell: ({ row }) => {
      const newSource = (channel_options as ChannelOptions)?.[
        (row?.original?.lead_deatils?.Type as string)?.toLowerCase()
      ]?.find(
        (item) =>
          item.value?.toLowerCase() ===
          (row.getValue("source") as string)?.toLowerCase().split(" ").join(""),
      );

      return newSource?.value ? (
        <div className="grid items-start w-fill  gap-1">
          <div className="flex items-start flex-col gap-3  w-full">
            <div className="h-full w-full flex gap-5">
              <p className="capitalize whitespace-nowrap">
                {row?.original?.lead_deatils?.id === 12
                  ? "Partner referral"
                  : row?.original?.lead_deatils?.id === 18
                    ? "Employee referral"
                    : newSource?.label}
              </p>
              <Img
                className="text-right h-5 w-5"
                src={newSource?.icon}
                alt="icon"
              />
            </div>
            <p className="capitalize font-normal text-sm text-[#666666]">
              {["outletID", "partnerOffice", "contact"].includes(
                row?.original?.lead_deatils?.source as string,
              )
                ? row?.original?.lead_deatils?.source === "partnerOffice"
                  ? row?.original?.referred_partner
                  : row?.original?.lead_deatils?.source === "contact"
                    ? "Contact"
                    : row?.original?.lead_deatils?.Channel === "Walk-in"
                      ? ""
                      : "Employee"
                : row?.original?.lead_deatils?.source === "linkdin"
                  ? "LinkedIn"
                  : row?.original?.lead_deatils?.source}
            </p>
          </div>
        </div>
      ) : (
        <p className="capitalize">{row.getValue("source")}</p>
      );
    },
    filterFn: (row, id, value) => {
      return String(row.getValue(id))
        .toLowerCase()
        .includes(String(value).toLowerCase());
    },
    enableSorting: false,
  },
  {
    accessorKey: "createDate",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Created " />
    ),
    cell: ({ row }) => {
      return (
        <div className="flex flex-col gap-1.5">
          <p className=" whitespace-nowrap">{row.getValue("createDate")}</p>
          <p className="capitalize font-normal text-sm text-[#666666]">
            {row.original.createdBy}
          </p>
        </div>
      );
    },
    filterFn: (row, id, value) => {
      return String(row.getValue(id))
        .toLowerCase()
        .includes(String(value).toLowerCase());
    },
  },
  {
    accessorKey: "opportunityContactStatus",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => {
      const values = close_reason_options.find(
        (item) =>
          item.value === (row.getValue("opportunityContactStatus") as string),
      );
      if (!values) {
        return (
          <div className="flex gap-2 items-center w-full">
            <p
              style={{ color: "#23AB28", backgroundColor: "#E1FFE2" }}
              className="py-[3px] px-2 w-full text-center rounded-md"
            >
              Open
            </p>
            {checkLevel(LEVEL_2) && row?.original?.latestQuotationPDF && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDownloadViaURL(
                    row?.original?.latestQuotationPDF as string,
                  );
                }}
                className=""
              >
                <svg
                  width="25"
                  height="24"
                  viewBox="0 0 25 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.59375 5.43359C3.59375 2.67217 5.83232 0.433594 8.59375 0.433594H15.537C15.8914 0.433594 16.2291 0.583994 16.4661 0.847394L21.3979 6.32706C21.6045 6.55659 21.7188 6.85446 21.7188 7.16327V18.5586C21.7188 21.32 19.4801 23.5586 16.7188 23.5586H8.59375C5.83232 23.5586 3.59375 21.32 3.59375 18.5586V5.43359Z"
                    fill="#165FE3"
                  />
                  <path
                    d="M21.7188 7.93359V7.16327C21.7188 6.85446 21.6045 6.55659 21.3979 6.32706L16.4661 0.847394C16.2291 0.583994 15.8914 0.433594 15.537 0.433594H14.2188V2.93359C14.2188 5.69502 16.4574 7.93359 19.2188 7.93359H21.7188Z"
                    fill="#98BDFF"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M8.26385 14.4462C8.64095 14.0913 9.23427 14.1094 9.58906 14.4865L12.6562 17.7467L15.7235 14.4865C16.0782 14.1094 16.6715 14.0913 17.0486 14.4462C17.4257 14.8009 17.4439 15.3942 17.089 15.7713L13.7179 19.3545C13.1385 19.9704 12.174 19.9704 11.5946 19.3545L8.22345 15.7713C7.86866 15.3942 7.88675 14.8009 8.26385 14.4462Z"
                    fill="white"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12.6562 8.56641C13.174 8.56641 13.5938 8.98614 13.5938 9.50391V18.8789C13.5938 19.3967 13.174 19.8164 12.6562 19.8164C12.1385 19.8164 11.7188 19.3967 11.7188 18.8789V9.50391C11.7188 8.98614 12.1385 8.56641 12.6562 8.56641Z"
                    fill="white"
                  />
                </svg>
              </button>
            )}
          </div>
        );
      }
      return (
        <p
          style={{ color: values?.textColor, backgroundColor: values?.bgColor }}
          className="py-[3px] px-2 text-center rounded-md"
        >
          {values.label}
        </p>
      );
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
    enableSorting: false,
  },
];

const remittanceProductListcolumns = (
  getOpportunity?: () => Promise<void>
): ColumnDef<any>[] => [
  {
    accessorKey: "productName",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Product" />
    ),
    cell: ({ row }) => {
      return (
        <div className="flex flex-col gap-1.5">
          <p className="capitalize">{row.getValue("productName")}</p>
          <p className="capitalize font-normal text-sm text-[#666666]">
            {row?.original?.subCategory}
          </p>
        </div>
      );
    },
    enableSorting: false,
  },
  {
    accessorKey: "Mode",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Mode" />
    ),
    cell: ({ row }) => {
      return (
        <div className="flex flex-col gap-1.5">
          <p className="capitalize">{row.getValue("Mode")}</p>
        </div>
      );
    },
    enableSorting: false,
  },
{
    id: "actions",
    header: "Action",
    cell: ({ row }) => {
      const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
    const [selectMode, setSelectMode] = useState<any>(row.original.Mode || "");
    const [currentId, setCurrentId] = useState<number | null>(null);
    const [selectLoan, setSelectLoan] = useState<string>(row.original?.took_loan ? "Yes" : "No");

    
    // open modal
    const openEditDialog = useCallback(
      (rowData: any) => {
        setCurrentId(rowData.id);
        setSelectMode(rowData.Mode || rowData.mode_opp || "");
        setSelectLoan(row.original?.took_loan ? "Yes" : "No");
        setIsEditOpen(true);
      },
      [setCurrentId, setSelectMode, setSelectLoan, setIsEditOpen]
    );

   const handleUpdate = async () => {
      if (!currentId) return;

      const auth = localStorage.getItem("authToken");
      if (!auth) return;
      const authToken = JSON.parse(auth);

      const payload: AxiosRequestConfig<editProductModeReqType> = {
        headers: { Authorization: `Bearer ${authToken}` },
        data: {
          opportunity_id: currentId,
          mode_opp: selectMode,
          took_loan: selectLoan === "Yes" ? true : false,
        },
      };

      try {
        const res = await editProductMode(payload);
        if (res.status === 200) {
          toast.success("Mode updated successfully");
          setIsEditOpen(false);
          await getOpportunity?.();
        } else {
          toast.error("Failed to update: " + res.statusText);
        }
      } catch (error: any) {
        toast.error(
          "Failed to update: " + (error.response?.data?.message || error.message)
        );
      }
    };

      return (
        <>
         <button
            className="p-2 hover:bg-gray-100 rounded disabled:cursor-default"
            onClick={() => openEditDialog(row?.original)}
            disabled={row?.original?.isOpportunityOpen === "false"}
          >
            <Pencil className="w-4 h-4 text-blue-600" />
          </button>
          {isEditOpen && (
            <>
              <Dialog
                open={isEditOpen}
                onOpenChange={setIsEditOpen}
              >
                <DialogContent className="max-w-[540px] bg-[#fff] rounded-lg">
                  <DialogHeader>
                    <DialogTitle className="text-lg text-left font-semibold text-gray-800">
                      Edit product
                    </DialogTitle>
                  </DialogHeader>

                  <div className="w-full h-[1px] bg-[#E5E7EB] mb-4" />

                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="col-span-1 sm:col-span-2 w-full">
                      <label className="text-sm font-medium text-gray-700 mb-1 block">
                        Mode
                      </label>
                      <Select value={selectMode} onValueChange={val=>{
                        setSelectMode(val);
                        if(val === 'ViaTransfer') {
                          setSelectLoan('No');
                        }
                      }}>
                        <SelectTrigger className="w-full h-[54px] border border-[#D0D5DD] rounded-[8px] focus:outline-none px-6 py-4.5">
                          <SelectValue
                            placeholder="Flywire"
                            className="text-sm font-medium text-[#333333]"
                          />
                        </SelectTrigger>
                        <SelectContent className="text-xs font-medium text-[#333333]">
                          {remitProductModeOptions?.length > 0 &&
                            remitProductModeOptions?.map((item: any, index: number) => (
                              <SelectItem value={item.value} key={index}>
                                {item.label}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {
                      selectMode !== 'ViaTransfer' &&
                      <div className="col-span-1 sm:col-span-2 w-full">
                        <label className="text-sm font-medium text-gray-700 mb-1 block">
                          Have you taken loan?
                        </label>
                        <Select value={selectLoan} onValueChange={setSelectLoan}>
                          <SelectTrigger className="w-full h-[54px] border border-[#D0D5DD] rounded-[8px] focus:outline-none px-6 py-4.5">
                            <SelectValue
                              placeholder={selectLoan}
                              className="text-sm font-medium text-[#333333]"
                            />
                          </SelectTrigger>
                          <SelectContent className="text-xs font-medium text-[#333333]">
                            <SelectItem value="Yes">
                              Yes
                            </SelectItem>
                            <SelectItem value="No">
                              No
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    }
                  </div>
                  {/* Buttons */}
                  <div className="flex flex-row w-full sm:justify-start gap-4 mt-6">
                    <Button
                      variant="outline"
                      onClick={() => setIsEditOpen(false)}
                      className="rounded-[8px] px-4 py-2.5 border-[#165FE3]"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleUpdate}
                      className="bg-blue-600 text-[#fff] rounded-[8px] px-4 py-2.5 hover:bg-blue-700 disabled:opacity-50"
                      disabled={!selectMode}
                    >
                      Update
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </>
          )}
        </>
      );
    },
    enableSorting: false,
  },

  // {
  //   id: "amount",
  //   accessorKey: "amount",
  //   header: ({ column }) => (
  //     <DataTableColumnHeader column={column} title="Amount" />
  //   ),
  //   cell: ({ row }) => {
  //     return (
  //       <div className="grid items-start w-fill  gap-1">
  //         <div className="flex items-start flex-col gap-3  w-full">
  //           <p className="capitalize font-normal text-sm text-[#666666]">
  //             USD 1000
  //           </p>
  //         </div>
  //       </div>
  //     )
  //   },
  //   filterFn: (row, id, value) => {
  //     return String(row.getValue(id))
  //       .toLowerCase()
  //       .includes(String(value).toLowerCase());
  //   },
  //   enableSorting: false,
  // },
];

// for forex single lead have show product table
const ForexProductListcolumns = (
  getOpportunity?: () => Promise<void>
): ColumnDef<any>[] => [
   {
    accessorKey: "country_currency_want",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Product" />
    ),
    cell: ({ row }) => (
      <div className="flex flex-col gap-1.5">
        <p className="capitalize">
          {row.original.name}
        </p>
        <p className="capitalize font-normal text-sm text-[#666666]">{row?.original?.buy_sell === "sell" ? row.original.country_currency_have:row.original.country_currency_want}</p>
      </div>
    ),
    enableSorting: false,
  },
  {
    accessorKey: "rate",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Customer rate" />
    ),
    cell: ({ row }) => <p>INR {row.original.rate}</p>,
    enableSorting: false,
  },
  {
    accessorKey: "quantity",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Quantity" />
    ),
    cell: ({ row }) => <p>{row?.original?.buy_sell === "sell" ? row.original.country_currency_have:row.original.country_currency_want}{' '}{row.original.quantity}</p>,
    enableSorting: false,
  },
  {
    accessorKey: "inr_amount",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Amount (INR)" />
    ),
    cell: ({ row }) => (
      <p>
        {row.original.inr_amount?.toLocaleString("en-IN", {
          style: "currency",
          currency: "INR",
        })}
      </p>
    ),
    enableSorting: false,
  },
];

const filteredColumns = checkLevel(LEVEL_2)
  ? columns.filter((col) => col.id !== "source")
  : columns;

const tablist = [
  { label: "Leads", value: "leads", enabled: true },
  { label: "Orders", value: "orders", enabled: false },
];
export const stageTags = [
  {
    label: 'Hot',
    value: 'Hot',
    color: 'bg-[#FF6F001A] text-[#FF6F00]',
    icon: `<svg width="15" height="16" viewBox="0 0 15 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M9.81569 12.4066C9.81569 11.5513 9.27702 11.054 8.24435 10.1773C8.01102 9.9773 7.75902 9.7653 7.49902 9.5293C7.20235 9.80196 6.92035 10.042 6.66169 10.2626C5.63102 11.1333 5.17969 11.5593 5.17969 12.4066C5.17969 13.0214 5.4239 13.611 5.85861 14.0457C6.29332 14.4804 6.88292 14.7246 7.49769 14.7246C8.11246 14.7246 8.70205 14.4804 9.13676 14.0457C9.57147 13.611 9.81569 13.0214 9.81569 12.4066Z" fill="#FF6F00"/>
<path d="M10.4373 2.69C9.63732 2.01067 8.81065 1.30867 7.97465 0.473333L7.49865 0L7.03199 0.474C5.52932 1.982 4.81865 4.298 4.51065 5.68733C4.27431 5.32137 4.09892 4.91948 3.99132 4.49733L3.70665 3.406L2.88465 4.17733C1.44532 5.52933 0.484652 6.87 0.484652 9.01667C0.470813 10.5657 0.975025 12.0749 1.91718 13.3045C2.85933 14.5342 4.18538 15.4137 5.68465 15.8033C6.06137 15.8988 6.44499 15.9645 6.83199 16C5.99284 15.8436 5.23472 15.3989 4.68874 14.7427C4.14275 14.0866 3.84324 13.2603 3.84199 12.4067C3.84199 10.9067 4.74665 10.14 5.79399 9.24533C6.16799 8.92733 6.59399 8.56667 7.02399 8.134L7.49865 7.662L7.97065 8.134C8.35465 8.518 8.73865 8.84333 9.10999 9.15933C10.1607 10.0507 11.1527 10.8927 11.1527 12.4067C11.1516 13.2569 10.8545 14.0802 10.3124 14.7352C9.77028 15.3902 9.01702 15.836 8.18199 15.996C9.91569 15.8278 11.5246 15.0198 12.6949 13.7297C13.8652 12.4396 14.513 10.7598 14.512 9.018C14.512 6.15 12.6233 4.54667 10.4373 2.69Z" fill="#FF6F00"/>
</svg>
`
  },
  {
    label: 'Warm',
    value: 'Warm',
    color: 'bg-[#F6B8001A] text-[#E3AA00]',
    icon: `<svg width="14" height="16" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M12.9858 16.0032L11.6972 15.6632C11.7745 15.3692 11.8618 15.0958 11.9478 14.8265C12.8092 12.9325 12.0725 10.0692 11.1045 8.74317C10.2916 7.4584 9.81612 5.9893 9.72196 4.47191C9.6278 2.95452 9.91805 1.4379 10.5658 0.0625L11.7845 0.603833C11.2223 1.78231 10.9695 3.08459 11.0501 4.38783C11.1307 5.69107 11.542 6.95228 12.2452 8.0525C13.3538 9.54183 14.2212 13.0412 13.2178 15.2318C13.1385 15.4798 13.0578 15.7332 12.9858 16.0032ZM8.55116 15.2318C9.55516 13.0392 8.6845 9.54183 7.5785 8.0525C6.87545 6.95224 6.46424 5.69103 6.38364 4.38782C6.30304 3.08461 6.55571 1.78234 7.11783 0.603833L5.89916 0.0625C5.25126 1.43787 4.96094 2.95451 5.05511 4.47192C5.14927 5.98934 5.62486 7.45845 6.43783 8.74317C7.40583 10.0678 8.14316 12.9338 7.28116 14.8265C7.19516 15.0932 7.10783 15.3692 7.0305 15.6632L8.31916 16.0032C8.39116 15.7332 8.47183 15.4798 8.55116 15.2318ZM3.8845 15.2318C4.8885 13.0392 4.01783 9.54183 2.91183 8.0525C2.20878 6.95224 1.79757 5.69103 1.71697 4.38782C1.63637 3.08461 1.88905 1.78234 2.45116 0.603833L1.2325 0.0625C0.584595 1.43787 0.294278 2.95451 0.388439 4.47192C0.4826 5.98934 0.958194 7.45845 1.77116 8.74317C2.73916 10.0665 3.4765 12.9332 2.6145 14.8265C2.5285 15.0932 2.44116 15.3692 2.36383 15.6632L3.6525 16.0032C3.7245 15.7332 3.80516 15.4798 3.8845 15.2318Z" fill="#E3AA00"/>
</svg>
`
  },
  {
    label: 'Cold',
    value: 'Cold',
    color: 'bg-[#4F8EFF1A] text-[#4E84E5]',
    icon: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M15.1752 9.31341L13.5452 9.74408C13.3064 9.80703 13.0831 9.91845 12.8892 10.0714L11.1599 9.06608C11.3915 8.37383 11.3915 7.62499 11.1599 6.93274L12.8892 5.92675C13.0831 6.0797 13.3064 6.19113 13.5452 6.25408C13.5452 6.25408 15.2899 6.70675 15.3452 6.70675C15.5047 6.70358 15.6579 6.64328 15.7767 6.5368C15.8956 6.43033 15.9723 6.28475 15.993 6.12651C16.0137 5.96827 15.9769 5.80786 15.8893 5.67444C15.8018 5.54102 15.6692 5.44344 15.5159 5.39941L13.8865 4.96808C13.7645 4.93569 13.6603 4.85622 13.5968 4.74711C13.5333 4.638 13.5157 4.50816 13.5479 4.38608L13.9792 2.75675C14.0016 2.67209 14.0071 2.58384 13.9954 2.49705C13.9837 2.41026 13.9551 2.32662 13.911 2.25091C13.867 2.1752 13.8085 2.1089 13.7389 2.0558C13.6693 2.00269 13.5898 1.96382 13.5052 1.94141C13.4205 1.919 13.3323 1.91348 13.2455 1.92517C13.1587 1.93686 13.0751 1.96554 12.9994 2.00955C12.9236 2.05357 12.8573 2.11206 12.8042 2.1817C12.7511 2.25134 12.7123 2.33075 12.6899 2.41541L12.2592 4.04475C12.1954 4.2838 12.1815 4.53344 12.2185 4.77808L10.4852 5.78475C10.0086 5.24745 9.37115 4.87846 8.66785 4.73275V2.73275C8.89725 2.64265 9.10575 2.50651 9.28052 2.33275L10.4725 1.14075C10.598 1.01574 10.6686 0.846021 10.6689 0.668924C10.6692 0.491827 10.5992 0.32186 10.4742 0.196412C10.3492 0.0709648 10.1795 0.000313603 10.0024 1.04148e-06C9.82527 -0.00031152 9.6553 0.0697402 9.52985 0.194746L8.33785 1.38675C8.24824 1.47542 8.12726 1.52515 8.00119 1.52515C7.87512 1.52515 7.75414 1.47542 7.66452 1.38675L6.47252 0.194746C6.34679 0.0733068 6.17838 0.00611057 6.00359 0.00762951C5.82879 0.00914846 5.66158 0.0792611 5.53797 0.202866C5.41437 0.326472 5.34426 0.49368 5.34274 0.668478C5.34122 0.843276 5.40842 1.01168 5.52985 1.13741L6.72185 2.33275C6.89662 2.50651 7.10513 2.64265 7.33452 2.73275V4.73275C6.63061 4.87721 5.99222 5.24527 5.51452 5.78208L3.78119 4.77541C3.81818 4.53077 3.80433 4.28113 3.74052 4.04208L3.31252 2.41275C3.29011 2.32809 3.25124 2.24867 3.19814 2.17903C3.14503 2.1094 3.07873 2.0509 3.00302 2.00688C2.92731 1.96287 2.84367 1.9342 2.75688 1.92251C2.67009 1.91082 2.58185 1.91633 2.49719 1.93875C2.41253 1.96116 2.33311 2.00003 2.26347 2.05313C2.19384 2.10623 2.13534 2.17253 2.09133 2.24824C2.04731 2.32395 2.01864 2.40759 2.00695 2.49438C1.99526 2.58118 2.00077 2.66942 2.02319 2.75408L2.45452 4.38341C2.48664 4.50549 2.46903 4.63533 2.40553 4.74444C2.34204 4.85355 2.23786 4.93302 2.11585 4.96541L0.48652 5.39941C0.33051 5.44116 0.194959 5.53812 0.105051 5.67228C0.0151428 5.80644 -0.0230088 5.96867 -0.0023157 6.12884C0.0183774 6.28901 0.0965082 6.43622 0.21756 6.54312C0.338613 6.65002 0.494356 6.70935 0.655853 6.71008C0.71252 6.71008 2.45585 6.25741 2.45585 6.25741C2.69466 6.19446 2.91796 6.08304 3.11185 5.93008L4.84252 6.93274C4.61087 7.62499 4.61087 8.37383 4.84252 9.06608L3.11319 10.0714C2.91929 9.91845 2.69599 9.80703 2.45719 9.74408L0.827186 9.31341C0.661193 9.28112 0.489133 9.31312 0.345845 9.40293C0.202558 9.49273 0.0987526 9.63363 0.0554497 9.7971C0.0121469 9.96056 0.0325827 10.1344 0.112619 10.2833C0.192655 10.4323 0.32631 10.5453 0.48652 10.5994L2.11585 11.0307C2.23786 11.0631 2.34204 11.1426 2.40553 11.2517C2.46903 11.3608 2.48664 11.4907 2.45452 11.6127L2.02319 13.2421C2.00077 13.3267 1.99526 13.415 2.00695 13.5018C2.01864 13.5886 2.04731 13.6722 2.09133 13.7479C2.13534 13.8236 2.19384 13.8899 2.26347 13.943C2.33311 13.9961 2.41253 14.035 2.49719 14.0574C2.58185 14.0798 2.67009 14.0853 2.75688 14.0737C2.84367 14.062 2.92731 14.0333 3.00302 13.9893C3.07873 13.9453 3.14503 13.8868 3.19814 13.8171C3.25124 13.7475 3.29011 13.6681 3.31252 13.5834L3.74319 11.9541C3.807 11.715 3.82084 11.4654 3.78385 11.2207L5.51719 10.2141C5.99381 10.7514 6.63123 11.1204 7.33452 11.2661V13.2661C7.10513 13.3562 6.89662 13.4923 6.72185 13.6661L5.52985 14.8614C5.40842 14.9871 5.34122 15.1555 5.34274 15.3303C5.34426 15.5051 5.41437 15.6724 5.53797 15.796C5.66158 15.9196 5.82879 15.9897 6.00359 15.9912C6.17838 15.9927 6.34679 15.9255 6.47252 15.8041L7.66785 14.6121C7.75747 14.5234 7.87845 14.4737 8.00452 14.4737C8.13059 14.4737 8.25157 14.5234 8.34119 14.6121L9.53319 15.8041C9.65892 15.9255 9.82732 15.9927 10.0021 15.9912C10.1769 15.9897 10.3441 15.9196 10.4677 15.796C10.5913 15.6724 10.6615 15.5051 10.663 15.3303C10.6645 15.1555 10.5973 14.9871 10.4759 14.8614L9.28385 13.6694C9.10842 13.4941 8.89872 13.3568 8.66785 13.2661V11.2661C9.37168 11.1218 10.0101 10.754 10.4879 10.2174L12.2212 11.2241C12.1842 11.4687 12.198 11.7184 12.2619 11.9574L12.6925 13.5867C12.7378 13.7577 12.8491 13.9037 13.002 13.9926C13.1549 14.0815 13.3369 14.106 13.5079 14.0607C13.6788 14.0155 13.8248 13.9042 13.9137 13.7512C14.0026 13.5983 14.0271 13.4164 13.9819 13.2454L13.5505 11.6161C13.5184 11.494 13.536 11.3642 13.5995 11.255C13.663 11.1459 13.7672 11.0665 13.8892 11.0341L15.5185 10.6027C15.605 10.5821 15.6865 10.5444 15.7582 10.4918C15.8299 10.4392 15.8904 10.3728 15.936 10.2965C15.9817 10.2202 16.0116 10.1356 16.0241 10.0475C16.0365 9.95947 16.0312 9.86983 16.0085 9.78386C15.9857 9.6979 15.946 9.61735 15.8917 9.54695C15.8374 9.47655 15.7695 9.41774 15.6921 9.37396C15.6147 9.33019 15.5294 9.30234 15.4411 9.29206C15.3527 9.28177 15.2632 9.28926 15.1779 9.31408L15.1752 9.31341Z" fill="#4E84E5"/>
</svg>
`
  }
]
export default function OpportunityDetails() {
  const formContext = useFormContext<CreatePartnerLeadType>();
  const { id } = useParams();
  const location = useLocation();

  useEffect(() => {
    if (id && formContext) {
      formContext.setValue("contact_id", id, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  }, [id, formContext]);

  const [isOpen, setIsOpen] = useState(false);
  const [opportunityList, setOpportunityList] = useState<
    OpportunityContactType[]
  >([]);
  const [isRowDetailsOpen, setIsRowDetailsOpen] = useState(false);

  const [selectedRowData, setSelectedRowData] =
    useState<OpportunityContactType | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [companyData, setCompanyDetails] = useState<any>([]);

  const [docs, setDocs] = useState<
    {
      doc_id: string;
      filename: string;
      file: string;
      date: string;
    }[]
  >([]);
  const [sheetSide, setSheetSide] = useState("right");
  const [activeTab, setActiveTab] = useState("leads");

  const navigate = useNavigate();

  const methods = useForm({
    defaultValues: companyData,
  });
  const { pipelineType } = useAppSelector((state) => state.CreateOpportunity);
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(companyData?.contactDetails?.notes || "");

  useEffect(() => {
    methods.reset(companyData);
  }, [companyData]);

  const handleEditClick = () => {
    setInputValue(companyData?.notes || "");
    setIsEditing(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
  };

  const handleSave = async () => {
    const auth = localStorage.getItem("authToken");
    if (auth === null) return;
    const authToken = JSON.parse(auth);

    const payload: AxiosRequestConfig<PutUpdateCustomerDetailsReqType> = {
      headers: { Authorization: `Bearer ${authToken}` },
      data: {
        // id: id as string,
        // notes: inputValue,
        // pipelineType: pipelineType,
        customers_id: id as string,
        notes: inputValue || "",
      },
    };

    try {
      const res = await PutUpdateCustomerDetails(payload);
      if (res.status === 200) {
        toast.success("Notes updated successfully");
        setCompanyDetails((prev) => ({ ...prev, notes: inputValue }));
        setIsEditing(false);
      } else {
        console.error("Update failed:", res);
        toast.error("Failed to update notes: " + res.statusText);
      }
    } catch (error) {
      console.error("Error updating notes:", error);
      toast.error(
        "Failed to update notes: " +
          (error.response?.data?.message || error.message),
      );
    }
  };

  const handleRowClick = (row: OpportunityContactType) => {
    setSelectedRowData(row);
    setIsRowDetailsOpen(true);
  };

  const handleDialogOpen = (open: boolean) => {
    setDialogOpen(open);
  };

  const dispatch = useAppDispatch();
  const handleSheetOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      dispatch(resetState());
    }
  };

  const handleUpdateCompany = async (value: any) => {
    setLoading(true);
    const auth = localStorage.getItem("authToken");
    if (auth === null) return;
    const authToken = JSON.parse(auth);

    let payload: AxiosRequestConfig<PutUpdateCustomerDetailsReqType> = {
      headers: { Authorization: `Bearer ${authToken}` },
    };

    if (pipelineType === "corporate") {
      payload.data = {
        id: id as string,
        company_name: value?.company_name,
        email: value?.company_email,
        contactPersonType: value?.contact_person_type?.value,
        contactName: value?.contact_person_name,
        contactNumber: value?.contact_person_phone,
        pipelineType: "coorporate",
      };
    }
    if (pipelineType === "partner") {
      payload.data = {
        id: id as string,
        contactName: value?.entity_name,
        contactEmail: value?.company_email,
        typeOfPartner: value?.entity_type?.value,
        pipelineType: "partner",
      };
    }
    if (pipelineType === "retail") {
      payload.data = {
        customers_id: id as string,
        name: value?.contact_name,
        phone_number: value?.contact_number,
        email: value?.contact_email,
        address: value?.contact_address,
        notes: inputValue || "",
      };
    }

    try {
      const res = (await PutUpdateCustomerDetails(payload)) as any;
      if (res.status === 200) {
        toast.success("Contact details updated successfully");
        handleDialogOpen(false);
        await getCompanyDetails();
        setLoading(false);
      }
    } catch (error) {
      toast.error("Failed to update contact details");
      setLoading(false);
    }
  };

  const getCompanyDetails = async () => {
    const auth = localStorage.getItem("authToken");
    if (auth === null) return;
    const authToken = JSON.parse(auth);
    // const payload: AxiosRequestConfig<GetCompanyByIdReqType> = {
    //   headers: { Authorization: `Bearer ${authToken}` },
    //   data: {
    //     id,
    //   },
    // };
      const payload: AxiosRequestConfig<GetStaffCustomerRequestType> = {
          data: {
            customers_id: id,
          },
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        };

    try {
      let response;
      if (!id) return;
      // if (pipelineType === "corporate") {
        response = (await getStaffCustomer(payload)) as any;
      // } 
      // else {
      //   response = (await getContactDetailById(payload)) as any;
      // }
      setCompanyDetails(response?.data?.customerDetail);
    } catch (error) {
      console.error("Error fetching options:", error);
    }
  };

  const getOpportunityList = async () => {
    const auth = localStorage.getItem("authToken");
    if (auth === null) return;
    const authToken = JSON.parse(auth);
    let payload: AxiosRequestConfig<GetOpportunityByCompanyReqType> = {
      headers: { Authorization: `Bearer ${authToken}` },
      params: {
        contactID: id,
      },
    };
    if (pipelineType === "corporate") {
      payload = {
        headers: { Authorization: `Bearer ${authToken}` },
        params: {
          companyID: id,
        },
      };
    }

    const name = localStorage.getItem("owner_name")?.toString();
    try {
      const response = (await getOpportunityByCompany(payload)) as any;
      if (response?.data?.length === 0) {
        return;
      }
      setOpportunityList(() => {
        return response?.data?.map((item: any) => {
          // const item= items?.leadDetail
            let productName = "";
            let productSubCategory = "";
            // If _product exists, use it
            if (item?._product) {
              productName = item._product.name;
              productSubCategory = item._product.subCategory;
            } else {
              const productNameSet: Set<string> = new Set();
              const subCategorySet: Set<string> = new Set();

              item?.product_details?.forEach((prod: any) => {
                if (prod?.name) productNameSet.add(prod.name);
                if (prod?.subCategory) subCategorySet.add(prod.subCategory);
              });

              productName = Array.from(productNameSet).join(", ");
              productSubCategory = Array.from(subCategorySet).join(", ");
            }

          return {
            id: item?.id,
            uniqueId: item?.unique_id,
            contactPerson: item?._contacts?.[0]?.contactName,
            product: productName,
            assignedTo: item?.assignedTo !== 0 ? item?._staff?.fname : name,
            createDate: getRelativeDate(item?.created_at),
            source: item?._channel_of_interaction?.[0]?.Channel,
            opportunityContactStatus: item?.reasonToClose,
            contactNumber: item?._contacts?.[0]?.contactNumber,
            subCategory: productSubCategory,
            location: item?._staff?.city,
            createdBy: item?.createdBy !== 0 ? item?._createdBy?.fname : name,
            lead_deatils: item?._channel_of_interaction?.[0],
            referred_partner: item?._refferedBy_partner?.name,
            referred_employee: item?._refferedBy_employee?.name,
            requestQuote_Type: item?.quotation_req,
            req_book_rate: item?.req_book_rate,
            purpose_id: item?.purpose_id,
            channel_of_interaction_id: item?._channel_of_interaction?.[0]?.id,
            contacts_id: item?._contacts?.[0]?.id,
            latestQuotationPDF:
              item?.interactions_id?.at(-1)?.[0]?.quotation_pdf,
            name: item?._contacts?.[0]?.contactName,
            mode_opp: item?.mode_opp,
            took_loan: item?.took_loan === true ? "With Loan" : "Without Loan",
            amount: item?.amount,
            country: item?.country,
            currency: item?.currency,
            stage_tag: item?.stage_tags,
          };
        });
      });
    } catch (error) {
      console.error("Error fetching options:", error);
    }
  };

  const getDocsForDownload = async () => {
    const auth = localStorage.getItem("authToken");
    if (auth === null) return;
    const authToken = JSON.parse(auth);
    const payload: AxiosRequestConfig<GetOpportunityByIdReqType> = {
      headers: { Authorization: `Bearer ${authToken}` },
      params: {
        contacts_id: id,
      },
    };
    try {
      const response = await downloadDoc(payload);
      if (response?.data?.length === 0) {
        return;
      }
      setDocs(
        () =>
          response?.data?.documents_id
            ?.map(
              (file: {
                doc_list_id: string;
                doc_name: string;
                document: { url: string };
                req_time: string;
              }) => {
                if (file?.document?.url) {
                  return {
                    doc_id: file.doc_list_id,
                    filename: file.doc_name,
                    file: file.document.url,
                    date: file?.req_time,
                  };
                }
                return null;
              },
            )
            .filter((document) => document !== null) as {
            doc_id: string;
            filename: string;
            file: string;
            date: string;
          }[],
      );
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error("Document view limit exceeded.", {
          description:
            "You have exceeded the limit of requests. Please try again later.",
        });
      }
      console.error("Error fetching docs:", error);
    }
  };

  const handleDownload = (url: string, filename: string) => {
    fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch((error) => toast.error("Documnet file is not available."));
  };

  const handleDownloadAll = () => {
    docs.forEach((doc: any, index) => {
      // Add a slight delay to each download to prevent overwhelming the browser
      setTimeout(() => {
        handleDownload(doc?.file?.url, doc?.filename);
      }, index * 500); // 500ms delay between each download
    });
  };

  useEffect(() => {
    getCompanyDetails();
  }, [isOpen, id]);

  useEffect(() => {
    getOpportunityList();
  }, [isOpen, id]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setSheetSide("bottom");
      } else {
        setSheetSide("right");
      }
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const { label, href } = buttonInfo();
  const isMobile = useIsMobile();

  return (
    <>
      {loading && <Spinner />}
      {isMobile ? (
        <Mobilecontactdetail />
      ) : (
        <div className="flex gap-8 h-full sm:py-2 sm:px-0 w-full flex-col font-inter font-thin px-4">
          <div className=" flex w-full justify-between items-center py-4 pl-5 border-b">
            <div className=" flex flex-col gap-2">
              <h3 className=" font-semibold leading-4 text-base text-black-label">
                <span className=" capitalize">{companyData?.full_name}</span>
              </h3>
            </div>
            {!checkLevel(LEVEL_2) && (
              <div className="flex flex-row gap-1">
                {pipelineType === "retail" && <div className=""></div>}
                {companyData?.partnerDetail?.id ? (
                  <a
                    onClick={() => {
                      if (companyData?.partnerDetail?.status != "active") {
                        navigate(
                          `/leads/partner/${companyData?.partnerDetail?.id}`
                        );
                      } else {
                        navigate(
                          `/partnerDetails/${companyData?.partnerDetail?.id}`
                        );
                      }
                    }}
                    className="text-[#165FE3] font-inter text-sm font-semibold leading-4 tracking-[0.02em] text-left cursor-pointer"
                  >
                    Partner
                  </a>
                ) : null}

                {companyData?.companyDetail?.id ? (
                  <a
                    onClick={() => {
                      if (companyData?.companyDetail?.status === "lead") {
                        navigate(
                          `/leads/corporate/${companyData?.companyDetail?.id}`
                        );
                      } else {
                        navigate(`#`);
                      }
                    }}
                    className="text-[#165FE3] font-inter text-sm font-semibold leading-4 tracking-[0.02em] text-left cursor-pointer"
                  >
                    Corporate
                  </a>
                ) : null}
              </div>
            )}
          </div>
          <div className="flex gap-5 w-full justify-between sm:flex-col">
            <div className="bg-[#fafafa] p-5 rounded-xl w-full relative">
              <div className="grid relative md:grid-cols-1 sm:grid-cols-1 w-full gap-5">
                {!checkLevel(LEVEL_2) && (
                  <div className="flex float-right absolute -top-3 -right-4 gap-2">
                    <AlertDialog open={dialogOpen}>
                      <AlertDialogTrigger
                        onClick={() => handleDialogOpen(!dialogOpen)}
                      >
                        <div className=" cursor-pointer ">
                          <svg
                            width="20"
                            height="21"
                            viewBox="0 0 20 21"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <g clipPath="url(#clip0_1689_36824)">
                              <path
                                d="M3.83594 4.82812H3.0026C2.56058 4.82813 2.13665 5.00372 1.82409 5.31628C1.51153 5.62884 1.33594 6.05276 1.33594 6.49479V13.9948C1.33594 14.4368 1.51153 14.8607 1.82409 15.1733C2.13665 15.4859 2.56058 15.6615 3.0026 15.6615H10.5026C10.9446 15.6615 11.3686 15.4859 11.6811 15.1733C11.9937 14.8607 12.1693 14.4368 12.1693 13.9948V13.1615"
                                stroke="#165FE3"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M11.3333 3.16676L13.8333 5.66676M14.9875 4.48759C15.3157 4.15938 15.5001 3.71424 15.5001 3.25009C15.5001 2.78594 15.3157 2.34079 14.9875 2.01259C14.6593 1.68438 14.2142 1.5 13.75 1.5C13.2858 1.5 12.8407 1.68438 12.5125 2.01259L5.5 9.00009V11.5001H8L14.9875 4.48759Z"
                                stroke="#165FE3"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_1689_36824">
                                <rect
                                  width="20"
                                  height="20"
                                  fill="white"
                                  transform="translate(0 0.5)"
                                />
                              </clipPath>
                            </defs>
                          </svg>
                        </div>
                      </AlertDialogTrigger>
                      <AlertDialogPortal>
                        <AlertDialogOverlay className=" bg-black-900 bg-opacity-60 animate-overlayShow fixed inset-0" />
                        <AlertDialogContent className="bg-white-A700 rounded-lg p-0 max-w-2xl w-full animate-contentShow sm:max-w-[90%]">
                          <AlertDialogHeader className="flex text-left border-b  rounded-t py-5 px-10 ">
                            <AlertDialogTitle className="font-bold capitalize p-0 text-lg text-black-label">
                              Edit {pipelineType} contact details
                            </AlertDialogTitle>
                            <AlertDialogDescription className="font-medium p-0 text-sm  text-[#7E7E7E] ">
                              Fill the required details
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <FormProvider {...methods}>
                            <div className="h-[500px] overflow-auto w-full">
                              <EditForm customerDetails={companyData} />
                            </div>
                          </FormProvider>
                          <AlertDialogFooter className="flex justify-start items-center gap-2.5 py-5 px-10 flex-row rounded-b-lg bg-gray-button-gray-lite">
                            <AlertDialogCancel
                              className=" m-0 rounded-lg border-blue-700 border py-[11px] px-4 text-blue-700"
                              asChild
                            >
                              <button
                                onClick={() => {
                                  methods.reset();
                                  methods.clearErrors();
                                  setDialogOpen(false);
                                }}
                              >
                                Cancel
                              </button>
                            </AlertDialogCancel>
                            <AlertDialogAction
                              className="rounded-lg border-blue-700 py-[11px] px-4 bg-blue-700 border text-white-A700"
                              asChild
                            >
                              <button
                                className=""
                                disabled={!methods.formState.isDirty}
                                onClick={methods.handleSubmit(
                                  handleUpdateCompany
                                )}
                              >
                                Submit
                              </button>
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialogPortal>
                    </AlertDialog>
                  </div>
                )}
                {pipelineType === "retail" && (
                  <>
                    <div className="flex items-center gap-2.5">
                      <div className="">
                        <svg
                          width="20"
                          height="21"
                          viewBox="0 0 20 21"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M4.03832 13.8267L4.03824 13.8266C2.4108 11.8712 1.31349 10.0388 0.585923 8.06396C0.111832 6.78722 -0.0725525 5.72711 0.00693179 4.72728L0.0069486 4.72707C0.0742514 3.92365 0.398545 3.22884 0.970445 2.65694L2.52038 1.107L2.52123 1.10615L2.52124 1.10616C2.97237 0.676114 3.48987 0.45 4.03108 0.45C4.57615 0.45 5.08956 0.675888 5.52435 1.10202C5.80703 1.36394 6.09357 1.65466 6.367 1.93637L6.37833 1.94804C6.51528 2.08901 6.64833 2.22598 6.78919 2.36684L8.024 3.60166C8.02404 3.60169 8.02407 3.60172 8.02411 3.60176C8.48331 4.05679 8.72675 4.58699 8.72675 5.12909C8.72675 5.67141 8.47898 6.20155 8.02417 6.65637C7.89564 6.78489 7.76746 6.91307 7.64347 7.04119L7.64331 7.04137C7.29903 7.39375 6.94201 7.75494 6.56121 8.101C6.83589 8.72675 7.22748 9.34413 7.80816 10.0791C8.97807 11.5136 10.2008 12.6286 11.5465 13.4831L11.5465 13.4831C11.6809 13.5686 11.8321 13.6463 11.9979 13.7292L11.9984 13.7295C12.1201 13.7903 12.25 13.8552 12.3774 13.9271L13.8341 12.4704C14.2769 12.0277 14.7988 11.7886 15.3448 11.7886C15.8907 11.7886 16.4087 12.0276 16.8393 12.4749C16.8394 12.475 16.8395 12.4752 16.8396 12.4753L19.3296 14.9653C19.783 15.4187 20.0125 15.9458 20.0135 16.4795C20.0146 17.013 19.7874 17.5472 19.3386 18.0149C19.1473 18.2146 18.9478 18.4099 18.7528 18.5966C18.7528 18.5966 18.7528 18.5966 18.7528 18.5966L4.03832 13.8267ZM4.03832 13.8267C6.01544 16.1901 8.39569 18.0512 11.1125 19.368L11.1129 19.3682M4.03832 13.8267L11.1129 19.3682M11.1129 19.3682C12.1459 19.8578 13.5331 20.4415 15.0805 20.5416L15.0815 20.5417M11.1129 19.3682L15.0815 20.5417M15.0815 20.5417L15.0934 20.5422M15.0815 20.5417L15.0934 20.5422M4.74134 1.91097L4.74138 1.91093C4.51188 1.6774 4.26707 1.56533 4.03108 1.56533C3.79038 1.56533 3.53711 1.67791 3.30359 1.90326L1.76225 3.4446C1.37284 3.834 1.16652 4.28297 1.11779 4.82251C1.0523 5.64574 1.21172 6.55392 1.63696 7.68926L1.63702 7.68942C2.3226 9.53968 3.35954 11.2671 4.90243 13.1244C6.77527 15.3652 9.03235 17.1303 11.6035 18.3745C12.5515 18.8257 13.8082 19.3525 15.1574 19.4389C15.2279 19.4431 15.301 19.4471 15.3697 19.4471C16.0896 19.4471 16.6436 19.2123 17.11 18.7098C17.3701 18.3986 17.656 18.121 17.9308 17.8543C17.9468 17.8388 17.9629 17.8232 17.9788 17.8077L17.9791 17.8074C18.1737 17.6211 18.3552 17.4437 18.5283 17.2583L18.5288 17.2577C18.7761 17.0003 18.8952 16.7457 18.8972 16.4999C18.8991 16.2546 18.7843 16.0077 18.5419 15.7654L16.043 13.2706L16.0417 13.2692L16.0417 13.2692C15.8836 13.099 15.6391 12.9163 15.3448 12.9163C15.1052 12.9163 14.8561 13.0362 14.6218 13.2705L13.0718 14.8205C13.0004 14.8919 12.7722 15.1169 12.419 15.1169C12.2776 15.1169 12.1373 15.0815 11.9944 15.0079C11.9943 15.0079 11.9942 15.0078 11.9941 15.0078L12.017 14.9633C12.0087 14.9592 12.0015 14.955 11.9942 14.9509C11.9869 14.9467 11.9797 14.9426 11.9714 14.9385L4.74134 1.91097ZM4.74134 1.91097L4.74308 1.91258C4.98288 2.13448 5.21025 2.36567 5.45043 2.60989C5.48723 2.64732 5.52434 2.68505 5.56184 2.72309L4.74134 1.91097ZM15.0934 20.5422C15.1843 20.5461 15.2729 20.55 15.3655 20.55M15.0934 20.5422L15.3655 20.55M15.3655 20.55C16.4105 20.55 17.2526 20.1884 17.9378 19.4448M15.3655 20.55L17.9378 19.4448M17.9378 19.4448C17.9413 19.4413 17.9518 19.4309 17.9597 19.4178L17.9378 19.4448Z"
                            fill="#999999"
                            stroke="#999999"
                            strokeWidth="0.1"
                          />
                        </svg>
                      </div>

                      <p className="font-normal text-base leading-[25px] text-black-label">
                        {companyData?.phone_number || (
                          <span className="text-gray-400">Not available</span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <div className="">
                        <svg
                          width="20"
                          height="17"
                          viewBox="0 0 20 17"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M1.10812 3.83305C0.947499 3.72606 0.815788 3.58106 0.72469 3.41092C0.633593 3.24078 0.58593 3.05077 0.585938 2.85778V2.8568C0.585938 2.16395 1.14765 1.60227 1.84054 1.60227H18.1594C18.8522 1.60227 19.4139 2.16395 19.4139 2.8568V2.85778C19.4139 3.24969 19.2181 3.61559 18.8918 3.83305C17.2992 4.89496 12.5006 8.09382 10.6963 9.29694C10.4901 9.43444 10.2478 9.50781 9.99995 9.50781C9.75212 9.50781 9.50984 9.43444 9.30365 9.29694C7.49928 8.09382 2.70066 4.89496 1.10812 3.83305Z"
                            stroke="#999999"
                            strokeWidth="1.3"
                            strokeMiterlimit="10"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M0.585938 2.85426V14.1511C0.585894 14.3159 0.618355 14.4791 0.681462 14.6314C0.744568 14.7837 0.837082 14.922 0.95371 15.0385C1.07019 15.1551 1.20853 15.2476 1.3608 15.3107C1.51307 15.3738 1.6763 15.4063 1.84113 15.4062H18.1587C18.3236 15.4063 18.4868 15.3738 18.6391 15.3107C18.7913 15.2476 18.9297 15.1551 19.0462 15.0385C19.1628 14.922 19.2553 14.7837 19.3184 14.6314C19.3815 14.4791 19.414 14.3159 19.4139 14.1511V2.85426"
                            stroke="#999999"
                            strokeWidth="1.3"
                            strokeMiterlimit="10"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <p className="font-normal text-base leading-[25px] text-black-label">
                        {companyData?.email || (
                          <span className="text-gray-400">Not available</span>
                        )}
                      </p>
                    </div>
                    <div className="flex w-full items-center gap-2.5">
                      <div className="">
                        <svg
                          width="16"
                          height="21"
                          viewBox="0 0 16 21"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M8 0.5C4.12297 0.5 0.96875 3.65422 0.96875 7.53125C0.96875 8.84117 1.3316 10.1195 2.01832 11.2286L7.59977 20.223C7.65233 20.3077 7.72564 20.3776 7.81276 20.4261C7.89988 20.4746 7.99792 20.5 8.09762 20.5H8.10227C8.20274 20.4992 8.30133 20.4726 8.38855 20.4227C8.47577 20.3728 8.54869 20.3014 8.60031 20.2152L14.0395 11.1336C14.6883 10.048 15.0312 8.80231 15.0312 7.53125C15.0312 3.65422 11.877 0.5 8 0.5ZM13.0338 10.532L8.08875 18.7885L3.01434 10.6112C2.44273 9.68805 2.13281 8.62305 2.13281 7.53125C2.13281 4.30039 4.76914 1.66406 8 1.66406C11.2309 1.66406 13.8633 4.30039 13.8633 7.53125C13.8633 8.59066 13.5738 9.62844 13.0338 10.532Z"
                            fill="#999999"
                          />
                          <path
                            d="M8 4.01562C6.06148 4.01562 4.48438 5.59273 4.48438 7.53125C4.48438 9.45738 6.03582 11.0469 8 11.0469C9.9884 11.0469 11.5156 9.43621 11.5156 7.53125C11.5156 5.59273 9.93852 4.01562 8 4.01562ZM8 9.88281C6.7009 9.88281 5.64844 8.82684 5.64844 7.53125C5.64844 6.23891 6.70766 5.17969 8 5.17969C9.29234 5.17969 10.3477 6.23891 10.3477 7.53125C10.3477 8.80793 9.31969 9.88281 8 9.88281Z"
                            fill="#999999"
                          />
                        </svg>
                      </div>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger
                            className="flex cursor-pointer items-end"
                            asChild
                          >
                            <p className=" w-full font-normal text-base leading-[25px] text-black-label">
                              {companyData?.address_line_1 ? (
                                `${companyData?.address_line_1
                                  ?.charAt(0)
                                  ?.toUpperCase()}${companyData?.address_line_1?.slice(
                                  1,
                                  30
                                )}`
                              ) : (
                                <span className="text-gray-400">
                                  Not available
                                </span>
                              )}
                            </p>
                          </TooltipTrigger>
                          <TooltipContent
                            side="bottom"
                            className="bg-black-900 text-white-A700 max-w-[200px] whitespace-normal break-words border-0"
                          >
                            <p className=" w-full font-normal text-base leading-[25px] ">
                              {companyData?.contactAddress?.at(-1)}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Right div */}
            <div className="bg-[#fafafa] p-5 flex sm:flex-row gap-3 w-full  rounded-xl relative">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger className="cursor-pointer" asChild>
                    {isEditing ? (
                      <div className="relative flex flex-col gap-2  items-start">
                        <textarea
                          value={inputValue}
                          onChange={handleInputChange}
                          className="border rounded p-2 sm:w-full w-[400px] resize-none  focus:outline-none overflow-auto "
                          placeholder="Enter your notes here"
                          rows={4}
                          style={{ minHeight: "100px", maxHeight: "200px" }}
                        />
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={handleSave}
                            className="text-white-A700 cursor-pointer bg-blue-700 text-sm rounded-lg py-1.5 px-3 hover:bg-blue-700"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setIsEditing(false)}
                            className="text-gray-600 bg-gray-300 cursor-pointer text-sm rounded-lg py-1.5 px-3 hover:bg-gray-400"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="whitespace-nowrap overflow-hidden text-ellipsis max-w-[400px] h-[100px]">
                        <p className="whitespace-normal break-words h-full overflow-hidden overflow-ellipsis">
                          {companyData?.notes && companyData.notes.length > 200
                            ? `${companyData.notes.substring(0, 200)}...`
                            : companyData?.notes || "No notes available"}
                        </p>
                      </div>
                    )}
                  </TooltipTrigger>
                  <TooltipContent
                    side="bottom"
                    className="bg-black-900 text-white-A700 max-w-xs whitespace-normal break-words border-0"
                  >
                    <p className="whitespace-normal max-w-xs break-words">
                      {companyData?.notes}
                    </p>
                  </TooltipContent>
                </Tooltip>

                {/* Right div - Edit button */}
                {!checkLevel(LEVEL_2) && (
                  <div className="flex ml-auto cursor-pointer">
                    {!isEditing && (
                      <svg
                        onClick={handleEditClick}
                        width="20"
                        height="21"
                        viewBox="0 0 20 21"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="ml-2 cursor-pointer"
                      >
                        <g clipPath="url(#clip0_1689_36824)">
                          <path
                            d="M3.83594 4.82812H3.0026C2.56058 4.82813 2.13665 5.00372 1.82409 5.31628C1.51153 5.62884 1.33594 6.05276 1.33594 6.49479V13.9948C1.33594 14.4368 1.51153 14.8607 1.82409 15.1733C2.13665 15.4859 2.56058 15.6615 3.0026 15.6615H10.5026C10.9446 15.6615 11.3686 15.4859 11.6811 15.1733C11.9937 14.8607 12.1693 14.4368 12.1693 13.9948V13.1615"
                            stroke="#165FE3"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M11.3333 3.16676L13.8333 5.66676M14.9875 4.48759C15.3157 4.15938 15.5001 3.71424 15.5001 3.25009C15.5001 2.78594 15.3157 2.34079 14.9875 2.01259C14.6593 1.68438 14.2142 1.5 13.75 1.5C13.2858 1.5 12.8407 1.68438 12.5125 2.01259L5.5 9.00009V11.5001H8L14.9875 4.48759Z"
                            stroke="#165FE3"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_1689_36824">
                            <rect
                              width="20"
                              height="20"
                              fill="white"
                              transform="translate(0 0.5)"
                            />
                          </clipPath>
                        </defs>
                      </svg>
                    )}
                  </div>
                )}
              </TooltipProvider>
            </div>
            <div className="flex gap-2 flex-col">
              {companyData?.customer ? (
                <DropdownMenu>
                  <DropdownMenuTrigger className="text-[#23AB28] px-4 py-2 flex items-center justify-between bg-[#E1FFE2] rounded-md whitespace-nowrap font-inter text-sm font-medium leading-5 w-full">
                    <p className="w-full">Active</p>
                    {!checkLevel(LEVEL_2) && (
                      <svg
                        width="14"
                        height="8"
                        viewBox="0 0 14 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M7.0026 6.38987L1.18796 0.511079C1.1411 0.462677 1.08547 0.424413 1.02428 0.398498C0.963095 0.372583 0.89757 0.359533 0.831498 0.360103C0.765426 0.360673 0.700119 0.374853 0.639356 0.40182C0.578593 0.428787 0.52358 0.468007 0.477502 0.51721C0.431423 0.566414 0.395194 0.624623 0.37091 0.68847C0.346626 0.752317 0.33477 0.820536 0.336028 0.889175C0.337286 0.957815 0.351632 1.02551 0.378237 1.08835C0.404843 1.15119 0.443178 1.20793 0.491026 1.25527L6.65414 7.48575C6.74743 7.58005 6.87245 7.63281 7.0026 7.63281C7.13276 7.63281 7.25778 7.58005 7.35107 7.48575L13.5142 1.25527C13.562 1.20793 13.6004 1.15119 13.627 1.08835C13.6536 1.02551 13.6679 0.957814 13.6692 0.889174C13.6704 0.820535 13.6586 0.752317 13.6343 0.68847C13.61 0.624623 13.5738 0.566413 13.5277 0.51721C13.4816 0.468007 13.4266 0.428787 13.3659 0.40182C13.3051 0.374852 13.2398 0.360673 13.1737 0.360103C13.1076 0.359532 13.0421 0.372582 12.9809 0.398498C12.9197 0.424412 12.8641 0.462677 12.8173 0.511078L7.0026 6.38987Z"
                          fill="#23AB28"
                        />
                      </svg>
                    )}
                  </DropdownMenuTrigger>
                  {/* <DropdownMenuContent>
                  <DropdownMenuItem>Profile</DropdownMenuItem>
                  <DropdownMenuItem>Billing</DropdownMenuItem>
                  <DropdownMenuItem>Team</DropdownMenuItem>
                  <DropdownMenuItem>Subscription</DropdownMenuItem>
                </DropdownMenuContent> */}
                </DropdownMenu>
              ) : (
                !checkLevel(LEVEL_2) && (
                  <button
                    onClick={() => {
                      dispatch(setPhone(companyData?.contactNumber));
                      dispatch(setEmail(companyData?.contactEmail));
                      dispatch(setContactId(id as string));
                      navigate("/customerregistration", {
                        state: {
                          contact_name: companyData?.contactName,
                          contact_number: companyData?.contactNumber,
                          contact_email: companyData?.contactEmail,
                          currentpath: "contactDetails",
                          contactID: companyData?.id,
                          pathName: "ContactPage",
                        },
                      });
                    }}
                    className="flex items-center gap-2 whitespace-nowrap rounded-md border border-blue-700 text-blue-700 px-4 py-2"
                  >
                    <FaFingerprint size={22} />
                    Start KYC
                  </button>
                )
              )}
              <DropdownMenu>
                <DropdownMenuTrigger className="text-[#333333] px-4 py-2 rounded-md whitespace-nowrap font-inter text-sm font-medium leading-5 text-center border border-[#165FE3]">
                  More Options
                </DropdownMenuTrigger>

                <DropdownMenuContent className="p-2">
                  {!checkLevel(LEVEL_2) && (
                    <>
                      {companyData?.partner_linked === 0  && (
                        <LinkPartner
                          contact_name={companyData?.full_name}
                          showLinkPartnerButton={true}
                          otherProps={{ contact_id: id }}
                        >
                          <DropdownMenuItem
                            onSelect={(e) => e.preventDefault()}
                          >
                            <span className="text-[#333333] rounded-lg whitespace-nowrap font-inter text-sm font-medium leading-5">
                              Link Partner
                            </span>
                          </DropdownMenuItem>
                        </LinkPartner>
                      )}
                      {companyData?.entity_table_id === 0 && (
                        <LinkCorporate
                          contact_name={companyData?.full_name}
                          showLinkPartnerButton={true}
                          otherProps={{ contact_id: id }}
                        >
                          <DropdownMenuItem
                            onSelect={(e) => e.preventDefault()}
                          >
                            <span className="text-[#333333] rounded-lg whitespace-nowrap font-inter text-sm font-medium leading-5">
                              Link Corporate
                            </span>
                          </DropdownMenuItem>
                        </LinkCorporate>
                      )}
                    </>
                  )}
                  <div>
                    <DocumentModal fetchFn={getDocsForDownload} docs={docs}>
                      <button className="flex items-center gap-2.5 py-2 px-2.5">
                        <p className="text-blue-700 font-semibold text-sm">
                          {checkLevel(LEVEL_2) ? "View Documents" : "Documents"}
                        </p>
                      </button>
                    </DocumentModal>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <div className="flex-1 relative flex flex-col overflow-auto gap-2">
            <div className=" flex w-full justify-between items-center sm:px-0">
              <div className=" flex flex-col gap-2 ">
                <Tabs
                  className="flex -mb-6 sm:-mb-2 gap-2"
                  value={pipelineType}
                  onValueChange={(value) => {
                    setActiveTab(value);
                  }}
                >
                  <TabsList className="h-[44px] p-0">
                    {tablist.map((tab) => (
                      <TabsTrigger
                        disabled={!tab.enabled}
                        key={tab.value}
                        value={tab.value}
                        className={` py-[18px] px-6 disabled:opacity-40 text-sm h-[44px] min-w-[120px] font-semibold ${
                          tab.value === activeTab
                            ? "border-blue-700 bg-navbar-menu-bg border-t-2 text-blue-700"
                            : " text-black-label"
                        }`}
                      >
                        {tab.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              </div>
              <div className="flex gap-3 sm:hidden">
                {!checkLevel(LEVEL_2) && (
                  <button
                    className="disabled:opacity-40 border border-blue-700 text-[#165FE3] font-semibold px-4 py-3 rounded-lg"
                    onClick={() => {
                      dispatch(setIsReferralMade(true));
                      navigate("/lead/getquote", {
                        state: {
                          id: id,
                          contact_name: companyData?.contactName,
                          contact_number: companyData?.contactNumber,
                          contact_email: companyData?.contactEmail,
                          currentpath: "contactDetails",
                          contactID: companyData?.id,
                        },
                      });
                    }}
                  >
                    Get Quote
                  </button>
                )}
                {!checkLevel(LEVEL_2) && (
                  <button
                    className="disabled:opacity-40 border border-blue-700 text-[#165FE3] font-semibold px-4 py-3 rounded-lg"
                    onClick={() => {
                      dispatch(setIsReferralMade(true));
                      navigate("/lead/retails/createlead", {
                        state: {
                          id: id,
                          contact_name: companyData?.contactName,
                          contact_number: companyData?.contactNumber,
                          contact_email: companyData?.contactEmail,
                          currentpath: "contactDetails",
                          contactID: companyData?.id,
                        },
                      });
                    }}
                  >
                    New Referral Lead
                  </button>
                )}
                <button
                  onClick={() =>
                    navigate("/lead/retails/createlead", {
                      state: {
                        id,
                        contact_name: companyData?.contactName,
                        contact_number: companyData?.contactNumber,
                        contact_email: companyData?.contactEmail,
                        currentpath: "contactDetails",
                        contactID: companyData?.id,
                      },
                    })
                  }
                  className=" bg-blue-700 border border-blue-700 text-white-A700 px-4 py-3 rounded-lg"
                >
                  {label}
                </button>
              </div>
            </div>
            {activeTab === "leads" && (
              <DataTable
                columns={filteredColumns}
                data={opportunityList}
                onRowClick={handleRowClick}
                highlightRow={true}
                isToolbarVisible={false}
                isPaginationVisible={opportunityList.length > 10}
                defaultSorting={[{ id: "id", desc: true }]}
              />
            )}
            {activeTab === "orders" && (
              <DataTable
                columns={[
                  {
                    header: "Order ID",
                  },
                  {
                    header: "Product",
                  },
                  {
                    header: "Assigned To",
                  },
                  {
                    header: "Created by",
                  },
                  {
                    header: "Lead Status",
                  },
                ]}
                data={[]}
                onRowClick={handleRowClick}
                highlightRow={true}
                isToolbarVisible={false}
                isPaginationVisible={false}
              />
            )}
            {isRowDetailsOpen &&
              (checkLevel(LEVEL_2) ? (
                <LeadOverView
                  isOpen={isRowDetailsOpen}
                  onOpenChange={setIsRowDetailsOpen}
                  rowData={selectedRowData}
                  getOpportunityList={getOpportunityList}
                />
              ) : (
                <RowDetailsSheet
                  isOpen={isRowDetailsOpen}
                  onOpenChange={setIsRowDetailsOpen}
                  rowData={selectedRowData}
                  getOpportunityList={getOpportunityList}
                />
              ))}

            <div className="p-2.5 rounded-[20px] hidden mt-3 sm:flex text-center border border-blue-700">
              <button
                onClick={() =>
                  navigate(href, {
                    state: {
                      id,
                      contact_name: companyData?.contactName,
                      contact_number: companyData?.contactNumber,
                      contact_email: companyData?.contactEmail,
                    },
                  })
                }
                type="button"
                className="px-4 py-[11px] w-full rounded-lg bg-blue-700 text-white-A700 border"
              >
                {label}
              </button>
            </div>
          </div>
        </div>
      )}
      <Toaster theme="dark" position="top-right" />
    </>
  );
}

interface RowDetailsSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  rowData: OpportunityContactType | null;
  getOpportunityList?: () => void;
  handleCloseModal?: () => void;
}

export const RowDetailsSheet: React.FC<RowDetailsSheetProps> = ({
  isOpen,
  onOpenChange,
  getOpportunityList,
  rowData,
  handleCloseModal,
}) => {
  const methods = useForm<OrderDetail>({
    resolver: zodResolver(orderDetailSchema),
    mode: "onChange",
  });

  const [selectedChannelOption, setSelectedChannelOptions] =
    useState<string>("");
    const dispatch = useAppDispatch()
  const [location, setLocation] = useState<{
    latitude: string;
    longitude: string;
  } | null>(null);
  const [interactionNote, setInteractionNote] = useState("");
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [showBothButtons, setShowBothButtons] = useState(true);
  const [detailNote, setDetailNote] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [date, setDate] = useState<Date>();
  const [opportunityDetails, setOpportunityDetails] = useState<any>({});
  const [productDetails, setProductDetails] = useState<any>({});
  const [purposeIDDetails, setpurposeIDDetails] = useState<any>({});
  const [interactionList, setInteractionList] = useState<
    OpportunityInteractionType[]
  >([]);
  const [forexLeadDetails, setForexLeadDetails] = useState<any>({});
  const [corporateLeadDetails, setCorporateLeadDetails] = useState();
  const [contactDetails, setContactDetails] = useState<{
    contactPerson: string;
    contactEmail: string;
    contactNumber: string;
    location: string;
    notes: string;
    oppId: string;
    contactId: string;
    partnerId: number;
    customerId: number;
  }>();
  const [status, setStatus] = useState<any>();
  const { pipelineType } = useAppSelector((state) => state.CreateOpportunity);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [selectedMode, setSelectedMode] = useState<string[]>([]);
  const [expires, setExpires] = useState("1");
  const [channelDetails, setChannelDetails] = useState<{
    channel: string;
    source: string;
  }>();
  const [channel, setChannel] = useState("whatsapp");
  const { id } = useParams();

  const [popupnotes, setPopupNotes] = useState<string>("");
  const [staffs, setStaffs] = useState<Option[]>([]);
  const [selectedStaffOptions, setSelectedStaffOptions] = useState<Option[]>(
    [],
  );
  const [pancardDetails, setPancardDetails]= useState<any>("");
  const [remittanceProductListData, setRemittanceProductListData] = useState<any>({})
  const [extraDetails, setExtraDetails] = useState<any>({});
  const [summary, setSummary] = useState("");
  const [followupNote, setFollowupNote] = useState("");
  const [isClearReminder, setIsClearReminder] = useState(false);
  const [editReminder, setEditReminder] = useState(false);
  const [companyData, setCompanyDetails] = useState<any>([]);
  const [selectedType, setSelectedType] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("interaction");
  const [selectedSubOption, setSelectedSubOption] = useState("");
  const [isChatSubmenuOpen, setIsChatSubmenuOpen] = useState(false);
  const isMobile = useIsMobile();

  const methods2 = useForm({
    defaultValues: {
      contactNumber: contactDetails?.contactNumber,
      contactEmail: contactDetails?.contactEmail,
      contactName: contactDetails?.contactPerson,
      contactAddress: [contactDetails?.location],
    },
  });

  useEffect(() => {
    methods2.reset();
    methods2.reset({
      contactNumber: contactDetails?.contactNumber,
      contactEmail: contactDetails?.contactEmail,
      contactName: contactDetails?.contactPerson,
      contactAddress: [contactDetails?.location],
    });
  }, [contactDetails]);

  /**
   * Best effort, never a gate.
   *
   * Xano stored `geo_coordinates` on an interaction and the screen refused
   * to submit without them. Nothing stores them now — the adapter does not
   * even send the field — so blocking on a browser permission prompt was
   * stopping people logging an update to a lead in exchange for nothing.
   */
  const requestLocation = async () => {
    setLoading(true);
    if ("geolocation" in navigator) {
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject);
        });
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setLoading(false);
        return true;
      } catch (error) {
        console.error("Error getting location:", error);
        setLoading(false);
        return false;
      }
    } else {
      console.error("Geolocation is not supported");
      setLoading(false);
      return false;
    }
  };

  useEffect(() => {
    const getLocation = async () => {
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject);
        });
        setLocation({
          latitude: position?.coords?.latitude,
          longitude: position?.coords?.longitude,
        });
        setLoading(false);
        return true;
      } catch (error) {
        setLoading(false);
        return false;
      }
    };

    getLocation();
  }, []);

  const handleChangeType = (newValue: string) => {
    setSelectedChannelOptions(newValue);
  };

  const handleNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setInteractionNote(value);
  };

  const createOrder = async () => {
    setLoading(true);
    try {
      const result = await convertLeadToOrder({
        leadId: String(rowData?.id ?? ""),
        invoiceNo: String(methods.getValues("invoice_id") ?? ""),
        invoiceDate: methods.getValues("invoice_date"),
        amount: Number(methods.getValues("amount")),
        notes: methods.getValues("notes") || "",
        invoiceFile: methods.getValues("invoice_file") ?? null,
      });
      if (result.warning) toast.warning(result.warning);
      else toast.success(`Order ORD-${result.orderNo} created`);
      methods.reset();
      await getOpportunityList?.();
    } catch (error) {
      console.error("Create order error:", error);
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : "Failed to create order",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDialogOpen = (open: boolean) => {
    setDialogOpen(open);
  };

  const handleUpdateCompany = async (value: any) => {
    setLoading(true);
    const auth = localStorage.getItem("authToken");
    if (auth === null) return;
    const authToken = JSON.parse(auth);

    let payload: AxiosRequestConfig<PutUpdateCompanyDetailsReqType> = {
      headers: { Authorization: `Bearer ${authToken}` },
    };

    if (pipelineType === "corporate") {
      payload.data = {
        id: id as string,
        company_name: value?.company_name,
        email: value?.company_email,
        contactPersonType: value?.contact_person_type?.value,
        contactName: value?.contact_person_name,
        contactNumber: value?.contact_person_phone,
        pipelineType: "coorporate",
      };
    }
    if (pipelineType === "partner") {
      payload.data = {
        id: id as string,
        contactName: value?.entity_name,
        contactEmail: value?.company_email,
        typeOfPartner: value?.entity_type?.value,
        pipelineType: "partner",
      };
    }
    if (pipelineType === "retail") {
      payload.data = {
        id: contactDetails?.contactId as string,
        contactName: value?.contact_name,
        contactNumber: value?.contact_number,
        contactEmail: value?.contact_email,
        pipelineType: "retail",
        contactAddress: [
          ...[contactDetails?.location as string],
          value?.contact_address,
        ],
      };
    }

    try {
      const res = (await PutUpdateCompanyDetails(payload)) as any;
      if (res.status === 200) {
        toast.success("Contact details updated successfully");
        setLoading(false);
        handleDialogOpen(false);
        await getCompanyDetails();
      }
    } catch (error) {
      console.error("Error in updating company details:", error);
      toast.error("Failed to update contact details");
      setLoading(false);
    }
  };

  const getCompanyDetails = async () => {
    const auth = localStorage.getItem("authToken");
    if (auth === null) return;
    const authToken = JSON.parse(auth);
    const payload: AxiosRequestConfig<GetCompanyByIdReqType> = {
      headers: { Authorization: `Bearer ${authToken}` },
      data: {
        id,
      },
    };
    try {
      let response;
      if (!id) return;
      if (pipelineType === "corporate") {
        response = (await getCompanyById(payload)) as any;
      } else {
        response = (await getContactDetailById(payload)) as any;
      }
      setCompanyDetails(response?.data);
    } catch (error) {
      console.error("Error fetching options:", error);
    }
  };

  const getOpportunity = async () => {
    const auth = localStorage.getItem("authToken");
    if (auth === null) return;
    const authToken = JSON.parse(auth);
    const payload: AxiosRequestConfig<GetOpportunityByIdReqType> = {
      headers: { Authorization: `Bearer ${authToken}` },
      data: {
        id: rowData?.id as string,
      },
    };
    try {
      const response = (await getForexOpportunityById(payload)) as any;
      if (response?.data?.length === 0) {
        return;
      }
      setForexLeadDetails(response?.data?.leadDetail?.forex_lead_details);
      const partner = response?.data?.partner;
      response.data = response?.data?.leadDetail;
      let finalCreatedBy;

      setPancardDetails(response?.data?.extra_detail)
    setRemittanceProductListData(() => ({
      id: response?.data?.id,
      productName: response?.data?._product?.name,
      subCategory: response?.data?._product?.subCategory,
      Mode: response?.data?.mode_opp,
      isOpportunityOpen: response?.data?.isOpen,
      took_loan: response?.data?.took_loan
    }));


      if (rowData?.channel === "Website") {
        finalCreatedBy = "System";
      } else {
        finalCreatedBy =
          rowData?.pipelineType?.toLowerCase() === "coorporate"
            ? "Corporate Portal"
            : response?.data?._createdBy?.fname || "N/A";
      }
      setOpportunityDetails(() => {
        return {
          id: response?.data?.id,
          reasonToClose: response?.data?.reasonToClose,
          contactPerson: response?.data?._coorporate_company?.[0]?.contact_name,
          email: response?.data?._coorporate_company?.[0]?.email,
          product: response?.data?._product?.name,
          productSubcategory: response?.data?._product?.subCategory,
          assignedTo: response?.data?._staff?.fname,
          channel: response?.data?._channel_of_interaction?.[0]?.Channel,
          createDate: new Date(response?.data?.created_at).toString(),
          isOpportunityOpen:
            response?.data?.isOpen === "true" ? "open" : "close",
          source: response?.data?.source,
          opportunityContactStatus:
            response?.data?._channel_of_interaction?.[0]?.Channel,
          createdBy: finalCreatedBy,
          documents: response?.data?.documents as any[],
          amount: response?.data?.amount as string,
          country: response?.data?.country as string,
          mode: response?.data?.mode_opp,
          forex_orderID: response?.data?.generic_order_details_id,
          generic_order_Status: response?.data?.generic_order_details?.status,
          generic_order: response?.data,
          isLoan: ((response?.data?._product?.name === "Education payment" && response?.data?.extra_detail?.purpose_id !== 20 && response?.data?.mode_opp !== 'ViaTransfer')) ? response?.data?.took_loan ? "With Loan" : "Without Loan" : "",
          ProductDetailsList: response?.data?.product_details
        };
      });
      setProductDetails(() => {
        return {
          id: response?.data?._product?.id,
          name: response?.data?._product?.name,
          subCategory: response?.data?._product?.subCategory,
        };
      });
      setpurposeIDDetails(()=> {
        return {
           id: response?.data?.extra_detail?.purpose_id,
          pancard: response?.data?.extra_detail?.pan_card,
        }
      })
      setContactDetails(() => {
        const address = response?.data?._customers?.address_id?.at(-1)?._address_single;
        const full_address = `${address?.house_number && address?.house_number +',' || ''} ${address?.street_number && address?.street_number + ',' || ''} ${address?.landmark && address?.landmark +',' || ''} ${address?.City && address?.City +',' || ''} ${address?.State && address?.State +',' ||''} ${address?.country && address?.country +',' || ''} ${address?.pincode && address?.pincode +',' || ''}`;
        const isAddressValid = full_address?.split(",").every((item) => item.trim() !== "");
        return {
          contactPerson: response?.data?._contacts?.[0]?.contactName || response?.data?._entity_table?.entity_name || response?.data?._customers?.full_name,
          contactEmail: response?.data?._contacts?.[0]?.contactEmail || response?.data?._entity_table?.email || response?.data?._customers?.email,
          contactNumber: response?.data?._contacts?.[0]?.contactNumber || response?.data?._entity_table?.phone_number || response?.data?._customers?.phone_number,
          location:
            response?.data?._contacts?.[0]?.contactAddress?.at(-1) || (!isAddressValid ? full_address : ''),
          notes: response?.data?._contacts?.[0]?.notes,
          contactId: response?.data?._contacts?.[0]?.id || response?.data?._entity_table?.id,
          oppId: response?.data?.id,
          partnerId: partner,
          customerId: response?.data?._contacts?.[0]?.linked_customer_id
        };
      });
      setExtraDetails(() => {
        return {
          branchName: response?.data?._outlet_branch?.name,
          staffName: response?.data?._refferedBy_staff?.fname,
          partnerName: response?.data?._refferedBy_partner?.name,
          staffId: response?.data?.refferedBy_staff,
          partnerId: response?.data?.refferedBy_partner,
          followup_time: response?.data?.followup_time,
          followup_note: response?.data?.reminder_note,
          note: response?.data?.notes,
          note_last_edited: response?.data?.note_last_edited,
          ref_contactName:
            response?.data?._refferedBy_contact?.[0]?.contactName,
          quote_requested: response?.data?.quotation_req,
          stage_tag: response?.data?.stage_tags,
        };
      });
      setChannelDetails(() => {
        return {
          channel: response?.data?._orderInteraction?.[0]?.Channel,
          source: response?.data?._orderInteraction?.[0]?.source,
        };
      });
      setNote(() => {
        return response?.data?.interactions_id?.[0]?.[0]?.note;
      });
      setStatus(() => {
        const status = opportunity_status_options.find(
          (status) =>
            status.value ===
            (response?.data?.isOpen === "true" ? "open" : "close"),
        );
        return status;
      });
      const name = localStorage.getItem("owner_name")?.toString();
      setInteractionList(() => {
        return response?.data?.interactions_id?.map((item: any) => ({
          id: item[0]?.id,
          interactionDateandTime: item[0]?.created_at,
          interactionSource: item[0]?._channel_of_interaction?.[0]?.Channel,
          interactionNote: item[0]?.note as string,
          interactionStatus:
            item[0]?.isOpen === true || item[0]?.isOpen === "true"
              ? "open"
              : "close",
          channel: item[0]?._channel_of_interaction?.[0]?.Channel,
          createdBy: item?.createdBy !== 0 ? item?._createdBy?.fname : name,
          channelType: item[0]?._channel_of_interaction?.[0]?.Type,
          channelSource: item[0]?._channel_of_interaction?.[0]?.Source,
          meetingStaffs: item[0]?.meetingStaff_id,
          quotationURL: item[0]?.quotation_pdf,
          quotation_corp: item[0]?.quotation_corp,
        }));
      });
    } catch (error) {
      console.error("Error fetching options:", error);
    }
  };

  const handleUpdateInteractionStatus = async (id: string) => {
    setLoading(true);
    const auth = localStorage.getItem("authToken");
    if (auth === null) return;
    const authToken = JSON.parse(auth);
    const payload: AxiosRequestConfig<PutUpdateInteractionStatusReqType> = {
      headers: { Authorization: `Bearer ${authToken}` },
      data: {
        id,
        isOpen: "false",
      },
    };
    try {
      const res = (await putUpdateInteractionStatus(payload)) as any;
      if (res.status === 200) {
        toast.success("Interaction status updated successfully");
        await getOpportunity();
        setLoading(false);
      }
    } catch (error) {
      toast.error("Failed to update interaction status");
      setLoading(false);
    }
  };

  const clearReminder = async (opportunity_id: string) => {
    const auth = localStorage.getItem("authToken");
    if (auth === null) return;
    const authToken = JSON.parse(auth);
    const payload: AxiosRequestConfig<ClearReminderReqType> = {
      headers: { Authorization: `Bearer ${authToken}` },
      data: { opportunity_id },
    };
    try {
      await clearReminderPatch(payload);
      await getOpportunity();
      setFollowupNote("");
      setDate(undefined);
      toast.success("Reminder cleared successfully");
    } catch (error) {
      toast.error("Failed to clear reminder");
    }
  };

  const handleUpdateNote = async (id: string, note: string) => {
    setLoading(true);
    const auth = localStorage.getItem("authToken");
    if (auth === null) return;
    const authToken = JSON.parse(auth);
    const payload: AxiosRequestConfig<PutUpdateOpportunityStatusReqType> = {
      headers: { Authorization: `Bearer ${authToken}` },
      data: {
        id,
        notes: note,
      },
    };
    try {
      const res = (await putUpdateOpportunityStatus(payload)) as any;
      if (res.status === 200) {
        toast.success("Note updated successfully");
        await getOpportunity();
        setLoading(false);
      }
    } catch (error) {
      toast.error("Failed to update note");
      setLoading(false);
    }
  };

  const handleUpdateOpportunity = async (
    reason: string,
    followup_time: Date | string | null,
    followup_note: string,
  ) => {
    setLoading(true);
    const auth = localStorage.getItem("authToken");
    if (auth === null) return;
    const authToken = JSON.parse(auth);
    let payload: AxiosRequestConfig<PutUpdateOpportunityStatusReqType> = {
      headers: { Authorization: `Bearer ${authToken}` },
    };

    if (followup_time === null) {
      payload.data = {
        id: rowData?.id as string,
        followup_time: undefined,
        reminder_note: "",
      };
    }
    if (followup_time) {
      payload.data = {
        id: rowData?.id as string,
        followup_time: date?.getTime().toString(),
        reminder_note: followup_note,
      };
    }

    if (reason && !followup_time && !followup_note) {
      payload.data = {
        id: rowData?.id as string,
        reasonToClose: reason,
        isopen: "false",
      };
    }
    if (!reason && !followup_time && !followup_note) {
      payload.data = {
        assignedto_id: relationShipManagerSelectedOption?.value as string,
        id: rowData?.id as string,
      };
    }
    try {
      const res = (await putUpdateOpportunityStatus(payload)) as any;
      if (res.status === 200) {
        if (followup_time) {
          toast.success("Reminder updated successfully");
        } else {
          toast.success("Lead status updated successfully");
        }
        setReason("");
        await getOpportunityList?.();
        await getOpportunity();
        setLoading(false);
      }
    } catch (error) {
      toast.error("Failed to update lead status");
      setLoading(false);
    }
  };

  const handleAddInteraction = async () => {
    try {
      setLoading(true);
      // Recorded when the browser offers it, ignored when it does not.
      await requestLocation();

      setDialogOpen(true);
      const auth = localStorage.getItem("authToken");
      if (auth === null) return;

      const authToken = JSON.parse(auth);

      const payload: AxiosRequestConfig<PostCorporateInteractionsReqType> = {
        headers: { Authorization: `Bearer ${authToken}` },
        data: {
          opportunity_id: rowData?.id as string,
          remarks: "",
          meeting_time: date,
          meetingStaff_id: selectedStaffOptions?.map((staff) =>
            Number(staff.value),
          ),
          note: interactionNote,
          channel_of_interaction_id: selectedChannelOption as string,
          quotation_pdf: "",
          geo_coordinates: {
            type: "point",
            data: {
              lng: location?.longitude as string,
              lat: location?.latitude as string,
            },
          },
        },
      };

      const res = (await postCorporateInteractions(payload)) as any;

      if (res.status === 200) {
        !checkLevel(LEVEL_2) && toast.success("Interaction added successfully");
        setInteractionNote("");
        setSelectedChannel(null);
        handleReset();
        setSelectedChannelOptions("");
        setLoading(false);
      }
    } catch (error) {
      toast.error("Failed to add interaction");
      setLoading(false);
    }
  };

  const InteractionColumn = useMemo<ColumnDef<OpportunityInteractionType>[]>(
    () => [
      {
        id: "interactionDateandTime",
        accessorKey: "interactionDateandTime",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Date and Time" />
        ),
        cell: ({ row }) => {
          return (
            <div className="flex flex-col gap-1.5">
              <p className="">
                {getRelativeDate(row.getValue("interactionDateandTime"))}
              </p>
              <p className="capitalize font-normal text-sm text-[#666666]">
                {row?.original?.createdBy}
              </p>
            </div>
          );
        },
        filterFn: (row, id, value) => {
          return String(row.getValue(id))
            .toLowerCase()
            .includes(String(value).toLowerCase());
        },
      },
      {
        accessorKey: "interactionSource",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Channel" />
        ),
        cell: ({ row }) => {
          const newSource = (channel_options as ChannelOptions)?.[
            (row?.original?.channelType as string)?.toLowerCase()
          ]?.find(
            (item) =>
              item.value?.toLowerCase() ===
              (row.getValue("interactionSource") as string)
                ?.toLowerCase()
                .split(" ")
                .join(""),
          );

          if (row.getValue("interactionSource") === "Website") {
            return <p className="capitalize">Website</p>;
          }

          if (newSource?.value === "meeting") {
            return (
              <div className="h-full max-w-[100px] items-start flex gap-10 ">
                <div className="flex flex-col gap-1">
                  <p className="capitalize">{newSource?.label}</p>
                  {row?.original?.meetingStaffs?.length > 0 && (
                    <p className="capitalize whitespace-nowrap font-normal text-sm text-[#666666]">
                      {row?.original?.meetingStaffs?.[0]?.fname as string}
                      <span>
                        {row?.original?.meetingStaffs?.length > 1 &&
                          ` + ${row?.original?.meetingStaffs?.length - 1} more`}
                      </span>
                    </p>
                  )}
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger className="w-full flex items-end " asChild>
                      <div className="pt-1">
                        <svg
                          width="15"
                          height="14"
                          viewBox="0 0 15 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M7.23438 0.09375C5.86845 0.09375 4.5332 0.498795 3.39747 1.25766C2.26174 2.01653 1.37655 3.09514 0.853835 4.35709C0.331117 5.61904 0.194351 7.00766 0.46083 8.34734C0.727309 9.68702 1.38507 10.9176 2.35092 11.8835C3.31678 12.8493 4.54735 13.5071 5.88704 13.7735C7.22672 14.04 8.61533 13.9033 9.87729 13.3805C11.1392 12.8578 12.2178 11.9726 12.9767 10.8369C13.7356 9.70118 14.1406 8.36593 14.1406 7C14.1387 5.16894 13.4105 3.41343 12.1157 2.11868C10.8209 0.823925 9.06544 0.0956836 7.23438 0.09375ZM6.96875 3.28125C7.12636 3.28125 7.28043 3.32799 7.41147 3.41555C7.54252 3.50311 7.64466 3.62756 7.70497 3.77317C7.76528 3.91878 7.78106 4.07901 7.75032 4.23359C7.71957 4.38817 7.64367 4.53016 7.53223 4.6416C7.42078 4.75305 7.27879 4.82894 7.12422 4.85969C6.96964 4.89044 6.80941 4.87466 6.6638 4.81434C6.51819 4.75403 6.39374 4.65189 6.30618 4.52084C6.21861 4.3898 6.17188 4.23573 6.17188 4.07812C6.17188 3.86678 6.25583 3.66409 6.40528 3.51465C6.55472 3.36521 6.75741 3.28125 6.96875 3.28125ZM7.76563 10.7188C7.48384 10.7188 7.21358 10.6068 7.01433 10.4076C6.81507 10.2083 6.70313 9.93804 6.70313 9.65625V7C6.56223 7 6.42711 6.94403 6.32748 6.8444C6.22785 6.74477 6.17188 6.60965 6.17188 6.46875C6.17188 6.32785 6.22785 6.19273 6.32748 6.0931C6.42711 5.99347 6.56223 5.9375 6.70313 5.9375C6.98492 5.9375 7.25517 6.04944 7.45443 6.2487C7.65369 6.44796 7.76563 6.71821 7.76563 7V9.65625C7.90652 9.65625 8.04165 9.71222 8.14128 9.81185C8.24091 9.91148 8.29688 10.0466 8.29688 10.1875C8.29688 10.3284 8.24091 10.4635 8.14128 10.5632C8.04165 10.6628 7.90652 10.7188 7.76563 10.7188Z"
                            fill="#666666"
                          />
                        </svg>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="bg-black-label border-0">
                      <p className="bg-black-label text-white-A700">
                        {row?.original?.meetingStaffs?.length
                          ? row?.original?.meetingStaffs
                              ?.map((staff: any) => {
                                return staff?.fname;
                              })
                              .join(", ")
                          : "No staffs available"}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            );
          }

          return newSource?.value ? (
            <div className="h-full flex gap-10">
              <div className="h-full w-[100px] flex gap-10">
                <p className="capitalize w-full whitespace-nowrap">
                  {newSource?.label}
                </p>
                <Img
                  className="text-right flex items-end h-5 w-5"
                  src={newSource?.icon}
                  alt="icon"
                />
              </div>
              <p className="capitalize font-normal text-sm text-[#666666]">
                {["outletID", "partnerOffice"].includes(
                  row?.original?.channelSource as string,
                )
                  ? ""
                  : row?.original?.channelSource}
              </p>
            </div>
          ) : (
            <p className="capitalize">{row?.original?.channelType}</p>
          );
        },
        filterFn: (row, id, value) => {
          return String(row.getValue(id))
            .toLowerCase()
            .includes(String(value).toLowerCase());
        },
        enableSorting: false,
      },
      {
        accessorKey: "interactionNote",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Note" />
        ),
        filterFn: (row, id, value) => {
          return String(row.getValue(id))
            .toLowerCase()
            .includes(String(value).toLowerCase());
        },
        cell: ({ row }) => {
          return (
            <div className="flex items-center justify-between gap-1.5">
              <p className="max-w-[200px] whitespace-normal break-words">
                {row.getValue("interactionNote") as string}
              </p>
              {row?.original?.quotationURL && (
                <div
                  style={{
                    display: ["Forex", "Remittance"].includes(
                      opportunityDetails?.productSubCategory,
                    )
                      ? "none"
                      : "block",
                  }}
                  className="flex gap-2.5"
                >
                  <button
                    disabled={row?.original?.quotationURL === ""}
                    className=" disabled:opacity-40"
                    onClick={() => {
                      window.open(row?.original?.quotationURL, "_blank");
                    }}
                  >
                    <svg
                      width="16"
                      height="20"
                      viewBox="0 0 16 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10.934 0.15457C10.8423 0.0579688 10.7166 0 10.5863 0H2.7843C1.34457 0 0.15625 1.18359 0.15625 2.62313V17.3767C0.15625 18.8164 1.34461 20 2.78426 20H13.3736C14.8132 20 16.0016 18.8164 16.0016 17.3767V5.66172C16.0016 5.53617 15.9436 5.41547 15.8615 5.32359L10.934 0.15457ZM11.0741 1.70535L14.3736 5.16906H12.2287C11.591 5.16906 11.0741 4.65695 11.0741 4.01934V1.70535ZM13.3736 19.0338H2.78426C1.88094 19.0338 1.12242 18.2851 1.12242 17.3767V2.62313C1.12242 1.7198 1.87605 0.966172 2.78426 0.966172H10.1079V4.0193C10.1079 5.19316 11.0548 6.13523 12.2287 6.13523H15.0354V17.3767C15.0354 18.2851 14.2818 19.0338 13.3736 19.0338Z"
                        fill="#165FE3"
                      />
                      <path
                        d="M12.052 15.6968H4.10027C3.83465 15.6968 3.61719 15.914 3.61719 16.1798C3.61719 16.4455 3.83461 16.6629 4.10027 16.6629H12.0568C12.3225 16.6629 12.5399 16.4455 12.5399 16.1798C12.5399 15.914 12.3225 15.6968 12.052 15.6968ZM7.72348 13.9624C7.81535 14.059 7.9409 14.117 8.07609 14.117C8.21145 14.117 8.33703 14.059 8.42875 13.9624L11.2597 10.9238C11.4433 10.7304 11.4288 10.4213 11.2356 10.2427C11.0422 10.0591 10.7331 10.0734 10.5545 10.2668L8.55918 12.4068V7.13152C8.55918 6.8657 8.34176 6.64844 8.07609 6.64844C7.81047 6.64844 7.59301 6.8657 7.59301 7.13152V12.4068L5.60266 10.2668C5.41906 10.0736 5.11469 10.0591 4.92148 10.2427C4.72832 10.4262 4.71383 10.7306 4.89738 10.9238L7.72348 13.9624Z"
                        fill="#165FE3"
                      />
                    </svg>
                  </button>
                </div>
              )}
              {row?.original?.quotation_corp?.url && (
                <div
                  style={{
                    display: ["Visa"].includes(
                      opportunityDetails?.productSubCategory,
                    )
                      ? "none"
                      : "block",
                  }}
                  className="flex gap-2.5"
                >
                  <button
                    disabled={row?.original?.quotation_corp?.url === ""}
                    className=" disabled:opacity-40"
                    onClick={() => {
                      window.open(row?.original?.quotation_corp?.url, "_blank");
                    }}
                  >
                    <svg
                      width="16"
                      height="20"
                      viewBox="0 0 16 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10.934 0.15457C10.8423 0.0579688 10.7166 0 10.5863 0H2.7843C1.34457 0 0.15625 1.18359 0.15625 2.62313V17.3767C0.15625 18.8164 1.34461 20 2.78426 20H13.3736C14.8132 20 16.0016 18.8164 16.0016 17.3767V5.66172C16.0016 5.53617 15.9436 5.41547 15.8615 5.32359L10.934 0.15457ZM11.0741 1.70535L14.3736 5.16906H12.2287C11.591 5.16906 11.0741 4.65695 11.0741 4.01934V1.70535ZM13.3736 19.0338H2.78426C1.88094 19.0338 1.12242 18.2851 1.12242 17.3767V2.62313C1.12242 1.7198 1.87605 0.966172 2.78426 0.966172H10.1079V4.0193C10.1079 5.19316 11.0548 6.13523 12.2287 6.13523H15.0354V17.3767C15.0354 18.2851 14.2818 19.0338 13.3736 19.0338Z"
                        fill="#165FE3"
                      />
                      <path
                        d="M12.052 15.6968H4.10027C3.83465 15.6968 3.61719 15.914 3.61719 16.1798C3.61719 16.4455 3.83461 16.6629 4.10027 16.6629H12.0568C12.3225 16.6629 12.5399 16.4455 12.5399 16.1798C12.5399 15.914 12.3225 15.6968 12.052 15.6968ZM7.72348 13.9624C7.81535 14.059 7.9409 14.117 8.07609 14.117C8.21145 14.117 8.33703 14.059 8.42875 13.9624L11.2597 10.9238C11.4433 10.7304 11.4288 10.4213 11.2356 10.2427C11.0422 10.0591 10.7331 10.0734 10.5545 10.2668L8.55918 12.4068V7.13152C8.55918 6.8657 8.34176 6.64844 8.07609 6.64844C7.81047 6.64844 7.59301 6.8657 7.59301 7.13152V12.4068L5.60266 10.2668C5.41906 10.0736 5.11469 10.0591 4.92148 10.2427C4.72832 10.4262 4.71383 10.7306 4.89738 10.9238L7.72348 13.9624Z"
                        fill="#165FE3"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          );
        },
        enableSorting: false,
      },
    ],
    [handleUpdateInteractionStatus],
  );

  const opportunityCloseReason = [
    {
      value: "buyLater",
      label: "Buy Later",
    },
    {
      value: "notInterested",
      label: "Not Interested",
    },
    {
      value: "notServicable",
      label: " Not Serviceable",
    },
    {
      value: "spam",
      label: "Spam",
    },
    {
      value: "noResponse",
      label: "No Response",
    },
  ];

  const getStaffs = async () => {
    const auth = localStorage.getItem("authToken");
    if (auth === null) return;
    const authToken = JSON.parse(auth);
    const payload: AxiosRequestConfig<GetAllStaffRequestType> = {
      headers: { Authorization: `Bearer ${authToken}` },
    };
    try {
      const res = (await getAllStaff(payload)) as any;
      const options = res.data.map((obj: any) => ({
        label: obj.fname,
        value: obj.id,
      }));
      setStaffs(options);
    } catch (error) {
      console.error("Error fetching options:", error);
    }
  };

  async function fetchRelationShipManager(): Promise<void | Option[]> {
    // get auth token from localstorage
    const auth: string | null = localStorage.getItem("authToken");
    if (auth === null) return;
    const authToken = JSON.parse(auth);

    const req = {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    };
    const name = localStorage.getItem("name");
    const is_owner = localStorage.getItem("is_owner");
    const owner_name = localStorage.getItem("owner_name");
    const myself = localStorage.getItem("staff_id");

    try {
      const res = await getRelationShipManager(req);

      const options = res.data.map((obj) => ({
        label: obj?.fname,
        value: obj?.id,
      }));

      const index = options.findIndex((item) => item?.value == myself);
      if (index > 0) {
        const item = options.splice(index, 1)[0];
        options.unshift(item);
      }

      return options as Option[];
    } catch (error) {
      toast.error("Error fetching options staff");
    }
  }

  const {
    options: relationShipManagerOptions,
    selectedOption: relationShipManagerSelectedOption,
    handleChange: relationShipManagerHandleChange,
  } = useSelectState(
    [],
    null,
    () => {},
    () => {},
    fetchRelationShipManager as () => Promise<Option[]>,
  );

  async function handleRequestDoc() {
    setLoading(true);
    const auth: string | null = localStorage.getItem("authToken");
    if (auth === null) return;
    const authToken = JSON.parse(auth);

    const req: AxiosRequestConfig<RequestDocReqType> = {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      data: {
        opportunity_id: rowData?.id,
        exp_time: expires,
        channel,
        doc_list: selectedMode.map((mode) => ({ filename: mode })),
        note: popupnotes,
        channel_of_interaction_id: selectedChannelOption as string,
        interaction_note: interactionNote,
      },
    };

    try {
      const response = await requestDoc(req);
      if (response.status === 200) {
        toast.success("Documnet request sent successfully");
        setLoading(false);
        setInteractionNote("");
        setSelectedChannelOptions("");
      }
    } catch (error) {
      toast.error("Unable to send document request");
      setLoading(false);
    }
  }

  const buttonState = () => {
    if (!interactionNote || !selectedChannelOption) return false;
    if (selectedChannelOption === "3") {
      if (!date || !selectedStaffOptions.length) return false;
    }
    return true;
  };

  const handleLetterUpload = async (file: File, reupload = false) => {
    setLoading(true);
    const auth: string | null = localStorage.getItem("authToken");
    if (auth === null) return;
    const authToken = JSON.parse(auth);
    const formData = new FormData();
    formData.append("opportunity_id", rowData?.id as string);
    formData.append("reupload", reupload ? "true" : "false");
    formData.append(
      "doc_list_id",
      productDetails?.name === "Education payment" ? "5" : "13",
    );
    const base64 = await convertFileToBase64(file); // Await the base64 conversion
    formData.append("file", base64);

    const req: AxiosRequestConfig<UploadLetterDocumnetsReqType> = {
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "multipart/form-data",
      },
      data: formData as any,
    };

    try {
      const response = await UploadLetterDocumnets(req);
      if (response.status === 200) {
        toast.success("Letter uploaded successfully");
        setLoading(false);
      }
    } catch (error) {
      toast.error("Unable to upload letter");
      setLoading(false);
    }
  };

  const handleQuoteUploadVisa = async (file: File) => {
    setLoading(true);
    const auth: string | null = localStorage.getItem("authToken");
    if (auth === null) return;
    const authToken = JSON.parse(auth);

    const base64 = await convertFileToBase64(file); // Await the base64 conversion

    const payload: AxiosRequestConfig<PostCorporateInteractionsReqType> = {
      headers: { Authorization: `Bearer ${authToken}` },
      data: {
        opportunity_id: rowData?.id as string,
        remarks: "",
        meeting_time: undefined,
        meetingStaff_id: [],
        note: interactionNote,
        channel_of_interaction_id: "9",
        quotation_pdf: "",
        geo_coordinates: {
          type: "point",
          data: {
            lng: location?.longitude as string,
            lat: location?.latitude as string,
          },
        },
        quotation_corporate: base64,
      },
    };

    try {
      const response = (await postCorporateInteractions(payload)) as any;
      if (response.status === 200) {
        toast.success("Quotation uploaded successfully");
        setLoading(false);
      }
    } catch (error) {
      toast.error("Unable to upload quotation");
      setLoading(false);
    }
  };

  useEffect(() => {
    setInteractionNote("");
    setSelectedChannelOptions("");
  }, [isOpen]);

  useEffect(() => {
    getStaffs();
  }, []);

  useEffect(() => {
    getOpportunity();
  }, [rowData?.id, loading]);

  const navigate = useNavigate();

  const handlePrepareQuote = () => {
    if (opportunityDetails?.productSubcategory === "Remittance") {
      dispatch(setPurposeId(purposeIDDetails?.id));
      navigate("/l2createremittancequotation", {
        state: {
          contact_name: contactDetails?.contactPerson,
          contact_number: contactDetails?.contactNumber,
          contact_email: contactDetails?.contactEmail,
          product_id: productDetails?.id,
          product_name: productDetails?.name,
          product_category: productDetails?.subCategory,
          opportunity_id: rowData?.id as string,
          remarks: "",
          meeting_time: date,
          meetingStaff_id: selectedStaffOptions?.map((staff) =>
            Number(staff.value),
          ),
          note: interactionNote,
          channel_of_interaction_id: selectedChannelOption as string,
          partner_name: extraDetails?.partnerName,
          partner_id: extraDetails?.partnerId,
          contact_id: contactDetails?.contactId,
          purpose_id: purposeIDDetails?.id,
        },
      });
    }
    if (opportunityDetails?.productSubcategory === "Forex") {
      navigate("/createforexquotation", {
        state: {
          contact_name: contactDetails?.contactPerson,
          contact_number: contactDetails?.contactNumber,
          contact_email: contactDetails?.contactEmail,
          product_id: productDetails?.id,
          opportunity_id: rowData?.id as string,
          remarks: "",
          meeting_time: date,
          meetingStaff_id: selectedStaffOptions?.map((staff) =>
            Number(staff.value),
          ),
          note: interactionNote,
          channel_of_interaction_id: selectedChannelOption as string,
        },
      });
    }
    return;
  };

  const generateSummary = async () => {
    setSummary("");
    const auth: string | null = localStorage.getItem("authToken");
    if (auth === null) return;
    const authToken = JSON.parse(auth);
    const req: AxiosRequestConfig<GenerateSummaryReqType> = {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      data: {
        lead_id: rowData?.id,
      },
    };

    try {
      const response = (await GenerateSummary(req)) as any;
      if (response.status === 200) {
        setSummary(response?.data?.response?.result?.summary);
      }
    } catch (error) {
      setSummary("Unable to generate summary");
    }
  };

  const opp_status = () => {
    const values = close_reason_options.find(
      (item) => item.value === opportunityDetails?.reasonToClose,
    );
    return (
      <p
        style={{ color: values?.textColor, backgroundColor: values?.bgColor }}
        className="py-[3px] px-2 rounded-md whitespace-nowrap"
      >
        {values?.label}
      </p>
    );
  };

  const openDetailNote = () => {
    if (extraDetails?.note) 
      setInputValue(extraDetails?.note);
    setDetailNote(true);
  };

  const handleReset = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    setSelectedChannel(null);
    setSelectedType("");
    setShowBothButtons(true);
  };

  const handleSelect = (type, value) => {
    setSelectedChannel({ type, value });
    setSelectedType("");
    setShowBothButtons(false);
  };

  const getSelectedLabel = (type, value) => {
    const options =
      type === "inbound" ? channel_options_inbound : channel_options_outbound;
    const selectedOption = options.find(
      (option) => option.value.toString() === value,
    );
    return selectedOption?.label || "";
  };

  useEffect(() => {
    if (!isOpen) {
      setActiveTab("interaction");
    }
  }, [isOpen]);

  const {mutate, isLoading} = useMutation({
    mutationFn: update_stage,
    onSuccess: (data) => {
      if (data.status === 200) {
        toast.success("Stage updated successfully");
        getOpportunity();
        getOpportunityList?.();
      } else {
        toast.error("Failed to update stage");
      }
    },
    onError: () => {
      toast.error("An error occurred while updating stage");
    },
  })

  const handleUpdateStage = (stage: string) => {
    mutate({
      opportunity_id: Number(rowData?.id),
      status: stage
    });
  };

  if (!rowData) return null;

  const handleDownloadInvoice = async (invoice: {
    url: string;
    name: string;
  }) => {
    if (!invoice?.url) {
      console.warn("Invoice URL not found");
      return;
    }

    try {
      const response = await fetch(invoice.url);
      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.statusText}`);
      }

      const blob = await response.blob();
      const finalFileName = invoice.name?.trim() || "document";

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = finalFileName;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      toast.error("Failed to download the file. Please try again.");
    }
  };

  const mobileView = () => {
    return (
      <Sheet open={isOpen} onOpenChange={onOpenChange}>
        <SheetContent className="bg-[#EBEEF5] w-[100%] p-2 gap-3 flex sm:max-w-full  overflow-auto flex-col">
          <div className="p-2 rounded-lg  bg-white-A700 flex-col w-full">
            <div className="flex w-full ">
              <div className="flex flex-col  w-full">
                <button
                  onClick={() => {
                    handleCloseModal?.(), onOpenChange(false);
                  }}
                  className="  flex justify-end"
                >
                  <XIcon />
                </button>
                <div className="flex gap-2">
                  <div className="flex  gap-2">
                    <div className="flex flex-col">
                      <div className="flex gap-2  items-center w-full ">
                        <h4 className="text-xl  capitalize text-black-label after:pl-2 font-semibold after:content-[''] after:border-r after:border-[#C4C4C4] after:w-[4px] after:h-[4px]">
                          {contactDetails?.full_name}
                        </h4>
                        <div className="flex ">
                          <p
                            className="text-sm  text-[#333333] font-inter"
                            style={{
                              fontWeight: "500",
                              fontSize: "14px",
                            }}
                          >
                            {rowData.product}
                          </p>
                        </div>
                        {/* Reminder icon */}
                        <div className="flex justify-end  ">
                          <Popover>
                            <PopoverTrigger
                              className="disabled:opacity-50"
                              disabled={
                                opportunityDetails?.isOpportunityOpen !== "open"
                              }
                            >
                              <button
                                onClick={() => setEditReminder(false)}
                                className="text-sm font-medium text-blue-700"
                              >
                                {extraDetails?.followup_time ? (
                                  <div className="relative">
                                    {new Date(extraDetails?.followup_time) <=
                                      new Date() && (
                                      <div className="absolute bottom-3 -right-1">
                                        <span className="animate-ping absolute inline-flex h-2 w-2  rounded-full bg-red-500 opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                                      </div>
                                    )}
                                    <svg
                                      width="19"
                                      height="20"
                                      viewBox="0 0 19 20"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M4.88693 8.70306C5.04296 6.015 6.94433 3.53906 9.50273 3.53906C12.0612 3.53906 13.9625 6.015 14.1185 8.70306C14.1896 9.92548 14.3771 11.0978 14.9557 12.1706C15.5716 13.3124 15.0178 14.8737 13.7789 15.0274C12.354 15.2041 10.6857 15.3724 9.50273 15.3724C8.31982 15.3724 6.65148 15.2041 5.22655 15.0274C3.98773 14.8737 3.43383 13.3124 4.04975 12.1706C4.62843 11.0978 4.81596 9.92548 4.88693 8.70306Z"
                                        fill="#98BDFF"
                                      />
                                      <path
                                        d="M9.4974 17.7057C10.3719 17.7057 11.0807 16.9596 11.0807 16.0391C11.0807 16.0391 10.1205 16.2474 9.4974 16.2474C8.87428 16.2474 7.91406 16.0391 7.91406 16.0391C7.91406 16.9596 8.62292 17.7057 9.4974 17.7057Z"
                                        fill="#165FE3"
                                      />
                                      <path
                                        d="M9.5026 2.28906C9.06537 2.28906 8.71094 2.66216 8.71094 3.1224C8.71094 3.23153 8.73089 3.33575 8.76707 3.43128C8.76707 3.43128 9.21293 3.33073 9.5026 3.33073C9.79228 3.33073 10.2381 3.43128 10.2381 3.43128C10.2743 3.33575 10.2943 3.23153 10.2943 3.1224C10.2943 2.66216 9.93984 2.28906 9.5026 2.28906Z"
                                        fill="#165FE3"
                                      />
                                      <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M4.70144 2.87531C4.94313 3.1086 4.95938 3.50395 4.73775 3.75836C3.99278 4.6135 3.4453 5.66089 3.16992 6.82081C3.09042 7.15569 2.76806 7.35931 2.44993 7.27563C2.13179 7.19194 1.93835 6.85262 2.01786 6.51775C2.34239 5.15081 2.98717 3.91833 3.86254 2.91352C4.08417 2.65912 4.45976 2.642 4.70144 2.87531ZM14.6959 2.87531C14.9375 2.642 15.3131 2.65912 15.5347 2.91352C16.4102 3.91833 17.0549 5.15081 17.3795 6.51775C17.459 6.85262 17.2655 7.19194 16.9474 7.27563C16.6292 7.35931 16.3068 7.15569 16.2274 6.82081C15.952 5.66089 15.4045 4.6135 14.6595 3.75836C14.4379 3.50395 14.4542 3.1086 14.6959 2.87531Z"
                                        fill="#165FE3"
                                      />
                                    </svg>
                                  </div>
                                ) : (
                                  <svg
                                    width="19"
                                    height="20"
                                    viewBox="0 0 19 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M4.88693 8.70306C5.04296 6.015 6.94433 3.53906 9.50273 3.53906C12.0612 3.53906 13.9625 6.015 14.1185 8.70306C14.1896 9.92548 14.3771 11.0978 14.9557 12.1706C15.5716 13.3124 15.0178 14.8737 13.7789 15.0274C12.354 15.2041 10.6857 15.3724 9.50273 15.3724C8.31982 15.3724 6.65148 15.2041 5.22655 15.0274C3.98773 14.8737 3.43383 13.3124 4.04975 12.1706C4.62843 11.0978 4.81596 9.92548 4.88693 8.70306Z"
                                      fill="#C4C4C4"
                                    />
                                    <path
                                      d="M9.4974 17.7057C10.3719 17.7057 11.0807 16.9596 11.0807 16.0391C11.0807 16.0391 10.1205 16.2474 9.4974 16.2474C8.87428 16.2474 7.91406 16.0391 7.91406 16.0391C7.91406 16.9596 8.62292 17.7057 9.4974 17.7057Z"
                                      fill="#165FE3"
                                    />
                                    <path
                                      d="M9.5026 2.28906C9.06537 2.28906 8.71094 2.66216 8.71094 3.1224C8.71094 3.23153 8.73089 3.33575 8.76707 3.43128C8.76707 3.43128 9.21293 3.33073 9.5026 3.33073C9.79228 3.33073 10.2381 3.43128 10.2381 3.43128C10.2743 3.33575 10.2943 3.23153 10.2943 3.1224C10.2943 2.66216 9.93984 2.28906 9.5026 2.28906Z"
                                      fill="#165FE3"
                                    />
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M4.70144 2.87531C4.94313 3.1086 4.95938 3.50395 4.73775 3.75836C3.99278 4.6135 3.4453 5.66089 3.16992 6.82081C3.09042 7.15569 2.76806 7.35931 2.44993 7.27563C2.13179 7.19194 1.93835 6.85262 2.01786 6.51775C2.34239 5.15081 2.98717 3.91833 3.86254 2.91352C4.08417 2.65912 4.45976 2.642 4.70144 2.87531ZM14.6959 2.87531C14.9375 2.642 15.3131 2.65912 15.5347 2.91352C16.4102 3.91833 17.0549 5.15081 17.3795 6.51775C17.459 6.85262 17.2655 7.19194 16.9474 7.27563C16.6292 7.35931 16.3068 7.15569 16.2274 6.82081C15.952 5.66089 15.4045 4.6135 14.6595 3.75836C14.4379 3.50395 14.4542 3.1086 14.6959 2.87531Z"
                                      fill="#999999"
                                    />
                                  </svg>
                                )}
                              </button>
                            </PopoverTrigger>
                            <PopoverContent
                              alignOffset={10}
                              align="start"
                              className="rounded-lg z-50 gap-4 p-2.5 flex flex-col"
                            >
                              {!extraDetails?.followup_time || editReminder ? (
                                <>
                                  <DateTimePicker
                                    date={date}
                                    setDate={setDate}
                                  />
                                  <Input
                                    type="text"
                                    value={followupNote}
                                    defaultValue={extraDetails?.followup_note}
                                    onChange={(e) =>
                                      setFollowupNote(e.target.value)
                                    }
                                    placeholder="Note (if any)"
                                    className="w-full placeholder:text-[#999999] border-0 h-5 border-b border-gray-200 p-0 rounded-none focus:border-b focus:border-blue-700 focus-visible:ring-0 focus-visible:ring-offset-0"
                                  />
                                  {date && (
                                    <div className="flex gap-2.5 items-center">
                                      <PopoverClose asChild>
                                        <button
                                          onClick={() => {
                                            setDate(undefined);
                                            setFollowupNote("");
                                            setEditReminder(false);
                                          }}
                                          className="text-sm w-full font-medium text-blue-700 border border-blue-700 rounded-[4px] py-[8px] px-4"
                                        >
                                          Cancel
                                        </button>
                                      </PopoverClose>
                                      <button
                                        onClick={() => {
                                          handleUpdateOpportunity(
                                            "",
                                            date,
                                            followupNote,
                                            "",
                                          );
                                          setEditReminder(false);
                                          setIsClearReminder(false);
                                        }}
                                        className="text-sm w-full font-medium border border-blue-700 rounded-[4px] py-[8px] px-4 bg-blue-700 text-white-A700"
                                      >
                                        Save
                                      </button>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div className="flex flex-col gap-2.5">
                                  <div className="flex flex-col items-start border-b border-gray-200 pb-2.5">
                                    <p className="text-sm font-medium text-[#999999] flex w-full justify-between items-center">
                                      Reminder set for
                                      <button
                                        onClick={() => {
                                          setEditReminder(true);
                                          setDate(
                                            new Date(
                                              extraDetails?.followup_time,
                                            ),
                                          );
                                          setFollowupNote(
                                            extraDetails?.followup_note,
                                          );
                                        }}
                                        className=""
                                      >
                                        <svg
                                          width="16"
                                          height="17"
                                          viewBox="0 0 16 17"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            d="M3.33594 4.74609H2.5026C2.06058 4.74609 1.63665 4.92169 1.32409 5.23425C1.01153 5.54681 0.835938 5.97073 0.835938 6.41276V13.9128C0.835938 14.3548 1.01153 14.7787 1.32409 15.0913C1.63665 15.4038 2.06058 15.5794 2.5026 15.5794H10.0026C10.4446 15.5794 10.8686 15.4038 11.1811 15.0913C11.4937 14.7787 11.6693 14.3548 11.6693 13.9128V13.0794"
                                            stroke="#165FE3"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                          <path
                                            d="M10.8333 3.08472L13.3333 5.58472M14.4875 4.40556C14.8157 4.07735 15.0001 3.63221 15.0001 3.16806C15.0001 2.70391 14.8157 2.25876 14.4875 1.93056C14.1593 1.60235 13.7142 1.41797 13.25 1.41797C12.7858 1.41797 12.3407 1.60235 12.0125 1.93056L5 8.91806V11.4181H7.5L14.4875 4.40556Z"
                                            stroke="#165FE3"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                      </button>
                                    </p>
                                    <p className="">
                                      {formatUnixTimestamp(
                                        extraDetails?.followup_time,
                                      )}
                                    </p>
                                  </div>
                                  {!isClearReminder && (
                                    <p className="text-sm font-medium text-[#999999]">
                                      {extraDetails?.followup_note}
                                    </p>
                                  )}
                                  {!isClearReminder && (
                                    <button
                                      onClick={() => setIsClearReminder(true)}
                                      className="text-sm w-full text-right font-medium text-blue-700 underline underline-offset-4"
                                    >
                                      Clear Reminder
                                    </button>
                                  )}
                                  {isClearReminder && (
                                    <div className="flex flex-col gap-2.5 items-center w-full">
                                      <p className="">
                                        You will not be notified for a follow
                                        up. Are you sure you want to remove the
                                        reminder on this lead?
                                      </p>
                                      <div className="flex gap-2.5 items-center w-full">
                                        <button
                                          onClick={() => {
                                            clearReminder(rowData.id);
                                          }}
                                          className="text-sm w-full font-medium text-blue-700 border border-blue-700 rounded-[4px] py-[8px] px-4"
                                        >
                                          Yes
                                        </button>
                                        <button
                                          onClick={() =>
                                            setIsClearReminder(false)
                                          }
                                          className="text-sm w-full font-medium border border-blue-700 rounded-[4px] py-[8px] px-4 bg-blue-700 text-white-A700"
                                        >
                                          No
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>
                      <p className="text-sm leading-[21px] text-[#999999] font-normal mt-2">
                        {" "}
                        ID-{rowData.id} | {rowData?.createDate} by{" "}
                        {opportunityDetails?.createdBy}
                      </p>
                      {/* <p className="text-sm text-[#999999] font-normal leading-[21px]">
                        {Number(opportunityDetails?.forex_orderID) > 0 &&
                          `Booking-ID - ${opportunityDetails.forex_orderID}`}
                      </p> */}
                    </div>
                  </div>
                </div>
                {/* Reassign feature */}
                <div className=" flex  mt-3">
                  <div className="flex gap-2.5 ">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9.835 19.67C15.2667 19.67 19.67 15.2667 19.67 9.835C19.67 4.40328 15.2667 0 9.835 0C4.40328 0 0 4.40328 0 9.835C0 15.2667 4.40328 19.67 9.835 19.67Z"
                        fill="#F1F1F1"
                      />
                      <path
                        d="M9.83656 4.85938C7.80072 4.85938 6.14844 6.51165 6.14844 8.5475C6.14844 10.544 7.7122 12.1668 9.78739 12.2258C9.81689 12.2258 9.85623 12.2258 9.8759 12.2258C9.89557 12.2258 9.92508 12.2258 9.94475 12.2258C9.95458 12.2258 9.96442 12.2258 9.96442 12.2258C11.9511 12.1569 13.5149 10.544 13.5247 8.5475C13.5247 6.51165 11.8724 4.85938 9.83656 4.85938Z"
                        fill="#333333"
                      />
                      <path
                        d="M16.5081 17.0582C14.7575 18.6711 12.4168 19.6645 9.84001 19.6645C7.26324 19.6645 4.92251 18.6711 3.17188 17.0582C3.40791 16.1632 4.04719 15.3469 4.98152 14.7175C7.66647 12.9275 12.0332 12.9275 14.6985 14.7175C15.6427 15.3469 16.2721 16.1632 16.5081 17.0582Z"
                        fill="#333333"
                      />
                    </svg>
                    <p className="font-medium text-sm text-black-label">
                      {opportunityDetails?.assignedTo ??
                        localStorage.getItem("owner_name")}
                    </p>
                  </div>
                  <div className="flex ml-auto">
                    {opportunityDetails?.isOpportunityOpen === "open" && (
                      <AlertDialog>
                        <AlertDialogTrigger>
                          <div
                            className="cursor-pointer text-sm font-medium text-[#165FE3] underline "
                            style={{
                              fontWeight: "600",
                              fontSize: "14px",
                            }}
                          >
                            Reassign
                          </div>
                        </AlertDialogTrigger>
                        <AlertDialogPortal>
                          <AlertDialogOverlay className="  bg-black-900 bg-opacity-60 animate-overlayShow fixed inset-0" />
                          <AlertDialogContent className="bg-white-A700 rounded-lg p-0 w-full animate-contentShow">
                            <AlertDialogHeader className="flex text-left border-b  rounded-t py-3 px-10 ">
                              <AlertDialogTitle className="font-bold p-0 text-lg text-black-label">
                                Select the staff to assign
                              </AlertDialogTitle>
                              <AlertDialogDescription></AlertDialogDescription>
                            </AlertDialogHeader>
                            <form className="px-10 w-full flex py-3 flex-col gap-2">
                              <label
                                htmlFor="staff_select"
                                className=" text-sm font-medium "
                              >
                                Choose a staff
                              </label>
                              <ReactSelect
                                menuPlacement="auto"
                                className=""
                                menuPosition="absolute"
                                placeholder="Select the staff"
                                styles={{
                                  control: (provided, state) => ({
                                    ...provided,
                                    zIndex: 0,
                                    borderRadius: ".5rem",
                                    padding: ".4rem",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderColor: state.isFocused
                                      ? "#165FE3"
                                      : "#333333",
                                    boxShadow: "none",
                                  }),
                                  menu: (provided, state) => ({
                                    ...provided,
                                    zIndex: 1000,
                                  }),
                                }}
                                options={relationShipManagerOptions}
                                onChange={relationShipManagerHandleChange}
                                value={relationShipManagerSelectedOption}
                                name="staff_select"
                                id="staff_select"
                              />
                            </form>
                            <AlertDialogFooter className="flex justify-start items-center gap-2.5 py-5 px-10 flex-row rounded-b-lg bg-gray-button-gray-lite">
                              <AlertDialogCancel
                                className=" m-0 rounded-lg border-blue-700 border py-[11px] px-4 text-blue-700"
                                asChild
                              >
                                <button>Cancel</button>
                              </AlertDialogCancel>
                              <AlertDialogAction
                                className="rounded-lg border-blue-700 py-[11px] px-4 bg-blue-700 border text-white-A700"
                                asChild
                              >
                                <button
                                  className=""
                                  onClick={() =>
                                    handleUpdateOpportunity("", "", "", "")
                                  }
                                >
                                  Submit
                                </button>
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialogPortal>
                      </AlertDialog>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {/* Summary */}
            <div className="flex flex-col mt-4">
              <div className="flex-1 w-full flex ">
                <div className=" flex w-full">
                  <Popover
                    onOpenChange={async (val) => {
                      if (val) {
                        await generateSummary();
                      }
                    }}
                  >
                    <PopoverTrigger className="w-full h-full">
                      <div className="flex items-center max-w-full w-full h-[42px] cursor-pointer">
                        <button className="text-blue-700 text-base  leading-4 h-full font-semibold bg-[#E1EBFC99] px-2 py-1.5 rounded-lg w-full flex items-center justify-center">
                          Summary
                          <div className="pl-2 ">
                            <svg
                              width="14"
                              height="16"
                              viewBox="0 0 14 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M10.6102 10.9659L10.6101 10.9655C10.6087 10.9616 10.6067 10.9596 10.6049 10.9583C10.6027 10.9569 10.5997 10.9559 10.5962 10.9559C10.5927 10.9559 10.5896 10.9569 10.5873 10.9584C10.5853 10.9597 10.5833 10.9618 10.5818 10.9659H10.6102ZM10.6102 10.9659L10.8389 11.6002L10.8389 11.6003C10.9669 11.955 11.1714 12.2771 11.4381 12.5437C11.7047 12.8104 12.0268 13.015 12.3815 13.1429L12.3816 13.1429L13.0158 13.3716C13.0158 13.3716 13.0158 13.3716 13.0159 13.3716C13.0187 13.3727 13.0212 13.3746 13.023 13.3771L13.064 13.3485L13.023 13.3771C13.0248 13.3797 13.0257 13.3827 13.0257 13.3858C13.0257 13.389 13.0248 13.392 13.023 13.3946L13.064 13.4232L13.023 13.3946C13.0212 13.3971 13.0187 13.399 13.0159 13.4001C13.0158 13.4001 13.0158 13.4001 13.0158 13.4001L12.3816 13.6287L12.3815 13.6288C12.0268 13.7567 11.7047 13.9613 11.4381 14.2279C11.1714 14.4946 10.9669 14.8167 10.8389 15.1714L10.8389 15.1715L10.6103 15.8057C10.6103 15.8057 10.6102 15.8057 10.6102 15.8057C10.6092 15.8086 10.6072 15.8111 10.6047 15.8129C10.6022 15.8147 10.5991 15.8157 10.596 15.8157C10.5929 15.8157 10.5898 15.8147 10.5873 15.8129C10.5848 15.8111 10.5828 15.8086 10.5818 15.8057C10.5818 15.8057 10.5817 15.8057 10.5817 15.8057L10.3531 15.1715L10.3531 15.1714C10.2251 14.8167 10.0206 14.4946 9.75393 14.2279C9.48729 13.9613 9.16516 13.7567 8.81046 13.6288L8.81045 13.6287L8.17624 13.4001C8.17621 13.4001 8.17617 13.4001 8.17614 13.4001C8.17325 13.399 8.17076 13.3971 8.169 13.3946C8.16721 13.392 8.16625 13.389 8.16625 13.3858C8.16625 13.3827 8.16721 13.3797 8.169 13.3771C8.17076 13.3746 8.17325 13.3727 8.17614 13.3716C8.17617 13.3716 8.17621 13.3716 8.17624 13.3716L8.81045 13.1429L8.81046 13.1429C9.16516 13.015 9.48729 12.8104 9.75393 12.5437C10.0206 12.2771 10.2251 11.955 10.3531 11.6003L10.3531 11.6002L10.5818 10.966L10.6102 10.9659ZM10.6572 10.9489L10.8859 11.5833C11.0114 11.9311 11.212 12.2469 11.4734 12.5084C11.7349 12.7698 12.0507 12.9704 12.3985 13.0959L10.6572 10.9489ZM10.6572 10.9489C10.637 10.8916 10.5555 10.8916 10.5348 10.9489H10.6572ZM4.96995 3.20353L4.97006 3.20383L5.42125 4.45685L5.42126 4.45687C5.67132 5.15071 6.07136 5.78084 6.59285 6.30235C7.11435 6.82387 7.74447 7.22392 8.43829 7.47399L8.4383 7.47399L9.69102 7.92509C9.69107 7.92511 9.69112 7.92513 9.69117 7.92515C9.70623 7.93071 9.71924 7.94075 9.72843 7.95391C9.73765 7.96712 9.7426 7.98285 9.7426 7.99896C9.7426 8.01507 9.73765 8.0308 9.72843 8.04401C9.71923 8.05717 9.70623 8.06721 9.69117 8.07277C9.69112 8.07278 9.69107 8.0728 9.69102 8.07282L8.4383 8.52392L8.43829 8.52393C7.74447 8.774 7.11435 9.17405 6.59285 9.69556C6.07136 10.2171 5.67132 10.8472 5.42126 11.541L5.42125 11.5411L4.97016 12.7938C4.97014 12.7939 4.97013 12.7939 4.97011 12.794C4.96455 12.809 4.95451 12.822 4.94135 12.8312C4.92814 12.8405 4.91241 12.8454 4.8963 12.8454C4.88019 12.8454 4.86447 12.8405 4.85126 12.8312C4.83809 12.822 4.82805 12.809 4.82249 12.794C4.82248 12.7939 4.82246 12.7939 4.82244 12.7938L4.37135 11.5411L4.37135 11.541C4.12128 10.8472 3.72125 10.2171 3.19975 9.69556C2.67825 9.17405 2.04814 8.774 1.35431 8.52393L1.3543 8.52392L0.101586 8.07282C0.101554 8.07281 0.101523 8.0728 0.101491 8.07279C0.0864045 8.06723 0.0733802 8.05719 0.0641733 8.04401C0.0549475 8.0308 0.05 8.01507 0.05 7.99896C0.05 7.98285 0.0549475 7.96712 0.0641733 7.95391C0.0733802 7.94073 0.0864043 7.93068 0.101492 7.92513C0.101523 7.92512 0.101554 7.92511 0.101586 7.92509L1.3543 7.47399L1.35431 7.47399C2.04814 7.22392 2.67825 6.82387 3.19975 6.30235C3.72125 5.78084 4.12128 5.15071 4.37135 4.45687L4.37135 4.45685L4.82254 3.20383L4.82265 3.20353C4.82803 3.18829 4.838 3.1751 4.85119 3.16576C4.86438 3.15643 4.88014 3.15142 4.8963 3.15142C4.91246 3.15142 4.92822 3.15643 4.94141 3.16576C4.95461 3.1751 4.96458 3.18829 4.96995 3.20353ZM10.8389 0.827029L10.8389 0.82704C10.9669 1.18175 11.1714 1.50389 11.4381 1.77053C11.7047 2.03717 12.0268 2.24176 12.3815 2.36973L12.3816 2.36973L13.0158 2.59837C13.0158 2.59838 13.0158 2.59838 13.0158 2.59839C13.0187 2.59946 13.0212 2.60139 13.023 2.60392L13.064 2.57525L13.023 2.60393C13.0248 2.60648 13.0257 2.60952 13.0257 2.61263C13.0257 2.61575 13.0248 2.61879 13.023 2.62134L13.064 2.65001L13.023 2.62134C13.0212 2.62387 13.0187 2.6258 13.0158 2.62687C13.0158 2.62688 13.0158 2.62689 13.0158 2.6269L12.3816 2.85553L12.3815 2.85554C12.0268 2.98351 11.7047 3.18809 11.4381 3.45473C11.1714 3.72137 10.9669 4.04351 10.8389 4.39822L10.8389 4.39824L10.6103 5.03247C10.6103 5.03249 10.6102 5.03252 10.6102 5.03255C10.6092 5.03544 10.6072 5.03794 10.6047 5.03971C10.6022 5.04149 10.5991 5.04245 10.596 5.04245C10.5929 5.04245 10.5898 5.04149 10.5873 5.03971C10.5848 5.03794 10.5828 5.03544 10.5818 5.03255C10.5818 5.03252 10.5817 5.03249 10.5817 5.03247L10.3531 4.39824L10.3531 4.39822C10.2251 4.04351 10.0206 3.72137 9.75393 3.45473C9.48729 3.18809 9.16516 2.98351 8.81046 2.85554L8.81045 2.85553L8.17624 2.6269C8.17621 2.62689 8.17619 2.62688 8.17616 2.62687C8.17327 2.6258 8.17077 2.62387 8.169 2.62134C8.16721 2.61879 8.16625 2.61575 8.16625 2.61263C8.16625 2.60952 8.16721 2.60648 8.169 2.60392C8.17077 2.60139 8.17328 2.59946 8.17618 2.59839C8.1762 2.59838 8.17622 2.59838 8.17624 2.59837L8.81045 2.36973L8.81046 2.36973C9.16516 2.24176 9.48729 2.03717 9.75393 1.77053C10.0206 1.50389 10.2251 1.18175 10.3531 0.82704L10.3531 0.827029L10.5817 0.192798C10.5817 0.192774 10.5818 0.19275 10.5818 0.192726C10.5828 0.189832 10.5848 0.187329 10.5873 0.185556C10.5898 0.18377 10.5929 0.182813 10.596 0.182813C10.5991 0.182813 10.6022 0.183771 10.6047 0.185556L10.6047 0.185558C10.6072 0.187329 10.6092 0.189829 10.6102 0.192725C10.6102 0.192749 10.6103 0.192774 10.6103 0.192798L10.8389 0.827029Z"
                                fill="#AAC8FF"
                                stroke="#A2A2A2"
                                strokeWidth="0.1"
                              />
                            </svg>
                          </div>
                        </button>
                      </div>
                    </PopoverTrigger>
                    <PopoverContent
                      alignOffset={10}
                      align="end"
                      className="rounded-lg z-50 gap-2.5 p-4 w-[280px] sm:w-[350px] md:w-[450px] flex flex-col lg:w-[590px]"
                    >
                      <div className="flex items-center justify-between w-full">
                        <p className="font-normal text-xs text-[#999999] leading-[18px]">
                          Generated on {formatDate(Date.now().toString())}
                        </p>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(summary);
                            toast.success("Copied to clipboard");
                          }}
                        >
                          <svg
                            width="13"
                            height="16"
                            viewBox="0 0 13 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M9.93685 4.31075L9.93685 4.31028C9.93352 3.60136 9.35783 3.025 8.65111 3.025H1.67793C0.968395 3.025 0.392187 3.60121 0.392187 4.31075V14.3159C0.392187 15.0254 0.968395 15.6016 1.67793 15.6016H8.65111C9.36065 15.6016 9.93685 15.0254 9.93685 14.3159L9.93685 4.31075ZM8.90437 14.3128C8.90437 14.4542 8.78941 14.5691 8.64803 14.5691H1.67486C1.53349 14.5691 1.41852 14.4542 1.41852 14.3128V4.31075C1.41852 4.16937 1.53349 4.05441 1.67486 4.05441H8.64803C8.78941 4.05441 8.90437 4.16937 8.90437 4.31075V14.3128Z"
                              fill="#165FE3"
                              stroke="#165FE3"
                              strokeWidth="0.2"
                            />
                            <path
                              d="M11.3152 0.4H4.342C3.63246 0.4 3.05625 0.976207 3.05625 1.68575C3.05625 1.97137 3.28533 2.20045 3.57095 2.20045C3.85657 2.20045 4.08566 1.97137 4.08566 1.68575C4.08566 1.54437 4.20062 1.42941 4.342 1.42941H11.3152C11.4565 1.42941 11.5715 1.54437 11.5715 1.68575V11.6909C11.5715 11.8322 11.4565 11.9472 11.3152 11.9472C11.0295 11.9472 10.8005 12.1763 10.8005 12.4619C10.8005 12.7475 11.0295 12.9766 11.3152 12.9766C12.0247 12.9766 12.6009 12.4004 12.6009 11.6909V1.68575C12.6009 0.976207 12.0247 0.4 11.3152 0.4Z"
                              fill="#165FE3"
                              stroke="#165FE3"
                              strokeWidth="0.2"
                            />
                          </svg>
                        </button>
                      </div>
                      <p className="font-normal text-sm text-black-label leading-[21px]">
                        {summary || (
                          <div className="space-y-2">
                            <Skeleton className="h-4 bg-gray-600 w-full rounded-full" />
                            <Skeleton className="h-4 bg-gray-600 w-full rounded-full" />
                            <Skeleton className="h-4 bg-gray-600 w-full rounded-full" />
                          </div>
                        )}
                      </p>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Thums up and down */}
              <div className="flex mt-4 gap-3 w-full ">
                {/* Thumbs Down Section */}
                <div className="flex gap-3 h-full w-full">
                  {opportunityDetails?.isOpportunityOpen === "open" &&
                  opportunityDetails?.reasonToClose !== "buyLater" ? (
                    <DropdownMenu modal>
                      <DropdownMenuTrigger
                        className="w-full"
                        disabled={
                          opportunityDetails?.isOpportunityOpen === "close"
                        }
                      >
                        <div className="flex items-center h-full w-full">
                          <div className="p-2 bg-red-50 rounded-lg flex items-center justify-center w-full h-[42px]">
                            <ThumbsDown
                              className="text-red-500 w-7 h-7"
                              size={28}
                            />                            
                          </div>
                        </div>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        className="border p-0 rounded-lg border-gray-300"
                        avoidCollisions
                        align="end"
                        alignOffset={10}
                      >
                        <DropdownMenuRadioGroup
                          value={reason}
                          onValueChange={(e) => {
                            setReason(e);
                            handleUpdateOpportunity(e, "", "");
                          }}
                        >
                          <DropdownMenuLabel
                            className="bg-gray-100 rounded-t py-3 px-4 text-base font-medium border-b"
                            style={{
                              fontSize: "14px",
                              fontWeight: "600",
                            }}
                          >
                            Mark unsuccessfull
                          </DropdownMenuLabel>
                          {opportunityCloseReason.map((reason) => (
                            <DropdownMenuRadioItem
                              key={reason.value}
                              value={reason.value}
                              className="py-3 px-4 cursor-pointer hover:bg-gray-200 bg-white text-sm font-normal"
                            >
                              {reason.label}
                            </DropdownMenuRadioItem>
                          ))}
                        </DropdownMenuRadioGroup>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : opportunityDetails?.reasonToClose === "buyLater" ? (
                    <DropdownMenu modal>
                      <DropdownMenuTrigger>
                        <div className="flex gap-3 h-fit">
                          <div
                            style={{
                              backgroundColor: "#FF8B5C21",
                              color: "#FF8B5C",
                              border: "1px solid #F5F5F5",
                            }}
                            className="flex items-center px-8 gap-3 rounded-md h-[42px] w-[160px]"
                          >
                            <button className="text-sm capitalize items-end font-medium">
                              Buy Later
                            </button>
                            <svg
                              className="bg-transparent"
                              width="15"
                              height="7"
                              viewBox="0 0 17 10"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <g clipPath="url(#clip0_1689_36934)">
                                <path
                                  d="M8.5026 7.38987L2.68796 1.51108C2.6411 1.46268 2.58547 1.42441 2.52428 1.3985C2.46309 1.37258 2.39757 1.35953 2.3315 1.3601C2.26543 1.36067 2.20012 1.37485 2.13936 1.40182C2.07859 1.42879 2.02358 1.46801 1.9775 1.51721C1.93142 1.56641 1.89519 1.62462 1.87091 1.68847C1.84663 1.75232 1.83477 1.82054 1.83603 1.88917C1.83729 1.95781 1.85163 2.02551 1.87824 2.08835C1.90484 2.15119 1.94318 2.20793 1.99103 2.25527L8.15414 8.48575C8.24743 8.58005 8.37245 8.63281 8.5026 8.63281C8.63276 8.63281 8.75778 8.58005 8.85107 8.48575L15.0142 2.25527C15.062 2.20793 15.1004 2.15119 15.127 2.08835C15.1536 2.02551 15.1679 1.95781 15.1692 1.88917C15.1704 1.82054 15.1586 1.75232 15.1343 1.68847C15.11 1.62462 15.0738 1.56641 15.0277 1.51721C14.9816 1.46801 14.9266 1.42879 14.8659 1.40182C14.8051 1.37485 14.7398 1.36067 14.6737 1.3601C14.6076 1.35953 14.5421 1.37258 14.4809 1.3985C14.4197 1.42441 14.3641 1.46268 14.3173 1.51108L8.5026 7.38987Z"
                                  fill="#FF8B5C"
                                />
                              </g>
                            </svg>
                          </div>
                        </div>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        className="border p-0 rounded-lg border-gray-300"
                        avoidCollisions
                        align="end"
                        alignOffset={10}
                      >
                        <DropdownMenuRadioGroup
                          value={reason}
                          onValueChange={(e) => {
                            setReason(e);
                            handleUpdateOpportunity(e, "", "", "");
                          }}
                        >
                          <DropdownMenuLabel
                            className="bg-gray-100 rounded-t py-3 px-4 text-base font-medium border-b"
                            style={{
                              fontSize: "14px",
                              fontWeight: "600",
                            }}
                          >
                            Mark unsuccessfull
                          </DropdownMenuLabel>
                          {opportunityCloseReason.map((reason) => (
                            <DropdownMenuRadioItem
                              key={reason.value}
                              value={reason.value}
                              className="py-3 px-4 cursor-pointer whitespace-nowrap hover:bg-gray-200 bg-white text-sm font-normal"
                            >
                              {reason.label}
                            </DropdownMenuRadioItem>
                          ))}
                        </DropdownMenuRadioGroup>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    (() => {
                      const values = close_reason_options.find(
                        (item) =>
                          item.value === opportunityDetails?.reasonToClose,
                      );
                      return (
                        <p
                          style={{
                            color: values?.textColor,
                            backgroundColor: values?.bgColor,
                          }}
                          className="w-[340px] h-[42px] px-2 py-1.5 rounded-lg gap-2.5 whitespace-nowrap flex items-center justify-center "
                        >
                          {values?.label}
                        </p>
                      );
                    })()
                  )}
                </div>

                {/* Thumbs Up Section */}
                {opportunityDetails?.isOpportunityOpen === "open" &&
                  (!reason || reason === "buyLater") && (
                    <div className="flex items-center h-full">                      
                      {opportunityDetails?.productSubcategory !==
                      "Remittance" ? (
                      ((opportunityDetails?.productSubcategory === 'Forex') || opportunityDetails?.ProductDetailsList?.some((item:any) => [9,16].includes(item?.product_id))) ?
                          <Link
                            state={{
                              opportunityId: contactDetails?.oppId,
                              contactId: contactDetails?.customerId,
                              name: contactDetails?.contactPerson,
                              number: contactDetails?.contactNumber,
                              email: contactDetails?.contactEmail,
                            }}
                            to={`/neworder?type=${'forex'}`}
                          >
                            <button className="p-2 bg-green-50 rounded-lg  flex items-center justify-center">
                              <ThumbsUp
                                className="text-green-500 w-7 h-7"
                                size={24}
                              />
                            </button>
                          </Link> 
                          :
                          <FormProvider {...methods}>
                            <OrderDetailForm
                              onSubmit={createOrder}
                              invoiceDetails={opportunityDetails}
                            />
                          </FormProvider>
                      ) : (
                        <Link
                          state={{
                            contactId: contactDetails?.contactId,
                            opportunityId: contactDetails?.oppId,
                            partnerId: contactDetails,
                            isLoan: remittanceProductListData?.took_loan ? "true" : "false",
                            mode: remittanceProductListData?.Mode,
                            purpose_id: purposeIDDetails?.id,
                            amount: opportunityDetails?.amount,
                            country: opportunityDetails?.country,
                          }}
                          to={
                            rowData?.product === "GIFT / Family Maintenance"
                              ? "/createRemit"
                              : "/createnewdeal"
                          }
                        >
                          <button className="p-2 bg-green-50 rounded-lg w-20 h-[34px] flex items-center justify-center">
                            <ThumbsUp
                              className="text-green-500 w-7 h-7"
                              size={24}
                            />
                          </button>
                        </Link>
                      )}
                    </div>
                  )}
              </div>
            </div>
          </div>
          {/* Tabs */}
          <div className="flex h-full bg-white-A700 p-2 overflow-auto rounded-lg mb-3">
            <Tabs
              value={activeTab}
              onValueChange={(value) => {
                setActiveTab(value);
              }}
              className="w-full h-full overflow-auto"
            >
              <TabsList className=" hide-scrollbar bg-[#F3F7FF] flex justify-between overflow-auto rounded-xl gap-2 h-11">
                {sheetTabs.map((tab) => {
                  if (
                    tab.value === "customers" &&
                    !rowData?.corporate_details?.travellers_list?.length
                  )
                    return null;
                  return (
                    <TabsTrigger
                      key={tab.value}
                      value={tab.value}
                      className={`relative text-base transition-colors py-3 px-5 h-11 ${
                        tab.value === activeTab
                          ? "text-blue-600 bg-white-A700 border-blue-700 border rounded-md before:bg-blue-600"
                          : "text-[#333333] font-inter font-semibold"
                      } font-inter text-[14px] font-[600] leading-[19.6px] text-center `}
                    >
                      {tab.label}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
              <TabsContent value="interaction">
                <div className="flex gap-0 overflow-auto h-full flex-grow flex-col ">
                  <InteractionCard
                    columns={InteractionColumn}
                    data={interactionList}
                    onRowClick={() => {}}
                    assignedTo={opportunityDetails?.assignedTo}
                  />
                </div>
              </TabsContent>
              <TabsContent value="customers">
                <div className="flex gap-0 overflow-auto h-full flex-grow flex-col ">
                  <DataTable
                    columns={[
                      {
                        accessorKey: "customerName",
                        header: "Name",
                      },
                      {
                        accessorKey: "customerEmail",
                        header: "Email",
                      },
                      {
                        accessorKey: "customerPhone",
                        header: "Phone",
                      },
                    ]}
                    data={
                      rowData?.corporate_details?.travellers_list?.map(
                        (data) => {
                          return {
                            customerName: data?.first_name
                              ? data?.first_name + " " + data?.last_name
                              : "N/A",
                            customerEmail: data?.email || "N/A",
                            customerPhone: data?.phone || "N/A",
                          };
                        },
                      ) || []
                    }
                    isToolbarVisible={false}
                    isPaginationVisible={false}
                    defaultSorting={[{ id: "customerName", desc: true }]}
                  />
                </div>
              </TabsContent>
              <TabsContent className="" value="files">
                <div className="">
                  {/* Personal Documents Section */}
                  {opportunityDetails?.generic_order?.generic_order_details
                    ?.personal_documents?.length > 0 && (
                    <div className="bg-white border-[#fff] rounded-[12px] p-3">
                      <h4 className="text-sm font-semibold mb-4 text-[#999999]">
                        Personal Documents
                      </h4>
                      <div className="flex flex-col flex-wrap gap-1.5 items-start">
                        {opportunityDetails?.generic_order?.generic_order_details?.personal_documents?.map(
                          (docItem: any, idx) => {
                            const { doc_list_id, doc } = docItem || {};
                            const verified = doc?.meta?.validated || false;

                            return (
                              <div
                                key={doc_list_id ?? idx}
                                className={`flex justify-between items-center py-2.5 px-4 w-full ${
                                  idx % 2 === 0 ? "bg-[#FAFAFA]" : "bg-white"
                                } rounded-[8px]`}
                              >
                                <div className="flex items-center gap-2 cursor-pointer">
                                  <span
                                    className={`text-[16px] ${
                                      verified
                                        ? "text-[#165FE3] font-semibold"
                                        : "text-[#2D3648] font-medium"
                                    }`}
                                  >
                                    {doc?.name || "Document"}
                                  </span>
                                </div>

                                {/* View and Download buttons */}
                                <div className="flex gap-4">
                                  {/* View Button */}
                                  <button
                                    onClick={() =>
                                      window.open(doc?.url, "_blank")
                                    }
                                    className="flex items-center gap-1 text-sm text-[#165FE3] hover:underline"
                                  >
                                    <Eye className="w-5 h-5 text-[#165FE3]" />
                                    <span className="font-semibold text-[14px]">
                                      View
                                    </span>
                                  </button>

                                  {/* Download Button */}
                                  <a
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleDownloadInvoice({
                                        url: doc?.url,
                                        name: doc?.name,
                                      });
                                    }}
                                    className="flex items-center gap-1 text-blue-600 text-sm leading-[140%] font-semibold"
                                  >
                                    <svg
                                      width="20"
                                      height="20"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      className="lucide lucide-download"
                                    >
                                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                      <polyline points="7 10 12 15 17 10" />
                                      <line x1="12" x2="12" y1="15" y2="3" />
                                    </svg>
                                    Download
                                  </a>
                                </div>
                              </div>
                            );
                          },
                        )}
                      </div>
                    </div>
                  )}
                  {/* Travel Documents Section */}
                  {opportunityDetails?.generic_order?.generic_order_details
                    ?.travel_documents?.length > 0 && (
                    <div className="bg-white border-[#fff] rounded-[12px] p-3 mt-6">
                      <h4 className="text-sm font-semibold mb-4 text-[#999999]">
                        Travel Documents
                      </h4>
                      <div className="flex flex-col flex-wrap gap-1.5 items-start">
                        {opportunityDetails?.generic_order?.generic_order_details?.travel_documents?.map(
                          (docItem: any, idx: any) => {
                            const { doc_list_id, doc } = docItem || {};
                            const verified = doc?.meta?.validated || false;

                            return (
                              <div
                                key={doc_list_id ?? idx}
                                className={`flex justify-between items-center py-2.5 px-4 w-full ${
                                  idx % 2 === 0 ? "bg-[#FAFAFA]" : "bg-white"
                                } rounded-[8px]`}
                              >
                                {/* Document name and verification */}
                                <div className="flex items-center gap-2 cursor-pointer">
                                  <span
                                    className={`text-[16px] ${
                                      verified
                                        ? "text-[#165FE3] font-semibold"
                                        : "text-[#2D3648] font-medium"
                                    }`}
                                  >
                                    {doc?.name || "Document"}
                                  </span>
                                </div>

                                {/* View and Download buttons */}
                                <div className="flex gap-4">
                                  {/* View Button */}
                                  <button
                                    onClick={() =>
                                      window.open(doc?.url, "_blank")
                                    }
                                    className="flex items-center gap-1 text-sm text-[#165FE3] hover:underline"
                                  >
                                    <Eye className="w-5 h-5 text-[#165FE3]" />
                                    <span className="font-semibold text-[14px]">
                                      View
                                    </span>
                                  </button>

                                  {/* Download Button */}
                                  <a
                                    onClick={(e) => {
                                      e.preventDefault(); // stop default link behavior
                                      handleDownloadInvoice({
                                        url: doc?.url,
                                        name: doc?.name,
                                      });
                                    }}
                                    className="flex items-center gap-1 text-blue-600 text-sm leading-[140%] font-semibold"
                                  >
                                    <svg
                                      width="20"
                                      height="20"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      className="lucide lucide-download"
                                    >
                                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                      <polyline points="7 10 12 15 17 10" />
                                      <line x1="12" x2="12" y1="15" y2="3" />
                                    </svg>
                                    Download
                                  </a>
                                </div>
                              </div>
                            );
                          },
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>
              <TabsContent value="details">
                <div className="flex flex-col gap-8 w-full pt-4">
                  <div className="flex flex-col gap-2.5 w-full bg-white-A700 p-2 rounded-md">
                    <h5 className="text-sm font-medium leading-4 text-[#999999]">
                      Contact Details
                    </h5>
                    <div className="flex flex-col gap-4 p-2.5 rounded-xl ">
                      {contactInsheet?.map((item) => {
                        if (item.value === "location") {
                          return (
                            <TooltipProvider key={item.value}>
                              <Tooltip>
                                <TooltipTrigger
                                  className="flex cursor-pointer items-end"
                                  asChild
                                >
                                  <div className="flex items-center gap-2.5">
                                    {item.icon}
                                    <div className="font-medium text-sm text-ellipsis text-black-label">
                                      {contactDetails?.[
                                        item.value as keyof typeof contactDetails
                                      ]?.substring(0, 30) || "Not Available"}
                                    </div>
                                    {contactDetails?.location && (
                                      <button
                                        onClick={() => {
                                          navigator.clipboard.writeText(
                                            (contactDetails?.[
                                              item.value as keyof typeof contactDetails
                                            ] as string) || "",
                                          );
                                          toast.success("Copied to clipboard");
                                        }}
                                        className=""
                                      >
                                        <svg
                                          width="13"
                                          height="16"
                                          viewBox="0 0 13 16"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            d="M9.93685 4.31075L9.93685 4.31028C9.93352 3.60136 9.35783 3.025 8.65111 3.025H1.67793C0.968395 3.025 0.392187 3.60121 0.392187 4.31075V14.3159C0.392187 15.0254 0.968395 15.6016 1.67793 15.6016H8.65111C9.36065 15.6016 9.93685 15.0254 9.93685 14.3159L9.93685 4.31075ZM8.90437 14.3128C8.90437 14.4542 8.78941 14.5691 8.64803 14.5691H1.67486C1.53349 14.5691 1.41852 14.4542 1.41852 14.3128V4.31075C1.41852 4.16937 1.53349 4.05441 1.67486 4.05441H8.64803C8.78941 4.05441 8.90437 4.16937 8.90437 4.31075V14.3128Z"
                                            fill="#165FE3"
                                            stroke="#165FE3"
                                            strokeWidth="0.2"
                                          />
                                          <path
                                            d="M11.3152 0.4H4.342C3.63246 0.4 3.05625 0.976207 3.05625 1.68575C3.05625 1.97137 3.28533 2.20045 3.57095 2.20045C3.85657 2.20045 4.08566 1.97137 4.08566 1.68575C4.08566 1.54437 4.20062 1.42941 4.342 1.42941H11.3152C11.4565 1.42941 11.5715 1.54437 11.5715 1.68575V11.6909C11.5715 11.8322 11.4565 11.9472 11.3152 11.9472C11.0295 11.9472 10.8005 12.1763 10.8005 12.4619C10.8005 12.7475 11.0295 12.9766 11.3152 12.9766C12.0247 12.9766 12.6009 12.4004 12.6009 11.6909V1.68575C12.6009 0.976207 12.0247 0.4 11.3152 0.4Z"
                                            fill="#165FE3"
                                            stroke="#165FE3"
                                            strokeWidth="0.2"
                                          />
                                        </svg>
                                      </button>
                                    )}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="bottom"
                                  className="bg-black-900 text-white-A700 max-w-[200px] whitespace-normal break-words border-0"
                                >
                                  {contactDetails?.[
                                    item.value as keyof typeof contactDetails
                                  ] || "Not Available"}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          );
                        }
                        return (
                          <div
                            key={item.value}
                            className="flex items-center gap-2.5"
                          >
                            {item.icon}
                            <span className="font-medium text-sm text-black-label">
                              {contactDetails?.[
                                item.value as keyof typeof contactDetails
                              ] || "Not Available"}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2.5 w-full flex-grow h-full bg-white-A700 p-2 rounded-md">
                    <h5 className="text-sm font-medium leading-4 text-[#999999]">
                      Lead Details
                    </h5>
                    <div className="flex flex-col gap-4 p-2.5 rounded-xl w-full">
                      <div className="flex gap-2.5 items-center">
                        <span className="text-[#666666] font-medium text-sm ">
                          Product
                        </span>
                        <span className="font-medium text-sm text-black-label">
                          {rowData.product}
                        </span>
                        <span className="text-[#999999] font-medium text-sm">
                          {`(${opportunityDetails?.productSubcategory})`}
                        </span>
                      </div>
                      <div className="flex gap-2.5 items-center">
                        <span className="text-[#666666] font-medium text-sm ">
                          Channel
                        </span>
                        <span className="font-medium text-sm text-black-label">
                          {channelDetails?.channel === "Refferal"
                            ? "Referral"
                            : channelDetails?.channel}
                        </span>
                        <span className="text-[#999999] font-medium text-sm">
                          {channelDetails?.source
                            ? ["outletID", "partnerOffice"].includes(
                                channelDetails?.source,
                              )
                              ? channelDetails?.channel === "Refferal"
                                ? extraDetails?.staffId
                                  ? `(${extraDetails?.staffName})`
                                  : `(${extraDetails?.partnerName})`
                                : channelDetails?.channel === "Walk-in"
                                  ? `(${extraDetails?.branchName})`
                                  : ``
                              : `(${channelDetails?.source})`
                            : ""}
                        </span>
                      </div>
                    </div>
                  </div>
                  {opportunityDetails?.amount &&
                    opportunityDetails?.country && (
                      <div className="flex flex-col gap-2.5 w-full flex-grow h-full">
                        <h5 className="text-sm font-medium leading-4 text-[#999999]">
                                Other details
                        </h5>
                      <div className="flex flex-col gap-4 p-2.5 rounded-xl bg-[#F9F9F9] w-full">
                          <div className="flex gap-2.5 items-center">
                              <span className="text-[#666666] font-medium text-sm ">
                                    Amount
                                  </span>
                                  <span className="font-medium text-sm text-black-label">
                                    {opportunityDetails?.amount}
                                  </span>
                                </div>
                                <div className="flex gap-2.5 items-center">
                                  <span className="text-[#666666] font-medium text-sm ">
                                    Country
                                  </span>
                                  <span className="font-medium text-sm text-black-label">
                                    {opportunityDetails?.country}
                                  </span>
                                </div>
                               {pancardDetails?.pan_card && (
                                <div className="flex gap-2.5 items-center">
                                  <span className="text-[#666666] font-medium text-sm">
                                    PanCard
                                  </span>
                                  <span className="font-medium text-sm text-black-label">
                                    {pancardDetails.pan_card}
                                  </span>
                                </div>
                              )}
                              </div>
                            </div>
                  )}
                  <div className="flex gap-0 overflow-auto h-full flex-grow flex-col mt-2">                    
                    {(opportunityDetails?.productSubcategory === "Remittance" && opportunityDetails?.product === "Education payment") ? (
                            Object.keys(remittanceProductListData).length === 0 ? (
                              <p>No data available</p>
                            ) : (
                              <DataTable
                                columns={remittanceProductListcolumns(getOpportunity)}
                                data={[remittanceProductListData]}
                                isToolbarVisible={false}
                                isPaginationVisible={false}
                                defaultSorting={[
                                  { id: "interactionDateandTime", desc: true },
                                ]}
                              />
                            )
                          ) : null}
                   </div>
                   <div className="flex gap-0 overflow-auto h-full flex-grow flex-col mt-2">                     
                      {
                        opportunityDetails?.ProductDetailsList?.length > 0 && (
                          <div>
                            <DataTable
                              columns={ForexProductListcolumns(getOpportunity)}
                              data={opportunityDetails?.ProductDetailsList}
                              isToolbarVisible={false}
                              isPaginationVisible={false}
                              defaultSorting={[
                              { id: "interactionDateandTime", desc: true },
                              ]}
                            />
                          </div>
                      )}
                    </div>
                </div>
              </TabsContent>
              <TabsContent value="notes">
                {!detailNote ? (
                  <div className="flex items-center gap-5 w-full">
                    <div className=" bg-[#FAFAFA] w-full flex items-center gap-2 py-2.5 px-2 rounded-lg text-left">
                      <div className="flex items-center justify-between w-full">
                        <div className="flex flex-col gap-1">
                          {" "}
                          <p className="font-normal text-xs leading-[18px] text-[#999999]">
                            {extraDetails?.note_last_edited ? (
                              <>
                                last edited on{" "}
                                {formatUnixTimestamp(
                                  extraDetails.note_last_edited,
                                )}
                              </>
                            ) : (
                              "Click on icon to add note"
                            )}
                          </p>
                          <p className="font-normal text-xs leading-[18px] text-[#999999] whitespace-pre-wrap">
                            {extraDetails?.note}
                          </p>
                        </div>

                        <button onClick={openDetailNote}>
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M3.33594 4.24609H2.5026C2.06058 4.24609 1.63665 4.42169 1.32409 4.73425C1.01153 5.04681 0.835938 5.47073 0.835938 5.91276V13.4128C0.835938 13.8548 1.01153 14.2787 1.32409 14.5913C1.63665 14.9038 2.06058 15.0794 2.5026 15.0794H10.0026C10.4446 15.0794 10.8686 14.9038 11.1811 14.5913C11.4937 14.2787 11.6693 13.8548 11.6693 13.4128V12.5794"
                              stroke="#165FE3"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M10.8333 2.58472L13.3333 5.08472M14.4875 3.90556C14.8157 3.57735 15.0001 3.13221 15.0001 2.66806C15.0001 2.20391 14.8157 1.75876 14.4875 1.43056C14.1593 1.10235 13.7142 0.917969 13.25 0.917969C12.7858 0.917969 12.3407 1.10235 12.0125 1.43056L5 8.41806V10.9181H7.5L14.4875 3.90556Z"
                              stroke="#165FE3"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between w-full">
                    <textarea
                      value={inputValue}
                      onChange={(e) => {
                        setInputValue(e.target.value);
                      }}
                      rows={5}
                      className="w-full border rounded-md border-black border-solid flex-1 px-2 py-2 mr-4"
                    />
                    <button
                      onClick={() => {
                        handleUpdateNote(opportunityDetails?.id, inputValue);
                        setDetailNote(false);
                      }}
                      className="text-white-A700 bg-blue-700 whitespace-nowrap rounded-lg py-2 px-4"
                    >
                      Save
                    </button>
                  </div>
                )}
                {forexLeadDetails &&
                  rowData?.productSubCategory === "Forex" && (
                    <div className="mt-4 flex items-center gap-2.5 text-[14px] font-normal text-[#999999]">
                      <img
                        src="/images/remit/lightInfo.svg"
                        alt="info"
                        className="w-4 h-4 object-fill"
                      />
                      <span>Please check your email for more details.</span>
                    </div>
                  )}
                {forexLeadDetails &&
                  rowData?.productSubCategory === "Forex" &&
                  Object.keys(forexLeadDetails)?.length > 0 && (
                    <div className="rounded-md py-4 w-full shadow-sm">
                      <div className="flex justify-between items-center mb-4 bg-[#F5F9FF] rounded-xl p-4">
                        <div className="flex items-center gap-2 text-sm text-gray-700 font-bold">
                          <span>
                            {opportunityDetails?.generic_order?._product
                              ?.type === "Card"
                              ? "Forex Card"
                              : "Forex currency notes"}
                          </span>
                          <span className="w-1 h-1 rounded-full"></span>
                          <span>
                            {forexLeadDetails?.buy_sell
                              ?.charAt(0)
                              .toUpperCase() +
                              forexLeadDetails?.buy_sell
                                ?.slice(1)
                                .toLowerCase()}
                          </span>
                          <span className="w-1 h-1 rounded-full"></span>
                          <span>{forexLeadDetails?.city || "N/A"}</span>
                        </div>
                        <div className="text-sm font-semibold text-gray-800">
                          {forexLeadDetails?.buy_sell === "Buy"
                            ? forexLeadDetails?.currency_want
                            : forexLeadDetails?.currency_have || "N/A"}{" "}
                          {/* {Math.ceil(
                            (opportunityDetails?.generic_order?.grand_total ||
                              0) * 10000,
                          ) / 10000} */}
                          {forexLeadDetails?.fx_amount || "N/A"}
                        </div>
                      </div>

                      <div className="space-y-2 text-sm text-[#999999] px-4">
                        <div className="flex justify-between">
                          <span>Customer rate</span>
                          <span className="text-gray-800 font-medium">
                            INR {forexLeadDetails?.rate || 0}
                          </span>
                        </div>
                        {/* <div className="flex justify-between">
                          <span>FCY amount</span>
                          <span className="text-gray-800 font-medium">
                            {forexLeadDetails?.buy_sell === "Buy"
                              ? forexLeadDetails?.currency_want
                              : forexLeadDetails?.currency_have || "N/A"}{" "}
                            {forexLeadDetails?.fx_amount || 0}
                          </span>
                        </div> */}
                        <div className="flex justify-between">
                          <span>Coupon code</span>
                          <span className="text-gray-800 font-medium">
                            {forexLeadDetails?.discount_coupon_id?.[0]?.coupon_name || "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                {rowData?.corporate_details?.Destination && (
                  <div className="flex justify-between items-center bg-[#F5F9FF] py-1 px-2">
                    <h4 className="text-black-label font-medium leading-[150%] -tracking-[4%] flex items-center gap-2.5">
                      Visa{" "}
                      <div class="w-[7px] h-[7px] bg-[#C4D7F9] rounded-full"></div>
                      {rowData?.corporate_details?.Destination}
                    </h4>
                    <div className=" flex items-center gap-1">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6 4.3125C5.6925 4.3125 5.4375 4.0575 5.4375 3.75V1.5C5.4375 1.1925 5.6925 0.9375 6 0.9375C6.3075 0.9375 6.5625 1.1925 6.5625 1.5V3.75C6.5625 4.0575 6.3075 4.3125 6 4.3125Z"
                          fill="#333333"
                        />
                        <path
                          d="M12 4.3125C11.6925 4.3125 11.4375 4.0575 11.4375 3.75V1.5C11.4375 1.1925 11.6925 0.9375 12 0.9375C12.3075 0.9375 12.5625 1.1925 12.5625 1.5V3.75C12.5625 4.0575 12.3075 4.3125 12 4.3125Z"
                          fill="#333333"
                        />
                        <path
                          d="M15.375 7.38086H2.625C2.3175 7.38086 2.0625 7.12586 2.0625 6.81836C2.0625 6.51086 2.3175 6.25586 2.625 6.25586H15.375C15.6825 6.25586 15.9375 6.51086 15.9375 6.81836C15.9375 7.12586 15.6825 7.38086 15.375 7.38086Z"
                          fill="#333333"
                        />
                        <path
                          d="M12 17.0625H6C3.2625 17.0625 1.6875 15.4875 1.6875 12.75V6.375C1.6875 3.6375 3.2625 2.0625 6 2.0625H12C14.7375 2.0625 16.3125 3.6375 16.3125 6.375V12.75C16.3125 15.4875 14.7375 17.0625 12 17.0625ZM6 3.1875C3.855 3.1875 2.8125 4.23 2.8125 6.375V12.75C2.8125 14.895 3.855 15.9375 6 15.9375H12C14.145 15.9375 15.1875 14.895 15.1875 12.75V6.375C15.1875 4.23 14.145 3.1875 12 3.1875H6Z"
                          fill="#333333"
                        />
                        <path
                          d="M6.375 10.875C6.2775 10.875 6.18 10.8525 6.09 10.815C6 10.7775 5.91751 10.725 5.84251 10.6575C5.77501 10.5825 5.72249 10.5 5.68499 10.41C5.64749 10.32 5.625 10.2225 5.625 10.125C5.625 9.93001 5.70751 9.73502 5.84251 9.59252C5.91751 9.52502 6 9.4725 6.09 9.435C6.225 9.375 6.37501 9.36 6.52501 9.39C6.57001 9.3975 6.615 9.4125 6.66 9.435C6.705 9.45 6.75 9.47252 6.795 9.50252C6.8325 9.53252 6.86999 9.56252 6.90749 9.59252C6.93749 9.63002 6.97499 9.66751 6.99749 9.70501C7.02749 9.75001 7.05001 9.79501 7.06501 9.84001C7.08751 9.885 7.10251 9.93 7.11001 9.975C7.11751 10.0275 7.125 10.0725 7.125 10.125C7.125 10.32 7.04249 10.515 6.90749 10.6575C6.76499 10.7925 6.57 10.875 6.375 10.875Z"
                          fill="#333333"
                        />
                        <path
                          d="M9 10.8742C8.805 10.8742 8.61001 10.7917 8.46751 10.6567C8.43751 10.6192 8.40751 10.5817 8.37751 10.5442C8.34751 10.4992 8.32499 10.4542 8.30999 10.4092C8.28749 10.3642 8.27249 10.3192 8.26499 10.2742C8.25749 10.2217 8.25 10.1767 8.25 10.1242C8.25 10.0267 8.27249 9.92917 8.30999 9.83917C8.34749 9.74917 8.40001 9.66668 8.46751 9.59168C8.67751 9.38168 9.015 9.31416 9.285 9.43416C9.3825 9.47166 9.45749 9.52418 9.53249 9.59168C9.66749 9.73418 9.75 9.92917 9.75 10.1242C9.75 10.1767 9.74251 10.2217 9.73501 10.2742C9.72751 10.3192 9.71251 10.3642 9.69001 10.4092C9.67501 10.4542 9.65249 10.4992 9.62249 10.5442C9.59249 10.5817 9.56249 10.6192 9.53249 10.6567C9.45749 10.7242 9.3825 10.7767 9.285 10.8142C9.195 10.8517 9.0975 10.8742 9 10.8742Z"
                          fill="#333333"
                        />
                        <path
                          d="M6.375 13.4992C6.2775 13.4992 6.18 13.4767 6.09 13.4392C6 13.4017 5.91751 13.3492 5.84251 13.2817C5.77501 13.2067 5.72249 13.1317 5.68499 13.0342C5.64749 12.9442 5.625 12.8467 5.625 12.7492C5.625 12.5542 5.70751 12.3592 5.84251 12.2167C5.91751 12.1492 6 12.0967 6.09 12.0592C6.3675 11.9392 6.69749 12.0067 6.90749 12.2167C6.93749 12.2542 6.97499 12.2917 6.99749 12.3292C7.02749 12.3742 7.05001 12.4192 7.06501 12.4642C7.08751 12.5092 7.10251 12.5542 7.11001 12.6067C7.11751 12.6517 7.125 12.7042 7.125 12.7492C7.125 12.9442 7.04249 13.1392 6.90749 13.2817C6.76499 13.4167 6.57 13.4992 6.375 13.4992Z"
                          fill="#333333"
                        />
                      </svg>
                      <time
                        className="text-black-label font-medium leading-4 -tracking-[4%]"
                        datetime={
                          rowData?.corporate_details?.trip_start_date as string
                        }
                      >
                        {rowData?.corporate_details?.trip_start_date}
                      </time>
                      <span className="text-[#999999] text-black-label font-medium leading-4 -tracking-[4%]">
                        -
                      </span>
                      <time
                        className="text-black-label font-medium leading-4 -tracking-[4%]"
                        datetime={
                          rowData?.corporate_details?.trip_end_date as string
                        }
                      >
                        {rowData?.corporate_details?.trip_end_date}
                      </time>
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
          {opportunityDetails?.isOpportunityOpen === "open" &&   activeTab === "interaction" && (
            <Sheet>
              <SheetTrigger asChild>
                <div className="relative">
                  <button className="" />
                  <div className="fixed bottom-0 left-0 right-0 mx-2 z-50 transition-all duration-300 ease-out bg-white shadow-lg rounded-t-2xl">
                    <button className="cursor-pointer w-full flex items-center justify-center h-12 bg-white-A700 border-t border-blue-700 rounded-t-2xl">
                      <span className="text-blue-700 text-sm font-semibold">
                        + Add Interaction
                      </span>
                    </button>
                  </div>
                </div>
              </SheetTrigger>
              <SheetOverlay />
              <SheetContent
                className="bg-white-A700 px-5 pt-3 pb-5"
                side="bottom"
              >
                <div className=" bg-white z-70 relative rounded-t-2xl bg-white-A700">
                  <div className="flex w-full items-center justify-center pb-[18px]">
                    <div className="flex relative items-center justify-center w-full ">
                      <p className="text-base text-blue-700 font-bold ">
                        + Add Interaction
                      </p>
                      <Popover>
                        {opportunityDetails?.productSubcategory !==
                          "Remittance" && (
                          <PopoverTrigger className="absolute right-0">
                            <svg
                              width="8"
                              height="22"
                              viewBox="0 0 8 22"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M3.03125 10.0156H3.02139V10.0256H3.03125V10.0156ZM3.03125 3.01562H3.02139V3.02563H3.03125V3.01562ZM3.03125 17.0156H3.02139V17.0256H3.03125V17.0156Z"
                                fill="black"
                                stroke="#292929"
                                stroke-width="4.36364"
                                stroke-linejoin="round"
                              />
                            </svg>
                          </PopoverTrigger>
                        )}
                        <PopoverContent className="py-1">
                          <div className="col-span-2">
                            {pipelineType === "retail" &&
                              opportunityDetails?.productSubcategory !==
                                "Remittance" && (
                                <DocPopup
                                  buttonName="Collect Documents"
                                  buttonState={buttonState}
                                  onSubmit={handleRequestDoc}
                                  selectedMode={selectedMode}
                                  setSelectedMode={setSelectedMode}
                                  expires={expires}
                                  setExpires={setExpires}
                                  popupnotes={popupnotes}
                                  setPopupNotes={setPopupNotes}
                                  channel={channel}
                                  setChannel={setChannel}
                                  className="bg-white-A700 text-[#000000]"
                                />
                              )}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  <div className="mb-4">
                    <textarea
                      onChange={(e) => {
                        setInteractionNote(e.target.value);
                      }}
                      value={interactionNote}
                      className="w-full p-3 border border-black-200 rounded-xl text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                      placeholder="Write your notes here..."
                      rows={3}
                      style={{ resize: "none" }}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <Select
                      disabled={selectedType === "outbound"}
                      onValueChange={(val) => {
                        setSelectedType("inbound");
                        setSelectedChannelOptions(val);
                      }}
                      value={
                        selectedType === "inbound" ? selectedChannelOption : ""
                      }
                    >
                      <SelectTrigger
                        hideIcon
                        className={`${
                          selectedType === "outbound"
                            ? "hidden"
                            : selectedType !== ""
                              ? "col-span-2"
                              : ""
                        } bg-[#F9F9F9] border-none hover:bg-blue-100 focus:ring-0 whitespace-nowrap focus:ring-offset-0 [&>svg]:hidden`}
                      >
                        <div className="flex gap-3 items-center">
                          <SelectValue placeholder="I contacted" />
                          {selectedChannelOption && (
                            <Img
                              src={
                                channel_options[
                                  selectedType as "inbound" | "outbound"
                                ].find(
                                  (val) =>
                                    val.label ===
                                    channel_options_inbound.find(
                                      (val) =>
                                        selectedChannelOption ===
                                        val.value.toString(),
                                    )?.label,
                                )?.icon
                              }
                              alt="channel"
                              width={20}
                              height={20}
                            />
                          )}
                        </div>
                        {selectedChannelOption && (
                          <X
                            className="h-4 w-4 z-50 text-blue-600 ml-2 cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              setSelectedType("");
                              setSelectedChannelOptions("");
                            }}
                          />
                        )}
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {channel_options_inbound
                            .filter((val) => val.label !== "Meeting")
                            .map((channel) => (
                              <SelectItem
                                className="cursor-pointer hover:bg-[#e5eff3]"
                                key={channel.value}
                                value={channel.value.toString()}
                              >
                                {channel.label}
                              </SelectItem>
                            ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>

                    <Select
                      disabled={selectedType === "inbound"}
                      onValueChange={(val) => {
                        setSelectedType("outbound");
                        setSelectedChannelOptions(val);
                      }}
                      value={
                        selectedType === "outbound" ? selectedChannelOption : ""
                      }
                    >
                      <SelectTrigger
                        hideIcon
                        className={`${
                          selectedType === "inbound"
                            ? "hidden"
                            : selectedType !== ""
                              ? "col-span-2"
                              : ""
                        } bg-[#F9F9F9] border-none hover:bg-blue-100 focus:ring-0 focus:ring-offset-0 whitespace-nowrap [&>svg]:hidden`}
                      >
                        <div className="flex items-center gap-3">
                          <SelectValue placeholder="Customer contacted" />
                          {selectedChannelOption && (
                            <Img
                              src={
                                channel_options[
                                  selectedType as "inbound" | "outbound"
                                ].find(
                                  (val) =>
                                    (val.label === "Referral"
                                      ? "Refferal"
                                      : val.label) ===
                                    channel_options_outbound.find(
                                      (val) =>
                                        selectedChannelOption ===
                                        val.value.toString(),
                                    )?.label,
                                )?.icon
                              }
                              alt="channel"
                              width={18}
                              height={18}
                            />
                          )}
                        </div>
                        {selectedChannelOption && (
                          <X
                            className="h-4 w-4 z-50 text-blue-600 ml-2 cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              setSelectedType("");
                              setSelectedChannelOptions("");
                            }}
                          />
                        )}
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {channel_options_outbound
                            .filter((val) => val.label !== "Meeting")
                            .map((channel) => {
                              return (
                                <SelectItem
                                  className="cursor-pointer hover:bg-[#e5eff3]"
                                  key={channel.value}
                                  value={channel.value.toString()}
                                >
                                  {channel.label === "Refferal"
                                    ? "Referral"
                                    : channel.label}
                                </SelectItem>
                              );
                            })}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={handlePrepareQuote}
                      disabled={!buttonState()}
                      className="w-full h-12 text-sm text-blue-600 bg-white border border-blue-600 rounded-lg disabled:opacity-50 shadow-sm"
                    >
                      Prepare Quote
                    </button>
                    <button
                      onClick={handleAddInteraction}
                      disabled={!buttonState()}
                      className="w-full h-12 text-sm text-white bg-blue-600 rounded-lg disabled:opacity-50 shadow-md"
                      style={{ color: "white" }}
                    >
                      Submit
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-3 mt-4">
                    {productDetails?.name?.toLowerCase() === "visa" && (
                      <button
                        disabled={extraDetails?.quote_requested}
                        className="px-4 py-[11px] text-sm text-center font-semibold border-blue-700 border rounded-lg text-blue-700 cursor-pointer disabled:cursor-not-allowed"
                      >
                        <label
                          htmlFor="upload_letter"
                          className="flex items-center text-center justify-center w-full gap-2.5 cursor-pointer"
                        >
                          <input
                            type="file"
                            id="upload_letter"
                            className="hidden"
                            accept=".pdf"
                            onChange={(e) =>
                              handleQuoteUploadVisa(
                                e?.target?.files?.[0] as File,
                              )
                            }
                          />
                          <p>Upload Quotation</p>
                          <svg
                            width="15"
                            height="19"
                            viewBox="0 0 15 19"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M0.5 4C0.5 1.79086 2.29086 0 4.5 0H10.0546C10.3381 0 10.6083 0.12032 10.7979 0.33104L14.7433 4.71477C14.9086 4.8984 15 5.13669 15 5.38374V14.5C15 16.7091 13.2091 18.5 11 18.5H4.5C2.29086 18.5 0.5 16.7091 0.5 14.5V4Z"
                              fill="#165FE3"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M11.26 10.8978C10.9583 11.1817 10.4837 11.1672 10.1998 10.8655L7.7461 8.25736L5.2923 10.8655C5.0085 11.1672 4.5339 11.1817 4.2322 10.8978C3.9305 10.614 3.916 10.1394 4.1999 9.83766L6.8968 6.97106C7.3603 6.47836 8.1319 6.47836 8.5954 6.97106L11.2923 9.83766C11.5762 10.1394 11.5617 10.614 11.26 10.8978Z"
                              fill="white"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M7.74609 15.6016C7.33189 15.6016 6.99609 15.2658 6.99609 14.8516V7.35156C6.99609 6.93736 7.33189 6.60156 7.74609 6.60156C8.16029 6.60156 8.49609 6.93736 8.49609 7.35156V14.8516C8.49609 15.2658 8.16029 15.6016 7.74609 15.6016Z"
                              fill="white"
                            />
                            <path
                              d="M14.9922 6V5.38374C14.9922 5.13669 14.9008 4.8984 14.7355 4.71477L10.7901 0.33104C10.6005 0.12032 10.3303 0 10.0468 0H8.99219V2C8.99219 4.20914 10.7831 6 12.9922 6H14.9922Z"
                              fill="#98BDFF"
                            />
                          </svg>
                        </label>
                      </button>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          )}
        </SheetContent>
      </Sheet>
    );
  };

  return (
    <>
      {(loading || isLoading) && <Spinner />}
      {isMobile ? (
        mobileView()
      ) : (
        <Sheet modal open={isOpen} onOpenChange={onOpenChange}>
          <SheetPortal>
            {/* <SheetOverlay /> */}
            <div className="">
              <SheetContent className="bg-[#EBEEF5] w-[50%] z-50 sm:w-full p-2.5 sm:p-2 gap-3 flex h-full overflow-auto flex-col">
                <button
                  onClick={() => onOpenChange(false)}
                  className="w-full hidden sm:flex justify-end"
                >
                  <XIcon />
                </button>
                <div className="p-2.5 gap-[30px] flex border rounded-lg  bg-white-A700 flex-col w-full">
                  <div className="flex w-full justify-between">
                    <div className="flex flex-col gap-[18px] w-full">
                      <div className="flex gap-2">
                        <div className="flex items-start gap-2">
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="text-xl leading-[30px] whitespace-nowrap capitalize text-black-label after:pl-2 font-semibold after:content-[''] after:border-r after:border-[#C4C4C4] after:w-[4px] after:h-[4px]">
                                {rowData?.pipelineType?.toLowerCase() ===
                                "coorporate"
                                  ? rowData?.customerName
                                  : contactDetails?.contactPerson}
                              </h4>
                              <div className="flex items-center gap-2">
                                <p className="text-sm whitespace-nowrap leading-[21px] text-[#999999] font-normal">
                                  {rowData.product}
                                </p>
                                <p className="text-sm text-[#999999] font-normal leading-[21px]">
                                  {opportunityDetails?.mode && "|"}
                                </p>
                                <p className="text-sm text-[#999999] font-normal leading-[21px]">
                                  {opportunityDetails?.mode}
                                </p>
                                <p className="text-sm text-[#999999] font-normal leading-[21px]">
                                  {opportunityDetails?.isLoan &&
                                    `- ${opportunityDetails?.isLoan}`}
                                </p>
                                {/* <p className="text-sm text-[#999999] font-normal leading-[21px]">
                                  {Number(opportunityDetails?.forex_orderID) >
                                    0 &&
                                    `Booking-ID - ${opportunityDetails.forex_orderID}`}
                                </p> */}
                                <Popover>
                                  <PopoverTrigger
                                    className="disabled:opacity-50"
                                    disabled={
                                      opportunityDetails?.isOpportunityOpen !==
                                      "open"
                                    }
                                  >
                                    <button
                                      onClick={() => setEditReminder(false)}
                                      className="text-sm font-medium text-blue-700"
                                    >
                                      {extraDetails?.followup_time ? (
                                        <div className="relative">
                                          {new Date(
                                            extraDetails?.followup_time,
                                          ) <= new Date() && (
                                            <div className="absolute bottom-3 -right-1">
                                              <span className="animate-ping absolute inline-flex h-2 w-2  rounded-full bg-red-500 opacity-75"></span>
                                              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                                            </div>
                                          )}
                                          <svg
                                            width="19"
                                            height="20"
                                            viewBox="0 0 19 20"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                          >
                                            <path
                                              d="M4.88693 8.70306C5.04296 6.015 6.94433 3.53906 9.50273 3.53906C12.0612 3.53906 13.9625 6.015 14.1185 8.70306C14.1896 9.92548 14.3771 11.0978 14.9557 12.1706C15.5716 13.3124 15.0178 14.8737 13.7789 15.0274C12.354 15.2041 10.6857 15.3724 9.50273 15.3724C8.31982 15.3724 6.65148 15.2041 5.22655 15.0274C3.98773 14.8737 3.43383 13.3124 4.04975 12.1706C4.62843 11.0978 4.81596 9.92548 4.88693 8.70306Z"
                                              fill="#98BDFF"
                                            />
                                            <path
                                              d="M9.4974 17.7057C10.3719 17.7057 11.0807 16.9596 11.0807 16.0391C11.0807 16.0391 10.1205 16.2474 9.4974 16.2474C8.87428 16.2474 7.91406 16.0391 7.91406 16.0391C7.91406 16.9596 8.62292 17.7057 9.4974 17.7057Z"
                                              fill="#165FE3"
                                            />
                                            <path
                                              d="M9.5026 2.28906C9.06537 2.28906 8.71094 2.66216 8.71094 3.1224C8.71094 3.23153 8.73089 3.33575 8.76707 3.43128C8.76707 3.43128 9.21293 3.33073 9.5026 3.33073C9.79228 3.33073 10.2381 3.43128 10.2381 3.43128C10.2743 3.33575 10.2943 3.23153 10.2943 3.1224C10.2943 2.66216 9.93984 2.28906 9.5026 2.28906Z"
                                              fill="#165FE3"
                                            />
                                            <path
                                              fillRule="evenodd"
                                              clipRule="evenodd"
                                              d="M4.70144 2.87531C4.94313 3.1086 4.95938 3.50395 4.73775 3.75836C3.99278 4.6135 3.4453 5.66089 3.16992 6.82081C3.09042 7.15569 2.76806 7.35931 2.44993 7.27563C2.13179 7.19194 1.93835 6.85262 2.01786 6.51775C2.34239 5.15081 2.98717 3.91833 3.86254 2.91352C4.08417 2.65912 4.45976 2.642 4.70144 2.87531ZM14.6959 2.87531C14.9375 2.642 15.3131 2.65912 15.5347 2.91352C16.4102 3.91833 17.0549 5.15081 17.3795 6.51775C17.459 6.85262 17.2655 7.19194 16.9474 7.27563C16.6292 7.35931 16.3068 7.15569 16.2274 6.82081C15.952 5.66089 15.4045 4.6135 14.6595 3.75836C14.4379 3.50395 14.4542 3.1086 14.6959 2.87531Z"
                                              fill="#165FE3"
                                            />
                                          </svg>
                                        </div>
                                      ) : (
                                        <svg
                                          width="19"
                                          height="20"
                                          viewBox="0 0 19 20"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            d="M4.88693 8.70306C5.04296 6.015 6.94433 3.53906 9.50273 3.53906C12.0612 3.53906 13.9625 6.015 14.1185 8.70306C14.1896 9.92548 14.3771 11.0978 14.9557 12.1706C15.5716 13.3124 15.0178 14.8737 13.7789 15.0274C12.354 15.2041 10.6857 15.3724 9.50273 15.3724C8.31982 15.3724 6.65148 15.2041 5.22655 15.0274C3.98773 14.8737 3.43383 13.3124 4.04975 12.1706C4.62843 11.0978 4.81596 9.92548 4.88693 8.70306Z"
                                            fill="#C4C4C4"
                                          />
                                          <path
                                            d="M9.4974 17.7057C10.3719 17.7057 11.0807 16.9596 11.0807 16.0391C11.0807 16.0391 10.1205 16.2474 9.4974 16.2474C8.87428 16.2474 7.91406 16.0391 7.91406 16.0391C7.91406 16.9596 8.62292 17.7057 9.4974 17.7057Z"
                                            fill="#165FE3"
                                          />
                                          <path
                                            d="M9.5026 2.28906C9.06537 2.28906 8.71094 2.66216 8.71094 3.1224C8.71094 3.23153 8.73089 3.33575 8.76707 3.43128C8.76707 3.43128 9.21293 3.33073 9.5026 3.33073C9.79228 3.33073 10.2381 3.43128 10.2381 3.43128C10.2743 3.33575 10.2943 3.23153 10.2943 3.1224C10.2943 2.66216 9.93984 2.28906 9.5026 2.28906Z"
                                            fill="#165FE3"
                                          />
                                          <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M4.70144 2.87531C4.94313 3.1086 4.95938 3.50395 4.73775 3.75836C3.99278 4.6135 3.4453 5.66089 3.16992 6.82081C3.09042 7.15569 2.76806 7.35931 2.44993 7.27563C2.13179 7.19194 1.93835 6.85262 2.01786 6.51775C2.34239 5.15081 2.98717 3.91833 3.86254 2.91352C4.08417 2.65912 4.45976 2.642 4.70144 2.87531ZM14.6959 2.87531C14.9375 2.642 15.3131 2.65912 15.5347 2.91352C16.4102 3.91833 17.0549 5.15081 17.3795 6.51775C17.459 6.85262 17.2655 7.19194 16.9474 7.27563C16.6292 7.35931 16.3068 7.15569 16.2274 6.82081C15.952 5.66089 15.4045 4.6135 14.6595 3.75836C14.4379 3.50395 14.4542 3.1086 14.6959 2.87531Z"
                                            fill="#999999"
                                          />
                                        </svg>
                                      )}
                                    </button>
                                  </PopoverTrigger>
                                  <PopoverContent
                                    alignOffset={10}
                                    align="start"
                                    className="rounded-lg z-50 gap-4 p-2.5 flex flex-col"
                                  >
                                    {!extraDetails?.followup_time ||
                                    editReminder ? (
                                      <>
                                        <DateTimePicker
                                          date={date}
                                          setDate={setDate}
                                        />
                                        <Input
                                          type="text"
                                          value={followupNote}
                                          defaultValue={
                                            extraDetails?.followup_note
                                          }
                                          onChange={(e) =>
                                            setFollowupNote(e.target.value)
                                          }
                                          placeholder="Note (if any)"
                                          className="w-full placeholder:text-[#999999] border-0 h-5 border-b border-gray-200 p-0 rounded-none focus:border-b focus:border-blue-700 focus-visible:ring-0 focus-visible:ring-offset-0"
                                        />
                                        {date && (
                                          <div className="flex gap-2.5 items-center">
                                            <PopoverClose asChild>
                                              <button
                                                onClick={() => {
                                                  setDate(undefined);
                                                  setFollowupNote("");
                                                  setEditReminder(false);
                                                }}
                                                className="text-sm w-full font-medium text-blue-700 border border-blue-700 rounded-[4px] py-[8px] px-4"
                                              >
                                                Cancel
                                              </button>
                                            </PopoverClose>
                                            <button
                                              onClick={() => {
                                                handleUpdateOpportunity(
                                                  "",
                                                  date,
                                                  followupNote,
                                                  "",
                                                );
                                                setEditReminder(false);
                                                setIsClearReminder(false);
                                              }}
                                              className="text-sm w-full font-medium border border-blue-700 rounded-[4px] py-[8px] px-4 bg-blue-700 text-white-A700"
                                            >
                                              Save
                                            </button>
                                          </div>
                                        )}
                                      </>
                                    ) : (
                                      <div className="flex flex-col gap-2.5">
                                        <div className="flex flex-col items-start border-b border-gray-200 pb-2.5">
                                          <p className="text-sm font-medium text-[#999999] flex w-full justify-between items-center">
                                            Reminder set for
                                            <button
                                              onClick={() => {
                                                setEditReminder(true);
                                                setDate(
                                                  new Date(
                                                    extraDetails?.followup_time,
                                                  ),
                                                );
                                                setFollowupNote(
                                                  extraDetails?.followup_note,
                                                );
                                              }}
                                              className=""
                                            >
                                              <svg
                                                width="16"
                                                height="17"
                                                viewBox="0 0 16 17"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                              >
                                                <path
                                                  d="M3.33594 4.74609H2.5026C2.06058 4.74609 1.63665 4.92169 1.32409 5.23425C1.01153 5.54681 0.835938 5.97073 0.835938 6.41276V13.9128C0.835938 14.3548 1.01153 14.7787 1.32409 15.0913C1.63665 15.4038 2.06058 15.5794 2.5026 15.5794H10.0026C10.4446 15.5794 10.8686 15.4038 11.1811 15.0913C11.4937 14.7787 11.6693 14.3548 11.6693 13.9128V13.0794"
                                                  stroke="#165FE3"
                                                  strokeWidth="1.5"
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                />
                                                <path
                                                  d="M10.8333 3.08472L13.3333 5.58472M14.4875 4.40556C14.8157 4.07735 15.0001 3.63221 15.0001 3.16806C15.0001 2.70391 14.8157 2.25876 14.4875 1.93056C14.1593 1.60235 13.7142 1.41797 13.25 1.41797C12.7858 1.41797 12.3407 1.60235 12.0125 1.93056L5 8.91806V11.4181H7.5L14.4875 4.40556Z"
                                                  stroke="#165FE3"
                                                  strokeWidth="1.5"
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                />
                                              </svg>
                                            </button>
                                          </p>
                                          <p className="">
                                            {formatDate(
                                              extraDetails?.followup_time,
                                            )}
                                          </p>
                                        </div>
                                        {!isClearReminder && (
                                          <p className="text-sm font-medium text-[#999999]">
                                            {extraDetails?.followup_note}
                                          </p>
                                        )}
                                        {!isClearReminder && (
                                          <button
                                            onClick={() =>
                                              setIsClearReminder(true)
                                            }
                                            className="text-sm w-full text-right font-medium text-blue-700 underline underline-offset-4"
                                          >
                                            Clear Reminder
                                          </button>
                                        )}
                                        {isClearReminder && (
                                          <div className="flex flex-col gap-2.5 items-center w-full">
                                            <p className="">
                                              You will not be notified for a
                                              follow up. Are you sure you want
                                              to remove the reminder on this
                                              lead?
                                            </p>
                                            <div className="flex gap-2.5 items-center w-full">
                                              <button
                                                onClick={() => {
                                                  clearReminder(rowData.id);
                                                }}
                                                className="text-sm w-full font-medium text-blue-700 border border-blue-700 rounded-[4px] py-[8px] px-4"
                                              >
                                                Yes
                                              </button>
                                              <button
                                                onClick={() =>
                                                  setIsClearReminder(false)
                                                }
                                                className="text-sm w-full font-medium border border-blue-700 rounded-[4px] py-[8px] px-4 bg-blue-700 text-white-A700"
                                              >
                                                No
                                              </button>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </PopoverContent>
                                </Popover>
                              </div>
                            </div>
                            <p className="text-sm leading-[21px] text-[#999999] font-normal">
                              {" "}
                              ID-{rowData.id} | {rowData?.createDate} by{" "}
                              {opportunityDetails?.createdBy}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="items-end flex gap-2.5">
                        <div className="flex gap-2.5 items-center">
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M9.835 19.67C15.2667 19.67 19.67 15.2667 19.67 9.835C19.67 4.40328 15.2667 0 9.835 0C4.40328 0 0 4.40328 0 9.835C0 15.2667 4.40328 19.67 9.835 19.67Z"
                              fill="#F1F1F1"
                            />
                            <path
                              d="M9.83656 4.85938C7.80072 4.85938 6.14844 6.51165 6.14844 8.5475C6.14844 10.544 7.7122 12.1668 9.78739 12.2258C9.81689 12.2258 9.85623 12.2258 9.8759 12.2258C9.89557 12.2258 9.92508 12.2258 9.94475 12.2258C9.95458 12.2258 9.96442 12.2258 9.96442 12.2258C11.9511 12.1569 13.5149 10.544 13.5247 8.5475C13.5247 6.51165 11.8724 4.85938 9.83656 4.85938Z"
                              fill="#333333"
                            />
                            <path
                              d="M16.5081 17.0582C14.7575 18.6711 12.4168 19.6645 9.84001 19.6645C7.26324 19.6645 4.92251 18.6711 3.17188 17.0582C3.40791 16.1632 4.04719 15.3469 4.98152 14.7175C7.66647 12.9275 12.0332 12.9275 14.6985 14.7175C15.6427 15.3469 16.2721 16.1632 16.5081 17.0582Z"
                              fill="#333333"
                            />
                          </svg>
                          <p className="font-medium text-sm text-black-label">
                            {opportunityDetails?.assignedTo ??
                              localStorage.getItem("owner_name")}
                          </p>
                        </div>
                        <div className="">
                          {opportunityDetails?.isOpportunityOpen === "open" && (
                            <AlertDialog>
                              <AlertDialogTrigger>
                                <div className=" cursor-pointer text-sm font-medium text-blue-700 ">
                                  Reassign
                                </div>
                              </AlertDialogTrigger>
                              <AlertDialogPortal>
                                <AlertDialogOverlay className="  bg-black-900 bg-opacity-60 animate-overlayShow fixed inset-0" />
                                <AlertDialogContent className="bg-white-A700 z-50 rounded-lg p-0 w-full animate-contentShow">
                                  <AlertDialogHeader className="flex text-left border-b  rounded-t py-3 px-10 ">
                                    <AlertDialogTitle className="font-bold p-0 text-lg text-black-label">
                                      Select the staff to assign
                                    </AlertDialogTitle>
                                    <AlertDialogDescription></AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <form className="px-10 w-full flex py-3 flex-col gap-2">
                                    <label
                                      htmlFor="staff_select"
                                      className=" text-sm font-medium "
                                    >
                                      Choose a staff
                                    </label>
                                    <ReactSelect
                                      menuPlacement="auto"
                                      className=""
                                      menuPosition="absolute"
                                      placeholder="Select the staff"
                                      styles={{
                                        control: (provided, state) => ({
                                          ...provided,
                                          zIndex: 0,
                                          borderRadius: ".5rem",
                                          padding: ".4rem",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          borderColor: state.isFocused
                                            ? "#165FE3"
                                            : "#333333",
                                          boxShadow: "none",
                                        }),
                                        menu: (provided, state) => ({
                                          ...provided,
                                          zIndex: 1000,
                                        }),
                                      }}
                                      options={relationShipManagerOptions}
                                      onChange={relationShipManagerHandleChange}
                                      value={relationShipManagerSelectedOption}
                                      name="staff_select"
                                      id="staff_select"
                                    />
                                  </form>
                                  <AlertDialogFooter className="flex justify-start items-center gap-2.5 py-5 px-10 flex-row rounded-b-lg bg-gray-button-gray-lite">
                                    <AlertDialogCancel
                                      className=" m-0 rounded-lg border-blue-700 border py-[11px] px-4 text-blue-700"
                                      asChild
                                    >
                                      <button>Cancel</button>
                                    </AlertDialogCancel>
                                    <AlertDialogAction
                                      className="rounded-lg border-blue-700 py-[11px] px-4 bg-blue-700 border text-white-A700"
                                      asChild
                                    >
                                      <button
                                        className=""
                                        onClick={() =>
                                          handleUpdateOpportunity(
                                            "",
                                            "",
                                            "",
                                            "",
                                          )
                                        }
                                      >
                                        Submit
                                      </button>
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialogPortal>
                            </AlertDialog>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Summary */}
                    <div className="flex-1 w-full flex flex-col h-full justify-between">
                      <div className="flex justify-end gap-2.5">
                        <div className="absolute top-5 right-7">
                          <Popover
                            onOpenChange={async (val) => {
                              if (val) {
                                await generateSummary();
                              }
                            }}
                          >
                            <PopoverTrigger>
                              <div className="flex items-center cursor-pointer">
                                <button className="text-blue-700 text-base leading-4 font-semibold bg-[#E1EBFC99] px-2 py-1.5 rounded-lg w-[81px] h-[28px] flex items-center justify-center">
                                  Summary
                                </button>
                                <div className="relative bottom-1 ">
                                  <svg
                                    width="14"
                                    height="16"
                                    viewBox="0 0 14 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M10.6102 10.9659L10.6101 10.9655C10.6087 10.9616 10.6067 10.9596 10.6049 10.9583C10.6027 10.9569 10.5997 10.9559 10.5962 10.9559C10.5927 10.9559 10.5896 10.9569 10.5873 10.9584C10.5853 10.9597 10.5833 10.9618 10.5818 10.9659H10.6102ZM10.6102 10.9659L10.8389 11.6002L10.8389 11.6003C10.9669 11.955 11.1714 12.2771 11.4381 12.5437C11.7047 12.8104 12.0268 13.015 12.3815 13.1429L12.3816 13.1429L13.0158 13.3716C13.0158 13.3716 13.0158 13.3716 13.0159 13.3716C13.0187 13.3727 13.0212 13.3746 13.023 13.3771L13.064 13.3485L13.023 13.3771C13.0248 13.3797 13.0257 13.3827 13.0257 13.3858C13.0257 13.389 13.0248 13.392 13.023 13.3946L13.064 13.4232L13.023 13.3946C13.0212 13.3971 13.0187 13.399 13.0159 13.4001C13.0158 13.4001 13.0158 13.4001 13.0158 13.4001L12.3816 13.6287L12.3815 13.6288C12.0268 13.7567 11.7047 13.9613 11.4381 14.2279C11.1714 14.4946 10.9669 14.8167 10.8389 15.1714L10.8389 15.1715L10.6103 15.8057C10.6103 15.8057 10.6102 15.8057 10.6102 15.8057C10.6092 15.8086 10.6072 15.8111 10.6047 15.8129C10.6022 15.8147 10.5991 15.8157 10.596 15.8157C10.5929 15.8157 10.5898 15.8147 10.5873 15.8129C10.5848 15.8111 10.5828 15.8086 10.5818 15.8057C10.5818 15.8057 10.5817 15.8057 10.5817 15.8057L10.3531 15.1715L10.3531 15.1714C10.2251 14.8167 10.0206 14.4946 9.75393 14.2279C9.48729 13.9613 9.16516 13.7567 8.81046 13.6288L8.81045 13.6287L8.17624 13.4001C8.17621 13.4001 8.17617 13.4001 8.17614 13.4001C8.17325 13.399 8.17076 13.3971 8.169 13.3946C8.16721 13.392 8.16625 13.389 8.16625 13.3858C8.16625 13.3827 8.16721 13.3797 8.169 13.3771C8.17076 13.3746 8.17325 13.3727 8.17614 13.3716C8.17617 13.3716 8.17621 13.3716 8.17624 13.3716L8.81045 13.1429L8.81046 13.1429C9.16516 13.015 9.48729 12.8104 9.75393 12.5437C10.0206 12.2771 10.2251 11.955 10.3531 11.6003L10.3531 11.6002L10.5818 10.966L10.6102 10.9659ZM10.6572 10.9489L10.8859 11.5833C11.0114 11.9311 11.212 12.2469 11.4734 12.5084C11.7349 12.7698 12.0507 12.9704 12.3985 13.0959L10.6572 10.9489ZM10.6572 10.9489C10.637 10.8916 10.5555 10.8916 10.5348 10.9489H10.6572ZM4.96995 3.20353L4.97006 3.20383L5.42125 4.45685L5.42126 4.45687C5.67132 5.15071 6.07136 5.78084 6.59285 6.30235C7.11435 6.82387 7.74447 7.22392 8.43829 7.47399L8.4383 7.47399L9.69102 7.92509C9.69107 7.92511 9.69112 7.92513 9.69117 7.92515C9.70623 7.93071 9.71924 7.94075 9.72843 7.95391C9.73765 7.96712 9.7426 7.98285 9.7426 7.99896C9.7426 8.01507 9.73765 8.0308 9.72843 8.04401C9.71923 8.05717 9.70623 8.06721 9.69117 8.07277C9.69112 8.07278 9.69107 8.0728 9.69102 8.07282L8.4383 8.52392L8.43829 8.52393C7.74447 8.774 7.11435 9.17405 6.59285 9.69556C6.07136 10.2171 5.67132 10.8472 5.42126 11.541L5.42125 11.5411L4.97016 12.7938C4.97014 12.7939 4.97013 12.7939 4.97011 12.794C4.96455 12.809 4.95451 12.822 4.94135 12.8312C4.92814 12.8405 4.91241 12.8454 4.8963 12.8454C4.88019 12.8454 4.86447 12.8405 4.85126 12.8312C4.83809 12.822 4.82805 12.809 4.82249 12.794C4.82248 12.7939 4.82246 12.7939 4.82244 12.7938L4.37135 11.5411L4.37135 11.541C4.12128 10.8472 3.72125 10.2171 3.19975 9.69556C2.67825 9.17405 2.04814 8.774 1.35431 8.52393L1.3543 8.52392L0.101586 8.07282C0.101554 8.07281 0.101523 8.0728 0.101491 8.07279C0.0864045 8.06723 0.0733802 8.05719 0.0641733 8.04401C0.0549475 8.0308 0.05 8.01507 0.05 7.99896C0.05 7.98285 0.0549475 7.96712 0.0641733 7.95391C0.0733802 7.94073 0.0864043 7.93068 0.101492 7.92513C0.101523 7.92512 0.101554 7.92511 0.101586 7.92509L1.3543 7.47399L1.35431 7.47399C2.04814 7.22392 2.67825 6.82387 3.19975 6.30235C3.72125 5.78084 4.12128 5.15071 4.37135 4.45687L4.37135 4.45685L4.82254 3.20383L4.82265 3.20353C4.82803 3.18829 4.838 3.1751 4.85119 3.16576C4.86438 3.15643 4.88014 3.15142 4.8963 3.15142C4.91246 3.15142 4.92822 3.15643 4.94141 3.16576C4.95461 3.1751 4.96458 3.18829 4.96995 3.20353ZM10.8389 0.827029L10.8389 0.82704C10.9669 1.18175 11.1714 1.50389 11.4381 1.77053C11.7047 2.03717 12.0268 2.24176 12.3815 2.36973L12.3816 2.36973L13.0158 2.59837C13.0158 2.59838 13.0158 2.59838 13.0158 2.59839C13.0187 2.59946 13.0212 2.60139 13.023 2.60392L13.064 2.57525L13.023 2.60393C13.0248 2.60648 13.0257 2.60952 13.0257 2.61263C13.0257 2.61575 13.0248 2.61879 13.023 2.62134L13.064 2.65001L13.023 2.62134C13.0212 2.62387 13.0187 2.6258 13.0158 2.62687C13.0158 2.62688 13.0158 2.62689 13.0158 2.6269L12.3816 2.85553L12.3815 2.85554C12.0268 2.98351 11.7047 3.18809 11.4381 3.45473C11.1714 3.72137 10.9669 4.04351 10.8389 4.39822L10.8389 4.39824L10.6103 5.03247C10.6103 5.03249 10.6102 5.03252 10.6102 5.03255C10.6092 5.03544 10.6072 5.03794 10.6047 5.03971C10.6022 5.04149 10.5991 5.04245 10.596 5.04245C10.5929 5.04245 10.5898 5.04149 10.5873 5.03971C10.5848 5.03794 10.5828 5.03544 10.5818 5.03255C10.5818 5.03252 10.5817 5.03249 10.5817 5.03247L10.3531 4.39824L10.3531 4.39822C10.2251 4.04351 10.0206 3.72137 9.75393 3.45473C9.48729 3.18809 9.16516 2.98351 8.81046 2.85554L8.81045 2.85553L8.17624 2.6269C8.17621 2.62689 8.17619 2.62688 8.17616 2.62687C8.17327 2.6258 8.17077 2.62387 8.169 2.62134C8.16721 2.61879 8.16625 2.61575 8.16625 2.61263C8.16625 2.60952 8.16721 2.60648 8.169 2.60392C8.17077 2.60139 8.17328 2.59946 8.17618 2.59839C8.1762 2.59838 8.17622 2.59838 8.17624 2.59837L8.81045 2.36973L8.81046 2.36973C9.16516 2.24176 9.48729 2.03717 9.75393 1.77053C10.0206 1.50389 10.2251 1.18175 10.3531 0.82704L10.3531 0.827029L10.5817 0.192798C10.5817 0.192774 10.5818 0.19275 10.5818 0.192726C10.5828 0.189832 10.5848 0.187329 10.5873 0.185556C10.5898 0.18377 10.5929 0.182813 10.596 0.182813C10.5991 0.182813 10.6022 0.183771 10.6047 0.185556L10.6047 0.185558C10.6072 0.187329 10.6092 0.189829 10.6102 0.192725C10.6102 0.192749 10.6103 0.192774 10.6103 0.192798L10.8389 0.827029Z"
                                      fill="#AAC8FF"
                                      stroke="#A2A2A2"
                                      strokeWidth="0.1"
                                    />
                                  </svg>
                                </div>
                              </div>
                            </PopoverTrigger>
                            <PopoverContent
                              alignOffset={10}
                              align="end"
                              className="rounded-lg z-50 gap-2.5 p-4 w-[280px] sm:w-[350px] md:w-[450px] flex flex-col lg:w-[590px]"
                            >
                              <div className="flex items-center justify-between w-full">
                                <p className="font-normal text-xs text-[#999999] leading-[18px]">
                                  Generated on{" "}
                                  {formatDate(Date.now().toString())}
                                </p>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(summary);
                                    toast.success("Copied to clipboard");
                                  }}
                                >
                                  <svg
                                    width="13"
                                    height="16"
                                    viewBox="0 0 13 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M9.93685 4.31075L9.93685 4.31028C9.93352 3.60136 9.35783 3.025 8.65111 3.025H1.67793C0.968395 3.025 0.392187 3.60121 0.392187 4.31075V14.3159C0.392187 15.0254 0.968395 15.6016 1.67793 15.6016H8.65111C9.36065 15.6016 9.93685 15.0254 9.93685 14.3159L9.93685 4.31075ZM8.90437 14.3128C8.90437 14.4542 8.78941 14.5691 8.64803 14.5691H1.67486C1.53349 14.5691 1.41852 14.4542 1.41852 14.3128V4.31075C1.41852 4.16937 1.53349 4.05441 1.67486 4.05441H8.64803C8.78941 4.05441 8.90437 4.16937 8.90437 4.31075V14.3128Z"
                                      fill="#165FE3"
                                      stroke="#165FE3"
                                      strokeWidth="0.2"
                                    />
                                    <path
                                      d="M11.3152 0.4H4.342C3.63246 0.4 3.05625 0.976207 3.05625 1.68575C3.05625 1.97137 3.28533 2.20045 3.57095 2.20045C3.85657 2.20045 4.08566 1.97137 4.08566 1.68575C4.08566 1.54437 4.20062 1.42941 4.342 1.42941H11.3152C11.4565 1.42941 11.5715 1.54437 11.5715 1.68575V11.6909C11.5715 11.8322 11.4565 11.9472 11.3152 11.9472C11.0295 11.9472 10.8005 12.1763 10.8005 12.4619C10.8005 12.7475 11.0295 12.9766 11.3152 12.9766C12.0247 12.9766 12.6009 12.4004 12.6009 11.6909V1.68575C12.6009 0.976207 12.0247 0.4 11.3152 0.4Z"
                                      fill="#165FE3"
                                      stroke="#165FE3"
                                      strokeWidth="0.2"
                                    />
                                  </svg>
                                </button>
                              </div>
                              <p className="font-normal text-sm text-black-label leading-[21px]">
                                {summary || (
                                  <div className="space-y-2">
                                    <Skeleton className="h-4 bg-gray-600 w-full rounded-full" />
                                    <Skeleton className="h-4 bg-gray-600 w-full rounded-full" />
                                    <Skeleton className="h-4 bg-gray-600 w-full rounded-full" />
                                  </div>
                                )}
                              </p>
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>
                    </div>

                    {/* Thums up and down */}
                    
                    <div className="flex place-items-end gap-4 pr-4">
                      {/* Thumbs Down Section */}
                      {
                          <div className="">
                            {opportunityDetails?.isOpportunityOpen === "open" && (<DropdownMenu>
                              <DropdownMenuTrigger >
                                <Button variant="outline" className={`flex items-center border-none gap-2 h-8 w-20 ${stageTags?.find(tag => tag.value === extraDetails?.stage_tag)?.color || 'bg-gray-100'}`}>
                                  {
                                    extraDetails?.stage_tag &&
                                    <p dangerouslySetInnerHTML={{
                                      __html: `${stageTags?.find(tag => tag.value === extraDetails?.stage_tag)?.icon || ''}`
                                    }} className=""></p>
                                  }
                                  {extraDetails?.stage_tag || 'Select Tag'}
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className="w-28 p-0 rounded-lg">
                                {
                                  stageTags?.map((tag) => (
                                    <DropdownMenuItem
                                      key={tag.value}
                                      onClick={() => handleUpdateStage(tag.value)}
                                      className={`${tag.color} cursor-pointer`}
                                    >
                                      <p dangerouslySetInnerHTML={{
                                        __html: `${tag.icon || ''}`
                                      }} className="mr-2 p-2"></p>
                                      {tag.label}
                                    </DropdownMenuItem>
                                  ))
                                }
                              </DropdownMenuContent>
                            </DropdownMenu>)}                    
                        </div>
                      }
                      <div className="flex gap-3 h-fit">
                        {opportunityDetails?.isOpportunityOpen === "open" &&
                        opportunityDetails?.reasonToClose !== "buyLater" ? (
                          <DropdownMenu modal>
                            <DropdownMenuTrigger
                              disabled={
                                opportunityDetails?.isOpportunityOpen ===
                                "close"
                              }
                            >
                              <div className="flex items-center h-fit">
                                <div className="p-2 bg-red-50 rounded-lg w-20 h-[34px] flex items-center justify-center sm:w-[90px] sm:h-10">
                                  <ThumbsDown
                                    className="text-red-500 w-6 h-6 sm:w-7 sm:h-7"
                                    size={28}
                                  />
                                </div>
                              </div>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              className="border p-0 rounded-lg border-gray-300"
                              avoidCollisions
                              align="end"
                              alignOffset={10}
                            >
                              <DropdownMenuRadioGroup
                                value={reason}
                                onValueChange={(e) => {
                                  setReason(e);
                                  handleUpdateOpportunity(e, "", "", "");
                                }}
                              >
                                <DropdownMenuLabel
                                  className="bg-gray-100 rounded-t py-3 px-4 text-base font-medium border-b"
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                  }}
                                >
                                  Mark unsuccessfull
                                </DropdownMenuLabel>
                                {opportunityCloseReason.map((reason) => (
                                  <DropdownMenuRadioItem
                                    key={reason.value}
                                    value={reason.value}
                                    className="py-3 px-4 cursor-pointer hover:bg-gray-200 bg-white text-sm font-normal"
                                  >
                                    {reason.label}
                                  </DropdownMenuRadioItem>
                                ))}
                              </DropdownMenuRadioGroup>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : opportunityDetails?.reasonToClose === "buyLater" ? (
                          <DropdownMenu modal>
                            <DropdownMenuTrigger>
                              <div className="flex gap-3  h-fit">
                                <div
                                  style={{
                                    backgroundColor: "#FF8B5C21",
                                    color: "#FF8B5C",
                                    border: "1px solid #F5F5F5",
                                  }}
                                  className="flex items-center px-8 gap-3 rounded-md h-8 w-40"
                                >
                                  <button className="text-sm capitalize  items-end font-medium">
                                    Buy Later
                                  </button>
                                  <svg
                                    className="bg-transparent"
                                    width="15"
                                    height="7"
                                    viewBox="0 0 17 10"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <g clipPath="url(#clip0_1689_36934)">
                                      <path
                                        d="M8.5026 7.38987L2.68796 1.51108C2.6411 1.46268 2.58547 1.42441 2.52428 1.3985C2.46309 1.37258 2.39757 1.35953 2.3315 1.3601C2.26543 1.36067 2.20012 1.37485 2.13936 1.40182C2.07859 1.42879 2.02358 1.46801 1.9775 1.51721C1.93142 1.56641 1.89519 1.62462 1.87091 1.68847C1.84663 1.75232 1.83477 1.82054 1.83603 1.88917C1.83729 1.95781 1.85163 2.02551 1.87824 2.08835C1.90484 2.15119 1.94318 2.20793 1.99103 2.25527L8.15414 8.48575C8.24743 8.58005 8.37245 8.63281 8.5026 8.63281C8.63276 8.63281 8.75778 8.58005 8.85107 8.48575L15.0142 2.25527C15.062 2.20793 15.1004 2.15119 15.127 2.08835C15.1536 2.02551 15.1679 1.95781 15.1692 1.88917C15.1704 1.82054 15.1586 1.75232 15.1343 1.68847C15.11 1.62462 15.0738 1.56641 15.0277 1.51721C14.9816 1.46801 14.9266 1.42879 14.8659 1.40182C14.8051 1.37485 14.7398 1.36067 14.6737 1.3601C14.6076 1.35953 14.5421 1.37258 14.4809 1.3985C14.4197 1.42441 14.3641 1.46268 14.3173 1.51108L8.5026 7.38987Z"
                                        fill="#FF8B5C"
                                      />
                                    </g>
                                  </svg>
                                </div>
                              </div>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              className="border p-0 rounded-lg border-gray-300"
                              avoidCollisions
                              align="end"
                              alignOffset={10}
                            >
                              <DropdownMenuRadioGroup
                                value={reason}
                                onValueChange={(e) => {
                                  setReason(e);
                                  handleUpdateOpportunity(e, "", "", "");
                                }}
                              >
                                <DropdownMenuLabel
                                  className="bg-gray-100 rounded-t py-3 px-4 text-base font-medium border-b"
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                  }}
                                >
                                  Mark unsuccessfull
                                </DropdownMenuLabel>
                                {opportunityCloseReason.map((reason) => (
                                  <DropdownMenuRadioItem
                                    key={reason.value}
                                    value={reason.value}
                                    className="py-3 px-4 cursor-pointer hover:bg-gray-200 bg-white text-sm font-normal"
                                  >
                                    {reason.label}
                                  </DropdownMenuRadioItem>
                                ))}
                              </DropdownMenuRadioGroup>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : (
                          opp_status()
                        )}
                      </div>                     

                      {/* Thumbs Up Section */}
                      {opportunityDetails?.isOpportunityOpen === "open" &&
                        (!reason || reason === "buyLater") && (
                          <div className="flex items-center h-fit">
                            {opportunityDetails?.productSubcategory !==
                              "Remittance" ? (
                                ((opportunityDetails?.productSubcategory === 'Forex') || opportunityDetails?.ProductDetailsList?.some((item:any) => [9,16].includes(item?.product_id))) ?
                                <Link
                                  state={{
                                    opportunityId: contactDetails?.oppId,
                                    contactId: contactDetails?.customerId,
                                    name: contactDetails?.contactPerson,
                                    number: contactDetails?.contactNumber,
                                    email: contactDetails?.contactEmail,
                                  }}
                                  to={`/neworder?type=${'forex'}`}
                                >
                                  <button className="p-2 bg-green-50 rounded-lg w-20 h-[34px] flex items-center justify-center">
                                    <ThumbsUp
                                      className="text-green-500 w-7 h-7"
                                      size={24}
                                    />
                                  </button>
                                </Link> :
                                <FormProvider {...methods}>
                                  <OrderDetailForm
                                    onSubmit={createOrder}
                                    invoiceDetails={opportunityDetails}
                                  />
                                </FormProvider>
                            ) : (
                              <Link
                                state={{
                                  contactId: contactDetails?.contactId,
                                  opportunityId: contactDetails?.oppId,
                                  partnerId:
                                    Number(localStorage.getItem("staff_id")) ===
                                    contactDetails?.partnerId
                                      ? 0
                                      : contactDetails?.partnerId,
                                  isLoan: remittanceProductListData?.took_loan ? "true" : "false",
                                  mode: remittanceProductListData?.Mode,
                                  purpose_id: purposeIDDetails?.id,
                                  amount: opportunityDetails?.amount,
                                  country: opportunityDetails?.country,
                                }}
                                to="/createnewdeal"                                
                              >
                                <button className="p-2 bg-green-50 rounded-lg w-20 h-[34px] flex items-center justify-center sm:w-[90px] sm:h-10">
                                  <ThumbsUp
                                    className="text-green-500 w-6 h-6 sm:w-7 sm:h-7"
                                    size={24}
                                  />
                                </button>
                              </Link>
                            )}
                          </div>
                        )}
                    </div>
                  </div>
                </div>
                {/* Tabs */}
                <div className="flex h-full overflow-auto bg-white-A700 p-2.5 rounded-lg">
                  <Tabs
                    value={activeTab}
                    onValueChange={(value) => {
                      setActiveTab(value);
                    }}
                    className="w-full sm:overflow-hidden"
                  >
                    <TabsList className="hide-scrollbar flex justify-between border-b border-gray-200 bg-transparent">
                      {sheetTabs.map((tab) => {
                        if (
                          tab.value === "customers" &&
                          !rowData?.corporate_details?.travellers_list?.length
                        )
                          return null;
                        return (
                          <TabsTrigger
                            key={tab.value}
                            value={tab.value}
                            className={`relative px-8 py-3 text-base transition-colors max-w-[220px]
                                ${
                                  tab.value === activeTab
                                    ? "text-blue-600 bg-blue-50 border-b border-white"
                                    : "text-gray-500"
                                }
                                ${
                                  tab.value === activeTab
                                    ? "before:absolute before:top-0 before:left-0 before:w-full before:h-0.5 before:bg-blue-600"
                                    : ""
                                }
                                font-inter text-[18px] font-[500] leading-[16px] text-center`}
                          >
                            <span className="relative flex items-center">
                              {tab.label}
                              {tab?.label === "Files" &&
                                opportunityDetails?.documents?.length > 0 && (
                                  <span className="absolute right-[-8px] top-[-4px] h-2 w-2 rounded-full bg-red-500"></span>
                                )}
                              {tab?.label === "Customers" && (
                                <span className="w-4 h-4 rounded-full bg-[#F1F1F1] font-semibold text-xs ml-2.5">
                                  {
                                    rowData?.corporate_details?.travellers_list
                                      ?.length
                                  }
                                </span>
                              )}
                              {tab?.label === "Notes" &&
                                (rowData?.productSubCategory === "Forex" ||
                                  rowData?.productSubCategory === "Visa") &&
                                forexLeadDetails && (
                                  <span className="absolute right-[-8px] top-[-4px] h-2 w-2 rounded-full bg-yellow-500"></span>
                                )}
                            </span>
                          </TabsTrigger>
                        );
                      })}
                    </TabsList>
                    <TabsContent value="interaction">
                      <div className="flex gap-0 overflow-auto h-full flex-grow flex-col ">
                        <DataTable
                          columns={InteractionColumn}
                          data={interactionList}
                          isToolbarVisible={false}
                          isPaginationVisible={false}
                          defaultSorting={[
                            { id: "interactionDateandTime", desc: true },
                          ]}
                        />
                      </div>
                    </TabsContent>
                    <TabsContent value="customers">
                      <div className="flex gap-0 overflow-auto h-full flex-grow flex-col ">
                        <DataTable
                          columns={[
                            {
                              accessorKey: "customerName",
                              header: "Name",
                            },
                            {
                              accessorKey: "customerEmail",
                              header: "Email",
                            },
                            {
                              accessorKey: "customerPhone",
                              header: "Phone",
                            },
                          ]}
                          data={
                            rowData?.corporate_details?.travellers_list?.map(
                              (data) => {
                                return {
                                  customerName: data?.first_name
                                    ? data?.first_name + " " + data?.last_name
                                    : "N/A",
                                  customerEmail: data?.email || "N/A",
                                  customerPhone: data?.phone || "N/A",
                                };
                              },
                            ) || []
                          }
                          isToolbarVisible={false}
                          isPaginationVisible={false}
                          defaultSorting={[{ id: "customerName", desc: true }]}
                        />
                      </div>
                    </TabsContent>
                    <TabsContent value="details">
                      <>
                      <div className="grid grid-cols-2 pl gap-8 w-full pt-4">
                        <div className="flex flex-col gap-2.5 w-full">
                          <h5 className="text-sm font-medium leading-4 text-[#999999]">
                            Contact Details
                          </h5>
                          <div className="flex flex-col gap-4 p-2.5 rounded-xl bg-[#F9F9F9] ">
                            {contactInsheet?.map((item) => {
                              if (item.value === "location") {
                                return (
                                  <TooltipProvider key={item.value}>
                                    <Tooltip>
                                      <TooltipTrigger
                                        className="flex cursor-pointer items-end"
                                        asChild
                                      >
                                        <div className="flex items-center gap-2.5">
                                          {item.icon}
                                          <div className="font-medium text-sm text-ellipsis text-black-label">
                                            {contactDetails?.[
                                              item.value as keyof typeof contactDetails
                                            ]?.substring(0, 30) ||
                                              "Not Available"}
                                          </div>
                                          {contactDetails?.location && (
                                            <button
                                              onClick={() => {
                                                navigator.clipboard.writeText(
                                                  (contactDetails?.[
                                                    item.value as keyof typeof contactDetails
                                                  ] as string) || "",
                                                );
                                                toast.success(
                                                  "Copied to clipboard",
                                                );
                                              }}
                                              className=""
                                            >
                                              <svg
                                                width="13"
                                                height="16"
                                                viewBox="0 0 13 16"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                              >
                                                <path
                                                  d="M9.93685 4.31075L9.93685 4.31028C9.93352 3.60136 9.35783 3.025 8.65111 3.025H1.67793C0.968395 3.025 0.392187 3.60121 0.392187 4.31075V14.3159C0.392187 15.0254 0.968395 15.6016 1.67793 15.6016H8.65111C9.36065 15.6016 9.93685 15.0254 9.93685 14.3159L9.93685 4.31075ZM8.90437 14.3128C8.90437 14.4542 8.78941 14.5691 8.64803 14.5691H1.67486C1.53349 14.5691 1.41852 14.4542 1.41852 14.3128V4.31075C1.41852 4.16937 1.53349 4.05441 1.67486 4.05441H8.64803C8.78941 4.05441 8.90437 4.16937 8.90437 4.31075V14.3128Z"
                                                  fill="#165FE3"
                                                  stroke="#165FE3"
                                                  strokeWidth="0.2"
                                                />
                                                <path
                                                  d="M11.3152 0.4H4.342C3.63246 0.4 3.05625 0.976207 3.05625 1.68575C3.05625 1.97137 3.28533 2.20045 3.57095 2.20045C3.85657 2.20045 4.08566 1.97137 4.08566 1.68575C4.08566 1.54437 4.20062 1.42941 4.342 1.42941H11.3152C11.4565 1.42941 11.5715 1.54437 11.5715 1.68575V11.6909C11.5715 11.8322 11.4565 11.9472 11.3152 11.9472C11.0295 11.9472 10.8005 12.1763 10.8005 12.4619C10.8005 12.7475 11.0295 12.9766 11.3152 12.9766C12.0247 12.9766 12.6009 12.4004 12.6009 11.6909V1.68575C12.6009 0.976207 12.0247 0.4 11.3152 0.4Z"
                                                  fill="#165FE3"
                                                  stroke="#165FE3"
                                                  strokeWidth="0.2"
                                                />
                                              </svg>
                                            </button>
                                          )}
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent
                                        side="bottom"
                                        className="bg-black-900 text-white-A700 max-w-[200px] whitespace-normal break-words border-0"
                                      >
                                        {contactDetails?.[
                                          item.value as keyof typeof contactDetails
                                        ] || "Not Available"}
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                );
                              }
                              return (
                                <div
                                  key={item.value}
                                  className="flex items-center gap-2.5"
                                >
                                  {item.icon}
                                  <span className="font-medium text-sm text-black-label">
                                    {contactDetails?.[
                                      item.value as keyof typeof contactDetails
                                    ] || "Not Available"}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        <div className="flex flex-col gap-2.5 w-full flex-grow h-full">
                          <h5 className="text-sm font-medium leading-4 text-[#999999]">
                            Lead Details
                          </h5>
                          <div className="flex flex-col gap-4 p-2.5 rounded-xl bg-[#F9F9F9] w-full">
                            <div className="flex gap-2.5 items-center">
                              <span className="text-[#666666] font-medium text-sm ">
                                Product
                              </span>
                              <span className="font-medium text-sm text-black-label">
                                {rowData.product}
                              </span>
                              <span className="text-[#999999] font-medium text-sm">
                                {`(${opportunityDetails?.productSubcategory})`}
                              </span>
                            </div>
                            <div className="flex gap-2.5 items-center">
                              <span className="text-[#666666] font-medium text-sm ">
                                Channel
                              </span>
                              <span className="font-medium text-sm text-black-label">
                                {channelDetails?.channel === "Refferal"
                                  ? "Referral"
                                  : channelDetails?.channel}
                              </span>
                              <span className="text-[#999999] font-medium text-sm">
                                {channelDetails?.source
                                  ? ["outletID", "partnerOffice"].includes(
                                      channelDetails?.source,
                                    )
                                    ? channelDetails?.channel === "Refferal"
                                      ? extraDetails?.staffId
                                        ? `(${extraDetails?.staffName})`
                                        : `(${extraDetails?.partnerName})`
                                      : channelDetails?.channel === "Walk-in"
                                        ? `(${extraDetails?.branchName})`
                                        : ``
                                    : `(${channelDetails?.source})`
                                  : ""}
                              </span>
                            </div>
                          </div>
                        </div>
                        {opportunityDetails?.amount &&
                          opportunityDetails?.country ? (
                            <div className="flex flex-col gap-2.5 w-full flex-grow h-full">
                              <h5 className="text-sm font-medium leading-4 text-[#999999]">
                                Other details
                              </h5>
                              <div className="flex flex-col gap-4 p-2.5 rounded-xl bg-[#F9F9F9] w-full">
                                <div className="flex gap-2.5 items-center">
                                  <span className="text-[#666666] font-medium text-sm ">
                                    Amount
                                  </span>
                                  <span className="font-medium text-sm text-black-label">
                                    {opportunityDetails?.amount}
                                  </span>
                                </div>
                                <div className="flex gap-2.5 items-center">
                                  <span className="text-[#666666] font-medium text-sm ">
                                    Country
                                  </span>
                                  <span className="font-medium text-sm text-black-label">
                                    {opportunityDetails?.country}
                                  </span>
                                </div>
                               {pancardDetails?.pan_card && (
                                <div className="flex gap-2.5 items-center">
                                  <span className="text-[#666666] font-medium text-sm">
                                    PanCard
                                  </span>
                                  <span className="font-medium text-sm text-black-label">
                                    {pancardDetails.pan_card}
                                  </span>
                                </div>
                              )}
                              </div>
                            </div>
                          ) : (
                            pancardDetails?.pan_card && (
                               <div className="flex flex-col gap-2.5 w-full flex-grow h-full">
                              <h5 className="text-sm font-medium leading-4 text-[#999999]">
                                Other details
                              </h5>
                              <div className="flex flex-col gap-4 p-2.5 rounded-xl bg-[#F9F9F9] w-full">
                               {pancardDetails?.pan_card && (
                                <div className="flex gap-2.5 items-center">
                                  <span className="text-[#666666] font-medium text-sm">
                                    PanCard
                                  </span>
                                  <span className="font-medium text-sm text-black-label">
                                    {pancardDetails.pan_card}
                                  </span>
                                </div>
                              )}
                              </div>
                            </div>
                            )
                          )}
                      </div>
                      <div className="flex gap-0 overflow-auto h-full flex-grow flex-col mt-2">                     
                          {(opportunityDetails?.productSubcategory === "Remittance" && opportunityDetails?.product === "Education payment") ? (
                            Object.keys(remittanceProductListData).length === 0 ? (
                              <p>No data available</p>
                            ) : (
                              <DataTable
                                columns={remittanceProductListcolumns(getOpportunity)}
                                data={[remittanceProductListData]}
                                isToolbarVisible={false}
                                isPaginationVisible={false}
                                defaultSorting={[
                                  { id: "interactionDateandTime", desc: true },
                                ]}
                              />
                            )
                          ) : null}
                      </div>
                      <div className="flex gap-0 overflow-auto h-full flex-grow flex-col mt-2">                     
                        {
                          opportunityDetails?.ProductDetailsList?.length > 0 && (
                            <div>
                              <DataTable
                                columns={ForexProductListcolumns(getOpportunity)}
                                data={opportunityDetails?.ProductDetailsList}
                                isToolbarVisible={false}
                                isPaginationVisible={false}
                                defaultSorting={[
                                  { id: "interactionDateandTime", desc: true },
                                ]}
                              />
                            </div>
                          )}
                      </div>
                    </>
                    </TabsContent>
                    <TabsContent className="" value="files">
                      <div className="space-y-4 flex gap-0 overflow-auto h-full flex-grow flex-col">
                        {/* Personal Documents Section */}
                        {opportunityDetails?.generic_order
                          ?.generic_order_details?.personal_documents?.length >
                          0 && (
                          <div className="bg-white border-[#fff] rounded-[12px] p-3">
                            <h4 className="text-sm font-semibold mb-4 text-[#999999]">
                              Personal Documents
                            </h4>
                            <div className="flex flex-col flex-wrap gap-1.5 items-start">
                              {opportunityDetails?.generic_order?.generic_order_details?.personal_documents?.map(
                                (docItem: any, idx) => {
                                  const { doc_list_id, doc } = docItem || {};
                                  const verified =
                                    doc?.meta?.validated || false;

                                  return (
                                    <div
                                      key={doc_list_id ?? idx}
                                      className={`flex justify-between items-center py-2.5 px-4 w-full ${
                                        idx % 2 === 0
                                          ? "bg-[#FAFAFA]"
                                          : "bg-white"
                                      } rounded-[8px]`}
                                    >
                                      <div className="flex items-center gap-2 cursor-pointer">
                                        <span
                                          className={`text-[16px] ${
                                            verified
                                              ? "text-[#165FE3] font-semibold"
                                              : "text-[#2D3648] font-medium"
                                          }`}
                                        >
                                          {doc?.name || "Document"}
                                        </span>
                                      </div>

                                      {/* View and Download buttons */}
                                      <div className="flex gap-4">
                                        {/* View Button */}
                                        <button
                                          onClick={() =>
                                            window.open(doc?.url, "_blank")
                                          }
                                          className="flex items-center gap-1 text-sm text-[#165FE3] hover:underline"
                                        >
                                          <Eye className="w-5 h-5 text-[#165FE3]" />
                                          <span className="font-semibold text-[14px]">
                                            View
                                          </span>
                                        </button>

                                        {/* Download Button */}
                                        <a
                                          onClick={(e) => {
                                            e.preventDefault();
                                            handleDownloadInvoice({
                                              url: doc?.url,
                                              name: doc?.name,
                                            });
                                          }}
                                          className="flex items-center gap-1 text-blue-600 text-sm leading-[140%] font-semibold"
                                        >
                                          <svg
                                            width="20"
                                            height="20"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="lucide lucide-download"
                                          >
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                            <polyline points="7 10 12 15 17 10" />
                                            <line
                                              x1="12"
                                              x2="12"
                                              y1="15"
                                              y2="3"
                                            />
                                          </svg>
                                          Download
                                        </a>
                                      </div>
                                    </div>
                                  );
                                },
                              )}
                            </div>
                          </div>
                        )}
                        {/* Travel Documents Section */}
                        {opportunityDetails?.generic_order
                          ?.generic_order_details?.travel_documents?.length >
                          0 && (
                          <div className="bg-white border-[#fff] rounded-[12px] p-3 mt-6">
                            <h4 className="text-sm font-semibold mb-4 text-[#999999]">
                              Travel Documents
                            </h4>
                            <div className="flex flex-col flex-wrap gap-1.5 items-start">
                              {opportunityDetails?.generic_order?.generic_order_details?.travel_documents?.map(
                                (docItem: any, idx: any) => {
                                  const { doc_list_id, doc } = docItem || {};
                                  const verified =
                                    doc?.meta?.validated || false;

                                  return (
                                    <div
                                      key={doc_list_id ?? idx}
                                      className={`flex justify-between items-center py-2.5 px-4 w-full ${
                                        idx % 2 === 0
                                          ? "bg-[#FAFAFA]"
                                          : "bg-white"
                                      } rounded-[8px]`}
                                    >
                                      {/* Document name and verification */}
                                      <div className="flex items-center gap-2 cursor-pointer">
                                        <span
                                          className={`text-[16px] ${
                                            verified
                                              ? "text-[#165FE3] font-semibold"
                                              : "text-[#2D3648] font-medium"
                                          }`}
                                        >
                                          {doc?.name || "Document"}
                                        </span>
                                      </div>

                                      {/* View and Download buttons */}
                                      <div className="flex gap-4">
                                        {/* View Button */}
                                        <button
                                          onClick={() =>
                                            window.open(doc?.url, "_blank")
                                          }
                                          className="flex items-center gap-1 text-sm text-[#165FE3] hover:underline"
                                        >
                                          <Eye className="w-5 h-5 text-[#165FE3]" />
                                          <span className="font-semibold text-[14px]">
                                            View
                                          </span>
                                        </button>

                                        {/* Download Button */}
                                        <a
                                          onClick={(e) => {
                                            e.preventDefault();
                                            handleDownloadInvoice({
                                              url: doc?.url,
                                              name: doc?.name,
                                            });
                                          }}
                                          className="flex items-center gap-1 text-blue-600 text-sm leading-[140%] font-semibold"
                                        >
                                          <svg
                                            width="20"
                                            height="20"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="lucide lucide-download"
                                          >
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                            <polyline points="7 10 12 15 17 10" />
                                            <line
                                              x1="12"
                                              x2="12"
                                              y1="15"
                                              y2="3"
                                            />
                                          </svg>
                                          Download
                                        </a>
                                      </div>
                                    </div>
                                  );
                                },
                              )}
                            </div>
                          </div>
                        )}

                        {opportunityDetails?.documents?.map((doc: any) => (
                          <div
                            key={doc?.doc_list_id}
                            className="flex items-center justify-between rounded-md py-2.5 px-5 odd:bg-[#FAFAFA] border-gray-100"
                          >
                            <div>
                              <h2 className="font-medium text-base leading-[150%] text-gray-800">
                                {doc?.filename}
                              </h2>
                              <div className="flex items-center mt-1 text-gray-500">
                                <span>{doc.author}</span>
                                {/* <span className="mx-2">|</span> */}
                                {/* <span>{doc.date}</span> */}
                              </div>
                            </div>

                            <div className="flex items-center gap-4">
                              {doc.canReupload && (
                                <button
                                  onClick={() => {}}
                                  // disabled={uploading === doc.id}
                                  className="flex items-center gap-2 text-blue-600 font-medium"
                                >
                                  <RotateCw
                                    className={`h-5 w-5 ${
                                      false ? "animate-spin" : ""
                                    }`}
                                  />
                                  Re-upload
                                </button>
                              )}

                              <button
                                onClick={() => {
                                  window.open(doc.file.url, "_blank");
                                }}
                                className="flex items-center gap-2 text-blue-600 text-sm leading-[140%] font-semibold"
                              >
                                <svg
                                  width="13"
                                  height="16"
                                  viewBox="0 0 13 16"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M0.460938 3.45946C0.460938 1.54885 2.00979 0 3.9204 0H8.72437C8.96956 0 9.20325 0.10406 9.36723 0.286305L12.7795 4.07764C12.9224 4.23645 13.0015 4.44254 13.0015 4.65621V12.5405C13.0015 14.4511 11.4526 16 9.54202 16H3.9204C2.00979 16 0.460938 14.4511 0.460938 12.5405V3.45946Z"
                                    fill="#165FE3"
                                  />
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M3.25886 9.60595C3.51977 9.36041 3.93029 9.37295 4.17577 9.63388L6.29793 11.8895L8.42013 9.63388C8.66558 9.37295 9.07605 9.36041 9.33697 9.60595C9.5979 9.8514 9.61045 10.2619 9.36491 10.5228L7.03246 13.002C6.63159 13.4281 5.96426 13.4281 5.5634 13.002L3.23091 10.5228C2.98543 10.2619 2.99795 9.8514 3.25886 9.60595Z"
                                    fill="white"
                                  />
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M6.29709 5.54297C6.65531 5.54297 6.94573 5.83338 6.94573 6.19162V12.6781C6.94573 13.0363 6.65531 13.3267 6.29709 13.3267C5.93886 13.3267 5.64844 13.0363 5.64844 12.6781V6.19162C5.64844 5.83338 5.93886 5.54297 6.29709 5.54297Z"
                                    fill="white"
                                  />
                                  <path
                                    d="M13.0017 5.18919V4.65621C13.0017 4.44254 12.9226 4.23645 12.7797 4.07764L9.36744 0.286305C9.20346 0.10406 8.96977 0 8.72459 0H7.8125V1.72973C7.8125 3.64034 9.36139 5.18919 11.272 5.18919H13.0017Z"
                                    fill="#98BDFF"
                                  />
                                </svg>
                                Download
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </TabsContent>
                    <TabsContent value="notes">
                      {!detailNote ? (
                        <div className="flex items-center bg-[#FAFAFA] gap-5 w-full">
                          <div className=" bg-[#FFFFFF] w-full flex items-center gap-2 py-2.5 px-2 rounded-lg text-left">
                            <div className="flex items-center justify-between w-full">
                              <div className="flex flex-col gap-1">
                                {" "}
                                <p className="font-normal text-xs leading-[18px] text-[#999999]">
                                  {extraDetails?.note_last_edited ? (
                                    <>
                                      last edited on{" "}
                                      {formatUnixTimestamp(
                                        extraDetails.note_last_edited,
                                      )}
                                    </>
                                  ) : (
                                    "Click on icon to add note"
                                  )}
                                </p>
                                <p className="font-normal text-xs leading-[18px] text-[#999999] whitespace-pre-wrap">
                                  {extraDetails?.note}
                                </p>
                              </div>

                              <button onClick={openDetailNote} className="">
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M3.33594 4.24609H2.5026C2.06058 4.24609 1.63665 4.42169 1.32409 4.73425C1.01153 5.04681 0.835938 5.47073 0.835938 5.91276V13.4128C0.835938 13.8548 1.01153 14.2787 1.32409 14.5913C1.63665 14.9038 2.06058 15.0794 2.5026 15.0794H10.0026C10.4446 15.0794 10.8686 14.9038 11.1811 14.5913C11.4937 14.2787 11.6693 13.8548 11.6693 13.4128V12.5794"
                                    stroke="#165FE3"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M10.8333 2.58472L13.3333 5.08472M14.4875 3.90556C14.8157 3.57735 15.0001 3.13221 15.0001 2.66806C15.0001 2.20391 14.8157 1.75876 14.4875 1.43056C14.1593 1.10235 13.7142 0.917969 13.25 0.917969C12.7858 0.917969 12.3407 1.10235 12.0125 1.43056L5 8.41806V10.9181H7.5L14.4875 3.90556Z"
                                    stroke="#165FE3"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col items-start justify-between w-full">
                        <textarea
                          value={inputValue}
                          onChange={(e) => {
                            setInputValue(e.target.value);
                          }}
                          rows={3}
                          className="w-full border rounded-md border-black border-solid px-2 py-2"
                        />
                        <div className="flex flex-row gap-4 mt-1">
                          <button
                            onClick={() => {
                              handleUpdateNote(opportunityDetails?.id, inputValue);
                              setDetailNote(false);
                            }}
                            className="text-white-A700 bg-blue-700 whitespace-nowrap rounded-lg py-2 px-4"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setDetailNote(false);
                            }}
                            className="text-blue-700 bg-white border border-blue-700 whitespace-nowrap rounded-lg py-2 px-4"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                      )}
                      {forexLeadDetails &&
                        rowData?.productSubCategory === "Forex" && (
                          <div className="mt-4 flex items-center gap-2.5 text-[14px] font-normal text-[#999999]">
                            <img
                              src="/images/remit/lightInfo.svg"
                              alt="info"
                              className="w-4 h-4 object-fill"
                            />
                            <span>
                              Please check your email for more details.
                            </span>
                          </div>
                        )}
                      {forexLeadDetails &&
                        rowData?.productSubCategory === "Forex" &&
                        Object.keys(forexLeadDetails)?.length > 0 && (
                          <div className="rounded-md py-4 w-full shadow-sm">
                            <div className="flex justify-between items-center mb-4 bg-[#F5F9FF] rounded-xl p-4">
                              <div className="flex items-center gap-2 text-sm text-gray-700 font-bold">
                                <span>
                                  {opportunityDetails?.generic_order?._product
                                    ?.type === "Card"
                                    ? "Forex Card"
                                    : "Forex currency notes"}
                                </span>
                                <span className="w-1 h-1 rounded-full"></span>
                                <span>
                                  {forexLeadDetails?.buy_sell
                                    ?.charAt(0)
                                    .toUpperCase() +
                                    forexLeadDetails?.buy_sell
                                      ?.slice(1)
                                      .toLowerCase()}
                                </span>
                                <span className="w-1 h-1 rounded-full"></span>
                                <span>{forexLeadDetails?.city || "N/A"}</span>
                              </div>
                              <div className="text-sm font-semibold text-gray-800">
                                {forexLeadDetails?.buy_sell === "buy"
                                  ? forexLeadDetails?.currency_want
                                  : forexLeadDetails?.currency_have ||
                                    "N/A"}{" "}
                                {/* {Math.ceil(
                                  (opportunityDetails?.generic_order
                                    ?.grand_total || 0) * 10000,
                                ) / 10000} */}
                                {forexLeadDetails?.fx_amount || "N/A"}
                              </div>
                            </div>

                            <div className="space-y-2 text-sm text-[#999999] px-4">
                              <div className="flex justify-between">
                                <span>Customer rate</span>
                                <span className="text-gray-800 font-medium">
                                  INR {forexLeadDetails?.rate || 0}
                                </span>
                              </div>
                              {/* <div className="flex justify-between">
                                <span>FCY amount</span>
                                <span className="text-gray-800 font-medium">
                                  {forexLeadDetails?.buy_sell === "buy"
                                    ? forexLeadDetails?.currency_want
                                    : forexLeadDetails?.currency_have ||
                                      "N/A"}{" "}
                                  {forexLeadDetails?.fx_amount || 0}
                                </span>
                              </div> */}
                              <div className="flex justify-between">
                                <span>Coupon code</span>
                                <span className="text-gray-800 font-medium">
                                  {forexLeadDetails?.discount_coupon_id?.[0]?.coupon_name || "N/A"}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      {rowData?.corporate_details?.Destination && (
                        <div className="flex justify-between items-center bg-[#F5F9FF] py-1 px-2">
                          <h4 className="text-black-label font-medium leading-[150%] -tracking-[4%] flex items-center gap-2.5">
                            Visa{" "}
                            <div class="w-[7px] h-[7px] bg-[#C4D7F9] rounded-full"></div>
                            {rowData?.corporate_details?.Destination}
                          </h4>
                          <div className=" flex items-center gap-1">
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 18 18"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M6 4.3125C5.6925 4.3125 5.4375 4.0575 5.4375 3.75V1.5C5.4375 1.1925 5.6925 0.9375 6 0.9375C6.3075 0.9375 6.5625 1.1925 6.5625 1.5V3.75C6.5625 4.0575 6.3075 4.3125 6 4.3125Z"
                                fill="#333333"
                              />
                              <path
                                d="M12 4.3125C11.6925 4.3125 11.4375 4.0575 11.4375 3.75V1.5C11.4375 1.1925 11.6925 0.9375 12 0.9375C12.3075 0.9375 12.5625 1.1925 12.5625 1.5V3.75C12.5625 4.0575 12.3075 4.3125 12 4.3125Z"
                                fill="#333333"
                              />
                              <path
                                d="M15.375 7.38086H2.625C2.3175 7.38086 2.0625 7.12586 2.0625 6.81836C2.0625 6.51086 2.3175 6.25586 2.625 6.25586H15.375C15.6825 6.25586 15.9375 6.51086 15.9375 6.81836C15.9375 7.12586 15.6825 7.38086 15.375 7.38086Z"
                                fill="#333333"
                              />
                              <path
                                d="M12 17.0625H6C3.2625 17.0625 1.6875 15.4875 1.6875 12.75V6.375C1.6875 3.6375 3.2625 2.0625 6 2.0625H12C14.7375 2.0625 16.3125 3.6375 16.3125 6.375V12.75C16.3125 15.4875 14.7375 17.0625 12 17.0625ZM6 3.1875C3.855 3.1875 2.8125 4.23 2.8125 6.375V12.75C2.8125 14.895 3.855 15.9375 6 15.9375H12C14.145 15.9375 15.1875 14.895 15.1875 12.75V6.375C15.1875 4.23 14.145 3.1875 12 3.1875H6Z"
                                fill="#333333"
                              />
                              <path
                                d="M6.375 10.875C6.2775 10.875 6.18 10.8525 6.09 10.815C6 10.7775 5.91751 10.725 5.84251 10.6575C5.77501 10.5825 5.72249 10.5 5.68499 10.41C5.64749 10.32 5.625 10.2225 5.625 10.125C5.625 9.93001 5.70751 9.73502 5.84251 9.59252C5.91751 9.52502 6 9.4725 6.09 9.435C6.225 9.375 6.37501 9.36 6.52501 9.39C6.57001 9.3975 6.615 9.4125 6.66 9.435C6.705 9.45 6.75 9.47252 6.795 9.50252C6.8325 9.53252 6.86999 9.56252 6.90749 9.59252C6.93749 9.63002 6.97499 9.66751 6.99749 9.70501C7.02749 9.75001 7.05001 9.79501 7.06501 9.84001C7.08751 9.885 7.10251 9.93 7.11001 9.975C7.11751 10.0275 7.125 10.0725 7.125 10.125C7.125 10.32 7.04249 10.515 6.90749 10.6575C6.76499 10.7925 6.57 10.875 6.375 10.875Z"
                                fill="#333333"
                              />
                              <path
                                d="M9 10.8742C8.805 10.8742 8.61001 10.7917 8.46751 10.6567C8.43751 10.6192 8.40751 10.5817 8.37751 10.5442C8.34751 10.4992 8.32499 10.4542 8.30999 10.4092C8.28749 10.3642 8.27249 10.3192 8.26499 10.2742C8.25749 10.2217 8.25 10.1767 8.25 10.1242C8.25 10.0267 8.27249 9.92917 8.30999 9.83917C8.34749 9.74917 8.40001 9.66668 8.46751 9.59168C8.67751 9.38168 9.015 9.31416 9.285 9.43416C9.3825 9.47166 9.45749 9.52418 9.53249 9.59168C9.66749 9.73418 9.75 9.92917 9.75 10.1242C9.75 10.1767 9.74251 10.2217 9.73501 10.2742C9.72751 10.3192 9.71251 10.3642 9.69001 10.4092C9.67501 10.4542 9.65249 10.4992 9.62249 10.5442C9.59249 10.5817 9.56249 10.6192 9.53249 10.6567C9.45749 10.7242 9.3825 10.7767 9.285 10.8142C9.195 10.8517 9.0975 10.8742 9 10.8742Z"
                                fill="#333333"
                              />
                              <path
                                d="M6.375 13.4992C6.2775 13.4992 6.18 13.4767 6.09 13.4392C6 13.4017 5.91751 13.3492 5.84251 13.2817C5.77501 13.2067 5.72249 13.1317 5.68499 13.0342C5.64749 12.9442 5.625 12.8467 5.625 12.7492C5.625 12.5542 5.70751 12.3592 5.84251 12.2167C5.91751 12.1492 6 12.0967 6.09 12.0592C6.3675 11.9392 6.69749 12.0067 6.90749 12.2167C6.93749 12.2542 6.97499 12.2917 6.99749 12.3292C7.02749 12.3742 7.05001 12.4192 7.06501 12.4642C7.08751 12.5092 7.10251 12.5542 7.11001 12.6067C7.11751 12.6517 7.125 12.7042 7.125 12.7492C7.125 12.9442 7.04249 13.1392 6.90749 13.2817C6.76499 13.4167 6.57 13.4992 6.375 13.4992Z"
                                fill="#333333"
                              />
                            </svg>
                            <time
                              className="text-black-label font-medium leading-4 -tracking-[4%]"
                              datetime={
                                rowData?.corporate_details
                                  ?.trip_start_date as string
                              }
                            >
                              {rowData?.corporate_details?.trip_start_date}
                            </time>
                            <span className="text-[#999999] text-black-label font-medium leading-4 -tracking-[4%]">
                              -
                            </span>
                            <time
                              className="text-black-label font-medium leading-4 -tracking-[4%]"
                              datetime={
                                rowData?.corporate_details
                                  ?.trip_end_date as string
                              }
                            >
                              {rowData?.corporate_details?.trip_end_date}
                            </time>
                          </div>
                        </div>
                      )}
                    </TabsContent>
                  </Tabs>
                </div>
              {opportunityDetails?.isOpportunityOpen === "open" &&  activeTab === "interaction" && (
                <div className="flex gap-8 sm:px-2 pt-1 rounded-lg items-end border-t p-2.5 bg-white-A700 border-gray-300 w-full">
                    <div className="flex flex-col gap-5 w-full">
                      <div className="flex flex-col gap-2.5">
                        <div className="flex justify-between w-full">
                          <label
                            htmlFor="interaction_note"
                            className="text-sm font-medium"
                          >
                            Add Interaction
                          </label>
                          {["6", "2"].includes(selectedChannelOption) && (
                            <EmailPopup notes={interactionNote} />
                          )}
                        </div>
                        <textarea
                          onChange={handleNoteChange}
                          value={interactionNote}
                          className="rounded-xl border border-gray-border-gray-lite h-20 w-full"
                          name="interaction_note"
                          id="interaction_note"
                          placeholder="write here..."
                        ></textarea>
                      </div>
                      <div className="flex w-full flex-wrap sm:flex-col justify-between items-start gap-5">
                        <div className="flex gap-4 relative">
                          {(showBothButtons ||
                            selectedChannel?.type === "inbound") && (
                            <div className="relative">
                              <Select
                                open={selectedType === "inbound"}
                                onOpenChange={(open) => {
                                  if (open) setSelectedType("inbound");
                                  else setSelectedType("");
                                }}
                                value={
                                  selectedChannel?.type === "inbound"
                                    ? selectedChannel.value
                                    : undefined
                                }
                                onValueChange={(value) => {
                                  handleSelect("inbound", value);
                                  setSelectedChannelOptions(value);
                                }}
                              >
                                <SelectTrigger
                                  className={`[&>svg]:hidden px-6 py-2.5 text-sm rounded-md transition-colors flex items-center gap-2 min-w-[160px]
                                        ${
                                          selectedChannel?.type === "inbound"
                                            ? "bg-blue-50 text-blue-600 border-transparent"
                                            : "bg-gray-50 text-gray-700 hover:bg-gray-100 border-transparent"
                                        }`}
                                >
                                  <div className="flex items-center justify-between w-full">
                                    <span>
                                      {selectedChannel?.type === "inbound"
                                        ? getSelectedLabel(
                                            "inbound",
                                            selectedChannel.value,
                                          )
                                        : "I contacted"}
                                    </span>
                                    {selectedChannel?.type === "inbound" && (
                                      <X
                                        className="h-4 w-4 text-blue-600 ml-2 cursor-pointer"
                                        onClick={handleReset}
                                      />
                                    )}
                                  </div>
                                </SelectTrigger>
                                <SelectContent className="w-40 p-1">
                                  <SelectGroup>
                                    {channel_options_inbound
                                      .filter((val) => val.label !== "Meeting")
                                      .map((channel) => (
                                        <SelectItem
                                          key={channel.value}
                                          value={channel.value.toString()}
                                          className={`[&>svg]:hidden text-sm rounded-md cursor-pointer transition-colors 
                                              ${
                                                selectedChannel?.type ===
                                                  "inbound" &&
                                                selectedChannel.value ===
                                                  channel.value.toString()
                                                  ? "bg-blue-100 text-blue-700 hover:bg-blue-100"
                                                  : "text-gray-700 hover:bg-blue-50"
                                              }`}
                                        >
                                          {channel.label}
                                        </SelectItem>
                                      ))}
                                  </SelectGroup>
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          {(showBothButtons ||
                            selectedChannel?.type === "outbound") && (
                            <div className="relative">
                              <Select
                                open={selectedType === "outbound"}
                                onOpenChange={(open) => {
                                  if (open) setSelectedType("outbound");
                                  else setSelectedType(null);
                                }}
                                value={
                                  selectedChannel?.type === "outbound"
                                    ? selectedChannel.value
                                    : undefined
                                }
                                onValueChange={(value) => {
                                  setSelectedChannelOptions(value);
                                  handleSelect("outbound", value);
                                }}
                              >
                                <SelectTrigger
                                  className={`px-6 py-2.5 text-sm rounded-md transition-colors flex items-center gap-2 min-w-[160px]
                                        ${
                                          selectedChannel?.type === "outbound"
                                            ? "bg-blue-50 text-blue-600 border-transparent"
                                            : "bg-gray-50 text-gray-700 hover:bg-gray-100 border-transparent"
                                        }`}
                                >
                                  <div className="flex items-center justify-between w-full">
                                    <span>
                                      {selectedChannel?.type === "outbound"
                                        ? getSelectedLabel(
                                            "outbound",
                                            selectedChannel.value,
                                          )
                                        : "Customer contacted"}
                                    </span>
                                    {selectedChannel?.type === "outbound" && (
                                      <X
                                        className="h-4 w-4 text-blue-600 ml-2 cursor-pointer"
                                        onClick={handleReset}
                                      />
                                    )}
                                  </div>
                                </SelectTrigger>
                                <SelectContent className="w-40 p-1">
                                  <SelectGroup>
                                    {channel_options_outbound
                                      .filter((val) => val.label !== "Meeting")
                                      .map((channel) => (
                                        <SelectItem
                                          key={channel.value}
                                          value={channel.value.toString()}
                                          className={` text-sm rounded-md cursor-pointer transition-colors data-[state=checked]:check:hidden
                                              ${
                                                selectedChannel?.type ===
                                                  "outbound" &&
                                                selectedChannel.value ===
                                                  channel.value.toString()
                                                  ? "bg-blue-100 text-blue-700 hover:bg-blue-100"
                                                  : "text-gray-700 hover:bg-blue-50"
                                              }`}
                                        >
                                          {channel.label}
                                        </SelectItem>
                                      ))}
                                  </SelectGroup>
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>

                        <div className=" flex-1 justify-start flex flex-wrap sm:flex-col gap-2 w-full">
                          {(productDetails?.name?.toLowerCase() ===
                            "education payment" ||
                            productDetails?.name?.toLowerCase() ===
                              "gic letter") && (
                            <button
                              disabled={extraDetails?.quote_requested}
                              className="px-4 py-[11px] text-sm font-semibold rounded-lg text-blue-700 cursor-pointer disabled:cursor-not-allowed"
                            >
                              <label
                                htmlFor="upload_letter"
                                className="flex items-center gap-2.5 cursor-pointer"
                              >
                                <input
                                  type="file"
                                  id="upload_letter"
                                  className="hidden"
                                  onChange={(e) =>
                                    handleLetterUpload(
                                      e?.target?.files?.[0] as File,
                                    )
                                  }
                                />
                                <p>Offer Letter</p>
                                <svg
                                  width="15"
                                  height="19"
                                  viewBox="0 0 15 19"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M0.5 4C0.5 1.79086 2.29086 0 4.5 0H10.0546C10.3381 0 10.6083 0.12032 10.7979 0.33104L14.7433 4.71477C14.9086 4.8984 15 5.13669 15 5.38374V14.5C15 16.7091 13.2091 18.5 11 18.5H4.5C2.29086 18.5 0.5 16.7091 0.5 14.5V4Z"
                                    fill="#165FE3"
                                  />
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M11.26 10.8978C10.9583 11.1817 10.4837 11.1672 10.1998 10.8655L7.7461 8.25736L5.2923 10.8655C5.0085 11.1672 4.5339 11.1817 4.2322 10.8978C3.9305 10.614 3.916 10.1394 4.1999 9.83766L6.8968 6.97106C7.3603 6.47836 8.1319 6.47836 8.5954 6.97106L11.2923 9.83766C11.5762 10.1394 11.5617 10.614 11.26 10.8978Z"
                                    fill="white"
                                  />
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M7.74609 15.6016C7.33189 15.6016 6.99609 15.2658 6.99609 14.8516V7.35156C6.99609 6.93736 7.33189 6.60156 7.74609 6.60156C8.16029 6.60156 8.49609 6.93736 8.49609 7.35156V14.8516C8.49609 15.2658 8.16029 15.6016 7.74609 15.6016Z"
                                    fill="white"
                                  />
                                  <path
                                    d="M14.9922 6V5.38374C14.9922 5.13669 14.9008 4.8984 14.7355 4.71477L10.7901 0.33104C10.6005 0.12032 10.3303 0 10.0468 0H8.99219V2C8.99219 4.20914 10.7831 6 12.9922 6H14.9922Z"
                                    fill="#98BDFF"
                                  />
                                </svg>
                              </label>
                            </button>
                          )}
                          {productDetails?.name?.toLowerCase() === "visa" && (
                            <button
                              disabled={extraDetails?.quote_requested}
                              className="px-4 py-[11px] text-sm font-semibold border-blue-700 border rounded-lg text-blue-700 cursor-pointer disabled:cursor-not-allowed"
                            >
                              <label
                                htmlFor="upload_letter"
                                className="flex items-center gap-2.5 cursor-pointer"
                              >
                                <input
                                  type="file"
                                  id="upload_letter"
                                  className="hidden"
                                  accept=".pdf"
                                  onChange={(e) =>
                                    handleQuoteUploadVisa(
                                      e?.target?.files?.[0] as File,
                                    )
                                  }
                                />
                                <p>Upload Quotation</p>
                                <svg
                                  width="15"
                                  height="19"
                                  viewBox="0 0 15 19"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M0.5 4C0.5 1.79086 2.29086 0 4.5 0H10.0546C10.3381 0 10.6083 0.12032 10.7979 0.33104L14.7433 4.71477C14.9086 4.8984 15 5.13669 15 5.38374V14.5C15 16.7091 13.2091 18.5 11 18.5H4.5C2.29086 18.5 0.5 16.7091 0.5 14.5V4Z"
                                    fill="#165FE3"
                                  />
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M11.26 10.8978C10.9583 11.1817 10.4837 11.1672 10.1998 10.8655L7.7461 8.25736L5.2923 10.8655C5.0085 11.1672 4.5339 11.1817 4.2322 10.8978C3.9305 10.614 3.916 10.1394 4.1999 9.83766L6.8968 6.97106C7.3603 6.47836 8.1319 6.47836 8.5954 6.97106L11.2923 9.83766C11.5762 10.1394 11.5617 10.614 11.26 10.8978Z"
                                    fill="white"
                                  />
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M7.74609 15.6016C7.33189 15.6016 6.99609 15.2658 6.99609 14.8516V7.35156C6.99609 6.93736 7.33189 6.60156 7.74609 6.60156C8.16029 6.60156 8.49609 6.93736 8.49609 7.35156V14.8516C8.49609 15.2658 8.16029 15.6016 7.74609 15.6016Z"
                                    fill="white"
                                  />
                                  <path
                                    d="M14.9922 6V5.38374C14.9922 5.13669 14.9008 4.8984 14.7355 4.71477L10.7901 0.33104C10.6005 0.12032 10.3303 0 10.0468 0H8.99219V2C8.99219 4.20914 10.7831 6 12.9922 6H14.9922Z"
                                    fill="#98BDFF"
                                  />
                                </svg>
                              </label>
                            </button>
                          )}
                          {["Forex", "Remittance"].includes(
                            opportunityDetails?.productSubcategory as string,
                          ) && (
                            <button
                              onClick={handlePrepareQuote}
                              disabled={!buttonState()}
                              className="px-4 py-[11px] text-sm font-semibold rounded-lg text-blue-700 border-blue-700 border disabled:opacity-40"
                            >
                              Prepare Quote
                            </button>
                          )}
                          <button
                            onClick={handleAddInteraction}
                            disabled={!buttonState()}
                            className=" px-4 py-[11px] text-sm font-semibold rounded-lg bg-blue-700 text-white-A700 disabled:opacity-40"
                          >
                            Submit
                          </button>
                          {pipelineType === "retail" &&
                            opportunityDetails?.productSubcategory !==
                              "Remittance" &&
                            opportunityDetails?.productSubcategory !==
                              "Visa" && (
                              <DocPopup
                                buttonState={buttonState}
                                onSubmit={handleRequestDoc}
                                selectedMode={selectedMode}
                                setSelectedMode={setSelectedMode}
                                expires={expires}
                                setExpires={setExpires}
                                popupnotes={popupnotes}
                                setPopupNotes={setPopupNotes}
                                channel={channel}
                                setChannel={setChannel}
                              />
                            )}
                        </div>
                      </div>
                    </div>
                </div>
                  )}
              </SheetContent>
            </div>
          </SheetPortal>
        </Sheet>
      )}
    </>
  );
};
